import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useClickOutside } from '../utils/useClickOutside';
import { collection, onSnapshot, doc, getDoc, serverTimestamp, deleteDoc, query, getDocs } from '../firebase.config';
import { enforcedAddDoc as addDoc, enforcedSetDoc as setDoc, setGlobalCompanyId } from '../utils/enforcedTracking';
import { useLanguage } from '../contexts/LanguageContext';
import { LimitReachedModal } from './UsageAlerts';

import InvoiceTemplate from './BillTemplates/InvoiceTemplate';
import InvoiceTemplateA5 from './BillTemplates/InvoiceTemplateA5';
import SimpleSalesTemplate from './BillTemplates/SimpleSalesTemplate';
import ModernSalesTemplate from './BillTemplates/ModernSalesTemplate';
import ChallanTemplate from './BillTemplates/ChallanTemplate';
import QuotationTemplate from './BillTemplates/QuotationTemplate';
import QuotationTemplateColored from './BillTemplates/QuotationTemplateColored';
import ReceiptTemplate from './BillTemplates/ReceiptTemplate';
import ActionButtons from './ActionButtons';

import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, app } from '../firebase.config';
import {
  getCollectionForCompany,
  normalizeCompanyDocSnapshot,
  buildBillPreviewCompanyDetails,
  buildBillPreviewBankDetails,
} from '../utils/dataAdapter';
import useActiveCompanyProfile from '../hooks/useActiveCompanyProfile';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { formatDateToCompanyFormat, getCompanyDateFormat } from '../utils/dateUtils';
import { useTableSort, SortableHeader } from '../utils/tableSort';
import { useTablePagination } from '../utils/tablePagination';
import PaginationControls from '../utils/PaginationControls';
import toast, { Toaster } from 'react-hot-toast';
import CompactFilterBar from './CompactFilterBar';
import {
  getPartyAdvance,
  allocateAdvanceToBill,
  markAdvanceUsed
} from '../utils/advanceUtils';
import { convertImageToDataUrl, preloadBillImages, inlineImagesForPrint } from '../utils/imageUtils';
import { roundTo2Decimals, calculateGSTAmount, calculateLineTotal, roundToNearestInteger } from '../utils/numberFormat';
import { normalizeTagName, tagDocIdFromName, collectLegacyTagsFromRowsAndBills } from '../utils/serviceTagRegistry';
import { updateDoc } from '../firebase.config';
import { 
  StandardModal, 
  StandardButton,
  PreviewModal,
  ConfirmationModal,
  FormModal,
  MODAL_SECTIONS,
} from './Modal';
import InfoHoverTip from './InfoHoverTip';
import {
  SUB_PARTY_MODAL_HELP,
  SUB_PARTY_NO_LINES_HELP,
  SUB_PARTY_AMOUNT_HELP,
  SUB_PARTY_AMOUNT_DETAIL_HELP,
  SUB_PARTY_SYNC_HELP,
  SUB_PARTY_ASSIGN_LINES_HELP,
  SUB_PARTY_NO_AVAILABLE_LINES_HELP,
  SUB_PARTY_ROUND_OFF_HELP,
  SUB_PARTY_TOTAL_MISMATCH_HELP,
  subPartyHiddenLinesHelp,
} from '../constants/subPartyHelpText';
import {
  SUB_PARTY_MODAL_SHELL_CLASS,
  SUB_PARTY_SCROLL_LIST_CLASS,
  SUB_PARTY_CARD_CLASS,
  SUB_PARTY_ROW_LAYOUT_CLASS,
  SUB_PARTY_NAME_FIELD_CLASS,
  SUB_PARTY_AMOUNT_FIELD_CLASS,
  SUB_PARTY_ACTIONS_CLASS,
  SUB_PARTY_SYNC_ROW_CLASS,
  SUB_PARTY_LINE_LIST_CLASS,
  SUB_PARTY_TOTALS_ROW_CLASS,
} from '../constants/subPartyModalLayout';

import imageManager from '../utils/imageManager';
import ShareButton from './Reports/ShareButton';
// Digital Signature URL Handler
const getDigitalSignatureUrl = async (signUrl, companyId) => {
  if (!signUrl || !companyId) return signUrl;
  
  // If it's already a cloud storage URL, return as is
  if (signUrl.startsWith('https://firebasestorage.googleapis.com/') || signUrl.startsWith('http')) {
    return signUrl;
  }
  
  // If it's base64 data, upload to cloud storage
  if (signUrl.startsWith('data:image/')) {
    try {
      const storage = getStorage();
      const base64Data = signUrl.split(',')[1];
      const binaryData = atob(base64Data);
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }
      
      const fileName = `signatures/${companyId}/digital_signature_${Date.now()}.png`;
      const fileRef = storageRef(storage, fileName);
      await uploadBytes(fileRef, bytes);
      const downloadURL = await getDownloadURL(fileRef);
      
      // Update company data with cloud URL
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        'branding.signatureUrl': downloadURL,
        'details.signUrl': downloadURL,
        'signUrl': downloadURL
      });
      
      console.log('✅ Digital signature uploaded to cloud storage:', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('❌ Failed to upload digital signature:', error);
      return signUrl; // Return original if upload fails
    }
  }
  
  return signUrl;
};

// Custom ShareButton component for sales (only PDF and Image)
const SalesShareButton = ({ onExportPDF, onExportImage, disabled = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Close dropdown on ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showDropdown]);

  const handleShareClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleOptionClick = async (action) => {
    if (!action || isProcessing) return;
    
    setShowDropdown(false);
    setIsProcessing(true);
    
    try {
      if (typeof action === 'function') {
        await action();
      }
    } catch (error) {
      // Silent error handling to prevent console spam
    } finally {
      setIsProcessing(false);
    }
  };

  const shareOptions = [
    { 
      label: '📄 Share as PDF', 
      action: onExportPDF,
      disabled: !onExportPDF
    },
    { 
      label: '🖼️ Share as Image', 
      action: onExportImage,
      disabled: !onExportImage
    }
  ].filter(option => !option.disabled);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleShareClick}
        disabled={disabled || isProcessing}
        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-md transition duration-300 text-sm relative"
      >
        {isProcessing ? '⏳' : '📱'} Share
      </button>
      
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 sm:w-48 w-40"
          style={{ zIndex: 9999 }}
        >
          {shareOptions.length === 0 ? (
            <div className="p-2 text-gray-500 text-xs">No sharing options available</div>
          ) : (
            shareOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionClick(option.action)}
                disabled={option.disabled || isProcessing}
                className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Make imageManager globally available for PDF generation
if (typeof window !== 'undefined') {
  window.imageManager = imageManager;
}

const initialItemRow = {
  item: '',
  nos: 1,
  length: 0,
  height: 0,
  qty: 0,
  qtyExpr: '',
  qtyDisplay: '',
  unit: '',
  quantityMeasurement: '',
  lineDiscountType: 'amount',
  lineDiscountValue: 0,
  rate: 0,
  rateIncludingGST: false, // New field for GST calculation option
  amount: 0,
  sgst: 0,
  cgst: 0,
  igst: 0,
  total: 0,
  tags: [], // Tags for tracking services like pasting, frame, vinyl, etc.
  services: [], // Optional per-tag service details
};

/** Same rule as cleanedRows in handleSaveInvoice — which lines count as “real” lines */
function isMeaningfulInvoiceRow(r) {
  const hasItem = !!(r.item && String(r.item).trim());
  const hasNumbers =
    (parseFloat(r.qty) || 0) > 0 ||
    (parseFloat(r.rate) || 0) > 0 ||
    (parseFloat(r.amount) || 0) > 0 ||
    (parseFloat(r.total) || 0) > 0 ||
    (parseFloat(r.length) || 0) > 0 ||
    (parseFloat(r.height) || 0) > 0 ||
    ((parseFloat(r.nos) || 0) > 1);
  return hasItem || hasNumbers;
}

/** Meaningful lines in order; cleanedIdx 0..n-1 matches index in saved bill.rows */
function getSubPartyLineRefsForRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row, rowIndex) => ({ row, rowIndex }))
    .filter(({ row }) => isMeaningfulInvoiceRow(row))
    .map((o, cleanedIdx) => ({ ...o, cleanedIdx }));
}

function sanitizeSubPartyLineIndices(raw, maxExclusive) {
  const cap = Math.max(0, parseInt(maxExclusive, 10) || 0);
  if (!Array.isArray(raw) || cap === 0) return [];
  const seen = new Set();
  const out = [];
  for (const v of raw) {
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n < 0 || n >= cap) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out.sort((a, b) => a - b);
}

/** Normalize modal draft rows for Firestore: name, amount, lineIndices (into saved rows order) */
function normalizeSubPartyBreakdownForSave(draftRows, meaningfulLineCount) {
  if (!Array.isArray(draftRows)) return [];
  const cap = Math.max(0, parseInt(meaningfulLineCount, 10) || 0);
  return draftRows
    .map((r) => ({
      name: String(r.name || '').trim(),
      amount: roundTo2Decimals(parseFloat(String(r.amount ?? '').replace(/,/g, '')) || 0),
      lineIndices: sanitizeSubPartyLineIndices(r.lineIndices, cap),
    }))
    .filter((r) => r.name.length > 0 && r.amount > 0);
}

function sumSubPartyBreakdownAmounts(rows) {
  if (!Array.isArray(rows)) return 0;
  return roundTo2Decimals(rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0));
}

function getLineTotalForSubParty(row, gstinType) {
  if (gstinType === 'Regular' && row && row.total !== undefined && row.total !== '') {
    return parseFloat(row.total) || 0;
  }
  return parseFloat(row?.amount) || 0;
}

/** Sum line totals for cleaned line indices (used as default sub party amount) */
function sumLineTotalsForSubPartyIndices(lineIndices, lineRefs, gstinType) {
  if (!Array.isArray(lineIndices) || !Array.isArray(lineRefs)) return 0;
  let sum = 0;
  lineIndices.forEach((ci) => {
    const ref = lineRefs.find((l) => l.cleanedIdx === ci);
    if (ref) sum += getLineTotalForSubParty(ref.row, gstinType);
  });
  return roundTo2Decimals(sum);
}

function collectAssignedSubPartyLineIndices(draftRows) {
  const assigned = new Set();
  if (!Array.isArray(draftRows)) return assigned;
  draftRows.forEach((r) => {
    (r.lineIndices || []).forEach((ci) => {
      const n = parseInt(ci, 10);
      if (Number.isFinite(n)) assigned.add(n);
    });
  });
  return assigned;
}

function areAllInvoiceLinesAssignedInSubPartyDraft(draftRows, lineRefCount) {
  if (!lineRefCount) return false;
  return collectAssignedSubPartyLineIndices(draftRows).size === lineRefCount;
}

/** UI-only `_invoiceRoundOffAdj` is stripped by normalizeSubPartyBreakdownForSave */
function applySubPartyDraftInvoiceRoundOff(nextDraft, lineRefs, gstinType, grandTotalRounded, grandTotalBeforeRounded) {
  if (!Array.isArray(nextDraft) || !Array.isArray(lineRefs) || lineRefs.length === 0) return nextDraft;
  const copy = nextDraft.map((r) => ({
    ...r,
    lineIndices: [...(r.lineIndices || [])],
    _invoiceRoundOffAdj: undefined,
  }));
  copy.forEach((r) => {
    r.amount = String(sumLineTotalsForSubPartyIndices(r.lineIndices, lineRefs, gstinType));
  });
  if (!areAllInvoiceLinesAssignedInSubPartyDraft(copy, lineRefs.length)) return copy;
  const totalsFromLines = roundTo2Decimals(
    copy.reduce((s, r) => s + sumLineTotalsForSubPartyIndices(r.lineIndices, lineRefs, gstinType), 0)
  );
  const expected = roundTo2Decimals(grandTotalBeforeRounded);
  if (Math.abs(totalsFromLines - expected) > 0.05) return copy;
  const delta = roundTo2Decimals(grandTotalRounded - totalsFromLines);
  if (Math.abs(delta) < 0.001) return copy;
  let absorbIdx = -1;
  for (let i = copy.length - 1; i >= 0; i--) {
    if ((copy[i].lineIndices || []).length > 0) {
      absorbIdx = i;
      break;
    }
  }
  if (absorbIdx < 0) return copy;
  const baseAmt = roundTo2Decimals(parseFloat(String(copy[absorbIdx].amount ?? '').replace(/,/g, '')) || 0);
  copy[absorbIdx].amount = String(roundTo2Decimals(baseAmt + delta));
  copy[absorbIdx]._invoiceRoundOffAdj = delta;
  return copy;
}

// Predefined units for new items
const predefinedUnits = [
  'Pieces', 'Piece', 'Kg', 'Gram', 'Ton', 'Quintal', 'Meter', 'Centimeter', 'Millimeter', 'Yard', 'Foot', 'Inch',
  'Litre', 'ML', 'Gallon', 'Drum', 'Cylinder',
  'Box', 'Packet', 'Carton', 'Bag', 'Bottle', 'Can', 'Jar', 'Tube',
  'Sheet', 'Strip', 'Roll', 'Ream', 'Bundle', 'Panel', 'Board', 'Plate', 'Slab', 'Block',
  'Sq. Ft.', 'Sq. Inch', 'Sq. Yard', 'Square Meter', 'Cubic Feet', 'Cubic Meter', 'Cu. Inch', 'Cu. Yard',
  'Hour', 'Day', 'Month', 'Year',
  'Lot', 'Job', 'Impression', 'Run',
  'Set', 'Pair',
  'Other'
];

const SERVICE_STATUS_OPTIONS = ['pending', 'done', 'paid'];

const createDefaultServiceEntry = (serviceName = '') => ({
  id: `svc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  serviceName: String(serviceName || '').trim().toLowerCase(),
  vendorId: '',
  vendorName: '',
  internalCost: '',
  customerCharge: '',
  showOnInvoice: false,
  status: 'pending',
  createdAt: new Date().toISOString(),
});

const normalizeServiceEntry = (entry = {}) => {
  const status = SERVICE_STATUS_OPTIONS.includes(entry.status) ? entry.status : 'pending';
  return {
    ...createDefaultServiceEntry(entry.serviceName || ''),
    ...entry,
    serviceName: String(entry.serviceName || '').trim().toLowerCase(),
    vendorId: entry.vendorId || '',
    vendorName: entry.vendorName || '',
    internalCost: entry.internalCost ?? '',
    customerCharge: entry.customerCharge ?? '',
    showOnInvoice: Boolean(entry.showOnInvoice),
    status,
  };
};

function getFinancialYear(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  if (month >= 4) {
    return `${year}-${(year + 1).toString().slice(-2)}`;
  } else {
    return `${year - 1}-${year.toString().slice(-2)}`;
  }
}

// Utility function to determine GST split type
function getGstTypeAndSplit(sellerGstin, buyerGstin, gstPercent) {
  if (!sellerGstin || !buyerGstin || sellerGstin.length < 2 || buyerGstin.length < 2) {
    return { type: 'CGST_SGST', cgst: gstPercent / 2, sgst: gstPercent / 2, igst: 0 };
  }
  const sellerState = sellerGstin.substring(0, 2);
  const buyerState = buyerGstin.substring(0, 2);
  if (sellerState === buyerState) {
    return { type: 'CGST_SGST', cgst: gstPercent / 2, sgst: gstPercent / 2, igst: 0 };
  } else {
    return { type: 'IGST', cgst: 0, sgst: 0, igst: gstPercent };
  }
}

// Utility for rounding to 2 decimal places - now imported from numberFormat.js
// function round2(val) {
//   return Math.round((parseFloat(val) + Number.EPSILON) * 100) / 100;
// }

// Parse a quantity expression like "5x3x2" or "10*2" into a numeric product and a normalized display string
function parseQtyExpression(input) {
  const s = String(input || '').trim().replace(/\s+/g, '');
  if (!s) return { ok: false, value: 0, display: '' };
  const parts = s.split(/[xX\*]/).filter(Boolean);
  if (parts.length === 0) return { ok: false, value: 0, display: '' };
  let product = 1;
  for (const p of parts) {
    const n = Number(p);
    if (!isFinite(n) || n <= 0) return { ok: false, value: 0, display: '' };
    product *= n;
  }
  return { ok: true, value: product, display: parts.join('×') };
}




// Dropdown Form Component for Desktop Table Rows
const DropdownFormRow = ({ bill, docType, docTypeOptions, companyCurrency, onView, onEdit, onDelete, onPrint, onPayment, onReceipts, receiptsCount, receiptNumbers, items = [], index = 0, isExpanded, onToggle, currentCompany }) => {
    // Format date according to company settings
    const date = formatDateToCompanyFormat(bill.invoiceDate || bill.date, currentCompany);
    
    // Format amount with currency
    const amount = typeof bill.amount === "number"
        ? formatCurrency(bill.amount, companyCurrency)
        : bill.amount;

    const paidAmount = formatCurrency(bill.paid || 0, companyCurrency);
    const outstandingAmount = formatCurrency(bill.outstanding || 0, companyCurrency);

    // Get bill items for this invoice - check both items and rows arrays
    const billItems = bill.items || bill.rows || [];

    // Helper function to get item name from item ID
    const getItemName = (itemId) => {
        if (!itemId || !items || !Array.isArray(items)) return null;
        const item = items.find(i => i.id === itemId || i.itemId === itemId);
        return item ? (item.name || item.itemName || item.description) : null;
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-2 transform transition-all duration-300 ease-in-out hover:shadow-md">
            {/* Main Row - Always Visible */}
            <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => onToggle(index)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-6 gap-2 lg:gap-4 items-center text-center">
                        <div className="text-sm font-medium text-gray-900 truncate" title={bill.number || bill.invoiceNumber}>
                            {bill.number || bill.invoiceNumber}
                        </div>
                        <div className="text-sm text-gray-600" title={date}>{date}</div>
                        <div className="text-sm text-gray-600 truncate" title={bill.partyName}>
                            {bill.partyName}
                        </div>
                        <div className="text-sm font-semibold text-blue-600" title={amount}>{amount}</div>
                        <div 
                            className="text-sm font-medium text-green-600 cursor-pointer hover:text-green-800 transition-colors hover:bg-green-50 px-1 py-0.5 rounded"
                            onClick={(e) => {
                                e.stopPropagation();
                                onReceipts(bill);
                            }}
                            title="Click to view receipt details"
                        >
                            {paidAmount}
                        </div>
                        <div 
                            className="text-sm font-medium text-red-600 cursor-pointer hover:text-red-800 transition-colors hover:bg-red-50 px-1 py-0.5 rounded"
                            onClick={(e) => {
                                e.stopPropagation();
                                onPayment(bill);
                            }}
                            title="Click to add payment"
                        >
                            {outstandingAmount}
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2 lg:ml-4">
                        <ActionButtons
                            enableDropdown={true}
                            billData={{
                                ...bill,
                                partyName: bill.partyName
                            }}
                            items={items}
                            actions={[
                                { type: 'edit', onClick: () => onEdit(bill) },
                                { type: 'delete', onClick: () => onDelete(bill.id) },
                                {
                                    type: docType === 'invoice' ? 'invoice' : docType === 'challan' ? 'challan' : 'quotation',
                                    onClick: () => onPrint(bill)
                                }
                            ]}
                            size="sm"
                        />
                        <button
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggle(index);
                            }}
                        >
                            <svg 
                                className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? '-rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Dropdown Content - Items List */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-blue-50 px-3 py-2 sm:p-4">
                    <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Items in this {docType}:</h4>
                        {billItems.length > 0 ? (
                            <div className="space-y-1 max-h-80 overflow-y-auto custom-scrollbar">
                                {billItems.map((item, itemIdx) => (
                                    <div key={itemIdx} className="bg-white p-2 rounded border border-gray-200 hover:bg-gray-50 transition-colors">
                                        {/* Item Name - Prominent on Mobile */}
                                        <div className="mb-1">
                                            <span className="text-sm sm:text-base font-medium text-gray-900 block" title={getItemName(item.item) || item.name || 'Unnamed Item'}>
                                                {getItemName(item.item) || item.name || 'Unnamed Item'}
                                            </span>
                                        </div>
                                        
                                        {/* Item Details - Responsive Layout */}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
                                            <span className="whitespace-nowrap">Qty: {item.qty || item.quantity || item.nos || 0}</span>
                                            <span className="whitespace-nowrap">Rate: {formatCurrency(item.rate || 0, companyCurrency)}</span>
                                            <span className="whitespace-nowrap font-medium text-gray-900">Total: {formatCurrency(item.amount || item.total || 0, companyCurrency)}</span>
                                        </div>
                                        {(item.description || item.desc) && (
                                            <div className="text-xs text-gray-500 mt-1 truncate" title={item.description || item.desc}>
                                                {item.description || item.desc}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 italic text-center py-4">No items found for this {docType}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Collapsible Sales Card Component for Mobile View
const SalesCard = ({ bill, docType, docTypeOptions, companyCurrency, onView, onEdit, onDelete, onPrint, onPayment, onReceipts, receiptsCount, receiptNumbers, items = [], index = 0, isExpanded, onToggle, currentCompany }) => {
    // Format date according to company settings
    const date = formatDateToCompanyFormat(bill.invoiceDate || bill.date, currentCompany);
    
    // Format amount with currency
    const amount = typeof bill.amount === "number"
        ? formatCurrency(bill.amount, companyCurrency)
        : bill.amount;

    // Get bill items for this invoice - check both items and rows arrays
    const billItems = bill.items || bill.rows || [];

    // Helper function to get item name from item ID
    const getItemName = (itemId) => {
        if (!itemId || !items || !Array.isArray(items)) return null;
        const item = items.find(i => i.id === itemId || i.itemId === itemId);
        return item ? (item.name || item.itemName || item.description) : null;
    };

    return (
        <div 
            className="bg-white border border-gray-200 rounded-lg shadow-sm mb-0 transform transition-all duration-300 ease-in-out hover:shadow-md hover:scale-[1.02]"
            style={{
                animationDelay: `${index * 50}ms`,
                animation: 'slideInUp 0.4s ease-out forwards'
            }}
        >
            {/* Header Section */}
            <div 
                className="px-3 py-2 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => onToggle(index)}
            >
                <div className="flex justify-between items-center">
                    <div className="flex-1 min-w-0">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{bill.number || bill.invoiceNumber}</h4>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{bill.partyName || 'No Party'}</p>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 ml-2">
                        <div className="text-right">
                            <div className="text-sm sm:text-lg font-semibold text-blue-600">{amount}</div>
                            <div className="text-xs text-gray-500">{date}</div>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="px-1 sm:px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 mb-2">
                                {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Payment Status Row - Single Row Layout */}
                <div className="mt-2 sm:mt-3 flex justify-between items-center">
                    <div 
                        className="text-xs sm:text-sm font-medium text-green-600 cursor-pointer hover:text-green-800 transition-colors px-1 sm:px-2 py-1 rounded hover:bg-green-50"
                        onClick={(e) => {
                            e.stopPropagation();
                            onReceipts(bill);
                        }}
                        title="Click to view receipt details"
                    >
                        <span className="hidden sm:inline">Paid: </span>
                        <span className="sm:hidden">💰 </span>
                        {formatCurrency(bill.paid || 0, companyCurrency)}
                    </div>
                    <div 
                        className="text-xs sm:text-sm font-medium text-red-600 cursor-pointer hover:text-red-800 transition-colors px-1 sm:px-2 py-1 rounded hover:bg-red-50"
                        onClick={(e) => {
                            e.stopPropagation();
                            onPayment(bill);
                        }}
                        title="Click to add payment"
                    >
                        <span className="hidden sm:inline">Outstanding: </span>
                        <span className="sm:hidden">💳 </span>
                        {formatCurrency(bill.outstanding || 0, companyCurrency)}
                    </div>
                </div>

                {/* Action Buttons with Dropdown */}
                <div className="mt-2 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-100 relative">
                    <div className="flex justify-between items-center">
                    <ActionButtons
                        enableDropdown={true}
                        billData={{
                            ...bill,
                            partyName: bill.partyName
                        }}
                        items={items}
                        actions={[
                            { type: 'edit', onClick: () => onEdit(bill) },
                            { type: 'delete', onClick: () => onDelete(bill.id) },
                            {
                                type: docType === 'invoice' ? 'invoice' : docType === 'challan' ? 'challan' : 'quotation',
                                onClick: () => onPrint(bill)
                            }
                        ]}
                            className="justify-start"
                        size="md"
                    />
                        <button
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggle(index);
                            }}
                        >
                            <svg 
                                className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? '-rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                        </button>
                </div>
            </div>
            </div>

            {/* Dropdown Content - Items List */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-blue-50 px-3 py-2 sm:p-4">
                    <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Items in this {docType}:</h4>
                        {billItems.length > 0 ? (
                            <div className="space-y-1 max-h-80 overflow-y-auto custom-scrollbar">
                                {billItems.map((item, itemIdx) => (
                                    <div key={itemIdx} className="bg-white p-2 rounded border border-gray-200 hover:bg-gray-50 transition-colors">
                                        {/* Item Name - Prominent on Mobile */}
                                        <div className="mb-1">
                                            <span className="text-sm sm:text-base font-medium text-gray-900 block" title={getItemName(item.item) || item.name || 'Unnamed Item'}>
                                                {getItemName(item.item) || item.name || 'Unnamed Item'}
                                            </span>
                                        </div>
                                        
                                        {/* Item Details - Responsive Layout */}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
                                            <span className="whitespace-nowrap">Qty: {item.qty || item.quantity || item.nos || 0}</span>
                                            <span className="whitespace-nowrap">Rate: {formatCurrency(item.rate || 0, companyCurrency)}</span>
                                            <span className="whitespace-nowrap font-medium text-gray-900">Total: {formatCurrency(item.amount || item.total || 0, companyCurrency)}</span>
                                        </div>
                                        {(item.description || item.desc) && (
                                            <div className="text-xs text-gray-500 mt-1 truncate" title={item.description || item.desc}>
                                                {item.description || item.desc}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 italic text-center py-4">No items found for this {docType}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ESC Key Functionality: Press ESC key to close modals in LIFO order (Last In, First Out)
// Order: Receipt Modal → Payment Details Modal → Payment Modal → Invoice Modal → View Modal → Success Modal → Add Item Modal
function Sales({ db, userId, isAuthReady, appId }) {
  const navigate = useNavigate();
  const { currentCompany, companyProfile } = useActiveCompanyProfile();
  const companyId = currentCompany?.companyId || currentCompany?.id || null;
  const { currency: companyCurrency } = useCompanyCurrency();
  const { t } = useLanguage();
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [party, setParty] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('Pending');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
  // Local text for item combobox per row (do NOT persist)
  const [itemSearchTextByRow, setItemSearchTextByRow] = useState({});
  
  // Bill-wise GST calculation type
  const [gstCalculationType, setGstCalculationType] = useState('excluding'); // 'including' or 'excluding'
  
  // Dropdown form state for expanded rows
  const [expandedRows, setExpandedRows] = useState({});
  const [notification, setNotification] = useState(null);
 
  // Modal management


  // Live data states
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [company, setCompany] = useState({});
  const billPreviewCompanyDetails = useMemo(
    () => buildBillPreviewCompanyDetails(company, companyProfile),
    [company, companyProfile]
  );
  const billPreviewBankDetails = useMemo(
    () => buildBillPreviewBankDetails(company, companyProfile),
    [company, companyProfile]
  );
  const [bills, setBills] = useState([]);



  // Remove template selection, always use SunriseTemplate
  // Remove print-related state and function
  // Remove: const [showPrint, setShowPrint] = useState(false);
  // Remove: const handlePrint = () => { ... }
  // Remove print area and print button from the return JSX
  
  // ADD ITEM Modal states
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  
  // ADD PARTY Modal states
  const [showAddPartyModal, setShowAddPartyModal] = useState(false);
  const [newPartyName, setNewPartyName] = useState('');
  const [newPartyGstin, setNewPartyGstin] = useState('');
  const [newPartyAddress, setNewPartyAddress] = useState('');
  const [newPartyPhone, setNewPartyPhone] = useState('');
  const [newPartyEmail, setNewPartyEmail] = useState('');
  const [newPartyGstType, setNewPartyGstType] = useState('Unregistered');
  const [addPartyMessage, setAddPartyMessage] = useState('');

  /** Invoice-level sub parties (names + amounts); amounts should sum to Grand Total — preview/reference */
  const [subPartyBreakdown, setSubPartyBreakdown] = useState([]);
  const [showSubPartyModal, setShowSubPartyModal] = useState(false);
  const [subPartyDraft, setSubPartyDraft] = useState([]);
  
  // Additional party fields
  const [newPartyOpeningBalance, setNewPartyOpeningBalance] = useState('');
  const [newPartyCreditLimit, setNewPartyCreditLimit] = useState('');
  const [newPartyCreditPeriod, setNewPartyCreditPeriod] = useState('');
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  
  // Mobile view carousel state
  const [currentMobileItemIndex, setCurrentMobileItemIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // Payment Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [payments, setPayments] = useState([]);
  const [selectedBillForPayment, setSelectedBillForPayment] = useState(null);
  
  // Payment Receipt Preview states
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPaymentForReceipt, setSelectedPaymentForReceipt] = useState(null);
  const [receiptZoom, setReceiptZoom] = useState(1);
  const receiptRef = useRef();
  
  // Image management states
  const [selectedImages, setSelectedImages] = useState({
    logo: null,
    seal: null,
    signature: null,
    qr: null
  });
  
  // Payment Details Modal states (for multiple receipts)
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [selectedBillForReceipts, setSelectedBillForReceipts] = useState(null);
  const [newItemName, setNewItemName] = useState('');
  const [newItemMeasurement, setNewItemMeasurement] = useState('');
  const [newItemRate, setNewItemRate] = useState('');
  const [newItemType, setNewItemType] = useState('Service');
  const [newItemHsnCode, setNewItemHsnCode] = useState('');
  const [newItemGstPercentage, setNewItemGstPercentage] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemPurchasePrice, setNewItemPurchasePrice] = useState('0');
  const [newItemSalePrice, setNewItemSalePrice] = useState('0');
  const [newItemCurrentStock, setNewItemCurrentStock] = useState('0');
  const [newItemIsActive, setNewItemIsActive] = useState(true);
  const [newItemCustomUnit, setNewItemCustomUnit] = useState('');
  const [addItemMessage, setAddItemMessage] = useState('');
  
  // Filter states for sales bill list
  const [filterSearch, setFilterSearch] = useState(''); // Combined search for bill number and party name
  const [filterDate, setFilterDate] = useState('');
  const [filterAmount, setFilterAmount] = useState('');
  const [filterTag, setFilterTag] = useState(''); // Filter by tag
  const [showFilters, setShowFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sales_showFilters');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });
  
  // Add state for document type (Invoice, Challan, Quotation)
  const [docType, setDocType] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const t = params.get('type');
      return ['invoice','challan','quotation'].includes(t) ? t : 'invoice';
    } catch { return 'invoice'; }
  }); // 'invoice', 'challan', 'quotation'
  const docTypeOptions = [
    { value: 'invoice', label: 'INVOICE', collection: 'salesBills', numberLabel: 'Invoice Number' },
    { value: 'challan', label: 'CHALLAN', collection: 'challans', numberLabel: 'Challan Number' },
    { value: 'quotation', label: t('quotation'), collection: 'quotations', numberLabel: 'Quotation Number' },
  ];



  // Add state to track GST type for each row (for UI feedback)
  const [gstTypes, setGstTypes] = useState(rows.map(() => ''));

  // Add state for editing bill
  const [editingBillId, setEditingBillId] = useState(null);

  // Quick items state variables
  const [mostUsedItems, setMostUsedItems] = useState([]);
  const [partyWiseItems, setPartyWiseItems] = useState([]);
  const [selectedPartyForQuickItems, setSelectedPartyForQuickItems] = useState('');
  const [showQuickItemsModal, setShowQuickItemsModal] = useState(false);
  const [quickItemModalPosition, setQuickItemModalPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
  
  // Mobile edit modal states
  const [showMobileEditModal, setShowMobileEditModal] = useState(false);
  const [partySearchText, setPartySearchText] = useState('');
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const partyDropdownRef = useRef(null);
  
  // Tag management states
  const [showTagPicker, setShowTagPicker] = useState({}); // { rowIndex: true/false }
  const tagPickerContainerRefs = useRef({});
  const [tagSearchText, setTagSearchText] = useState({}); // { rowIndex: 'search text' }
  const [showServiceEditorModal, setShowServiceEditorModal] = useState(false);
  const [serviceEditorContext, setServiceEditorContext] = useState({ rowIndex: null, serviceName: '' });
  const [serviceEditorForm, setServiceEditorForm] = useState(createDefaultServiceEntry());
  const [serviceTagMaster, setServiceTagMaster] = useState([]);
  const [showManageTagsModal, setShowManageTagsModal] = useState(false);
  const [manageTagsNewName, setManageTagsNewName] = useState('');
  const [pinTagForItem, setPinTagForItem] = useState({});

  const openServiceOperations = () => {
    const params = new URLSearchParams();
    const firstTaggedRow = rows.find(row => Array.isArray(row.tags) && row.tags.length > 0);
    if (firstTaggedRow?.tags?.[0]) params.set('tag', firstTaggedRow.tags[0]);
    params.set('status', 'pending');
    const query = params.toString();
    navigate(`/service-operations${query ? `?${query}` : ''}`);
  };
  
  // Filtered parties for mobile search
  const filteredParties = parties.filter(p => 
    p.firmName.toLowerCase().includes(partySearchText.toLowerCase())
  );
  
  // Close party dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPartyDropdown && partyDropdownRef.current && !partyDropdownRef.current.contains(event.target)) {
        setShowPartyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPartyDropdown]);

  // Close tag picker when clicking outside (button + dropdown container)
  useEffect(() => {
    const hasOpenPicker = Object.values(showTagPicker).some(Boolean);
    if (!hasOpenPicker) return;

    const handleClickOutside = (event) => {
      const clickedInside = event.target instanceof Element
        && Boolean(event.target.closest('[data-tag-picker-container="true"]'));
      if (!clickedInside) setShowTagPicker({});
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTagPicker]);

  // Sync docType and view bill to URL for deep links
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    if (params.get('type') !== docType) params.set('type', docType);
    if (editingBillId) params.set('edit', editingBillId); else params.delete('edit');
    const base = window.location.hash.split('?')[0];
    const next = `${base}?${params.toString()}`;
    if (window.location.hash !== next) window.location.hash = next;
  }, [docType, editingBillId]);

  // On mount, open view/edit if URL has ids
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const viewId = params.get('view');
    const editId = params.get('edit');
    if (viewId) {
      const selected = docTypeOptions.find(opt => opt.value === docType);
      if (selected) {
        getDoc(doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), viewId)).then(snap => {
          if (snap.exists()) { setViewBill({ id: snap.id, ...snap.data() }); setShowViewModal(true); }
        }).catch(() => {});
      }
    }
    if (editId) setEditingBillId(editId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add state for custom fields
  const [customFields, setCustomFields] = useState({
    ewayBillNo: '',
    ewayQr: '',
    ewayDate: ''
  });
  const [showEwayBill, setShowEwayBill] = useState(() => {
    // Get user's last preference from localStorage, default to true
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('showEwayBill');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showEwayBillModal, setShowEwayBillModal] = useState(false);

  // Ref for scrolling to the invoice items table
  const tableRef = useRef(null);
  const quickItemButtonRef = useRef(null);

  // Add at the top:
  const [errorMessage, setErrorMessage] = useState('');
  const [showDocTypeDropdown, setShowDocTypeDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDocTypeDropdown && !event.target.closest('.relative')) {
        setShowDocTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDocTypeDropdown]);

  // Legacy bill-level discount (kept for backward compatibility in saved bills)
  const [discountType, setDiscountType] = useState('amount');
  const [discountValue, setDiscountValue] = useState('');

  const [quotationTermsOverride, setQuotationTermsOverride] = useState('');

  // Table sorting hook with default sort by bill number descending (LIFO)
  const { sortConfig, handleSort, getSortedData } = useTableSort([], { key: 'number', direction: 'desc' });



  // Fetch parties
  useEffect(() => {
    if (db && userId && isAuthReady) {
      const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      if (!partiesCollectionRef) return;
      
      const unsubscribe = onSnapshot(
        partiesCollectionRef,
        (snapshot) => {
          const allParties = [];
          snapshot.forEach((doc) => {
            allParties.push({ id: doc.id, ...doc.data() });
          });
          allParties.sort((a, b) => (a.firmName || '').localeCompare(b.firmName || ''));
          setParties(allParties);
        }
      );
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch items
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const itemsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
      if (!itemsCollectionRef) return;
      
      const unsubscribe = onSnapshot(itemsCollectionRef, (snapshot) => {
        const itemsArr = [];
        snapshot.forEach((doc) => {
          itemsArr.push({ id: doc.id, ...doc.data() });
        });
        itemsArr.sort((a, b) => (a.itemName || '').localeCompare(b.itemName || ''));
        setItems(itemsArr);
      });
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  useEffect(() => {
    if (!db || !userId || !isAuthReady || !companyId) return;
    const ref = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    if (!ref) return;
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setServiceTagMaster(list);
      },
      (err) => console.error('serviceTags listener', err)
    );
    return () => unsub();
  }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch purchases for stock calculation
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const purchasesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
      if (!purchasesCollectionRef) return;
      
      const unsubscribe = onSnapshot(purchasesCollectionRef, (snapshot) => {
        const bills = [];
        snapshot.forEach((doc) => {
          bills.push({ id: doc.id, ...doc.data() });
        });
        setPurchases(bills);
      });
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch company details
  useEffect(() => {
    if (db && isAuthReady && companyId) {
      const unsubscribe = onSnapshot(doc(db, 'companies', companyId), (docSnap) => {
        if (docSnap.exists()) {
          const d = docSnap.data() || {};
          const companyData = normalizeCompanyDocSnapshot(d, companyId);
          
          // Process digital signature if it's base64 data - AUTOMATIC CONVERSION
          if (companyData.signUrl && companyData.signUrl.startsWith('data:image/')) {
            console.log('🔄 Auto-converting base64 digital signature to cloud storage...');
            getDigitalSignatureUrl(companyData.signUrl, companyId).then(cloudUrl => {
              if (cloudUrl !== companyData.signUrl) {
                console.log('✅ Digital signature auto-converted to cloud storage:', cloudUrl);
                // Update company data with new cloud URL
                companyData.signUrl = cloudUrl;
                setCompany({...companyData});
                // Show user notification
                console.log('🎉 Digital signature automatically converted to cloud storage for better performance!');
                setNotification({
                  type: 'success',
                  message: 'Digital signature automatically converted to cloud storage for better performance!'
                });
                // Auto-hide notification after 5 seconds
                setTimeout(() => setNotification(null), 5000);
              }
            }).catch(error => {
              console.error('❌ Failed to auto-convert digital signature:', error);
              // Keep the base64 version as fallback
              console.log('⚠️ Keeping base64 signature as fallback');
            });
          } else if (companyData.signUrl) {
            console.log('📄 Digital signature URL found (already in cloud storage):', companyData.signUrl);
          } else {
            console.log('⚠️ No digital signature found in company data');
          }
          setCompany(companyData);
          
          // Debug company data for seal/sign and GST
          console.log('Company data loaded:', {
            rawData: d,
            detailsData: d.details,
            brandingData: d.branding,
            finalCompanyData: companyData,
            sealUrl: companyData.sealUrl,
            signUrl: companyData.signUrl,
            gstinType: companyData.gstinType,
            gstinTypeFromRaw: d.gstinType,
            gstinTypeFromDetails: d.details?.gstinType,
            allGstinTypeSources: {
              rootGstinType: d.gstinType,
              detailsGstinType: d.details?.gstinType,
              rootGstType: d.gstType,
              basicInfoGstType: d.basicInfo?.gstType,
              detailsGstType: d.details?.gstType,
              branding: d.branding?.gstinType,
              final: companyData.gstinType
            },
            signUrlSources: {
              rootSignUrl: d.signUrl,
              detailsSignUrl: d.details?.signUrl,
              brandingSignatureUrl: d.branding?.signatureUrl,
              finalSignUrl: companyData.signUrl,
              isBase64: companyData.signUrl && companyData.signUrl.startsWith('data:image/'),
              isCloudUrl: companyData.signUrl && companyData.signUrl.startsWith('https://firebasestorage.googleapis.com/')
            }
          });
          
          // Set GST calculation type from company settings
          const gstCalcSetting = d.gstCalculation || d.details?.gstCalculation || 'excluding';
          setGstCalculationType(gstCalcSetting);
        }
      });
      return () => unsubscribe();
    }
  }, [db, isAuthReady, companyId]);

  // Fetch payments
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      if (!paymentsRef) return;
      
      const paymentsQuery = query(paymentsRef);
      const unsubscribe = onSnapshot(paymentsQuery, (snapshot) => {
        const paymentsData = [];
        snapshot.forEach((doc) => {
          paymentsData.push({ id: doc.id, ...doc.data() });
        });
        setPayments(paymentsData);
      });
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Generate receipt number for sales payments
  const generateReceiptNumber = async () => {
    const currentYear = new Date().getFullYear();
    const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
    const paymentType = docType === 'invoice' ? 'I' : docType === 'challan' ? 'C' : 'Q';
    
    try {
      const paymentsQuery = query(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      
      let highestSequence = 0;
      
      paymentsSnapshot.forEach((doc) => {
        const payment = doc.data();
        if (payment.receiptNumber) {
          const regexPattern = new RegExp(`PR${paymentType}${financialYear.replace('-', '\\-')}\\/(\\d+)`);
          const match = payment.receiptNumber.match(regexPattern);
          
          if (match) {
            const sequence = parseInt(match[1]);
            if (sequence > highestSequence) {
              highestSequence = sequence;
            }
          }
        }
      });
      
      const nextSequence = highestSequence + 1;
      return `PR${paymentType}${financialYear}/${nextSequence.toString().padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating receipt number:', error);
      return `PR${paymentType}${financialYear}/001`;
    }
  };

  // Get party name by ID
  const getPartyName = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party ? (party.firmName || party.name) : 'Unknown Party';
  };

  // Calculate outstanding amount for a bill
  const getBillOutstanding = (bill) => {
    const billPayments = payments.filter(p => 
      p.allocations && p.allocations.some(a => a.billId === bill.id && a.billType === docType)
    );
    
    const totalPaid = billPayments.reduce((sum, payment) => {
      const allocation = payment.allocations.find(a => a.billId === bill.id && a.billType === docType);
      return sum + (allocation ? allocation.allocatedAmount : 0);
    }, 0);
    
    const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
    return totalAmount - totalPaid;
  };

  // Fetch bills for the selected type
  useEffect(() => {
    if (db && userId && isAuthReady) {
      const selected = docTypeOptions.find(opt => opt.value === docType);
      if (!selected) return;
      const collectionRef = getCollectionForCompany(db, { companyId, appId, userId }, selected.collection);
      if (!collectionRef) return;
      
      const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
        const arr = [];
        snapshot.forEach((doc) => {
          arr.push({ id: doc.id, ...doc.data() });
        });
        // LIFO sorting is now handled by the pagination utility
        setBills(arr);
      }, (error) => {
        console.error('Error fetching bills:', error);
        setBills([]);
      });
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId, docType]);

  // Fetch most used items for quick access
  useEffect(() => {
    if (db && userId && isAuthReady && bills.length > 0) {
      // Aggregate item usage from sales bills
      const itemUsage = {};
      
      bills.forEach(bill => {
        // Check both bill.items and bill.rows for item data
        const billItems = bill.items || bill.rows || [];
        if (Array.isArray(billItems)) {
          billItems.forEach(item => {
            if (item.item && (item.qty || item.nos) && item.rate) {
              if (!itemUsage[item.item]) {
                itemUsage[item.item] = {
                  itemId: item.item, // Use itemId for consistency
                  id: item.item,
                  count: 0,
                  lastUsed: null,
                  lastRate: 0,
                  lastQty: 0
                };
              }
              
              itemUsage[item.item].count += 1;
              const billDate = bill.invoiceDate || bill.date ? new Date(bill.invoiceDate || bill.date) : new Date();
              
              if (!itemUsage[item.item].lastUsed || billDate > itemUsage[item.item].lastUsed) {
                itemUsage[item.item].lastUsed = billDate;
                itemUsage[item.item].lastRate = parseFloat(item.rate) || 0;
                itemUsage[item.item].lastQty = parseFloat(item.qty || item.nos) || 0;
              }
            }
          });
        }
      });
      
      // Convert to array and sort by usage count, then by last used date
      const sortedItems = Object.values(itemUsage)
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        })
        .slice(0, 20); // Top 20 most used items
      
      // Add item names to the quick items data
      const itemsWithNames = sortedItems.map(item => {
        const itemDetails = items.find(it => it.id === item.itemId);
        return {
          ...item,
          itemName: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item',
          name: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item'
        };
      });
      
      setMostUsedItems(itemsWithNames);
    } else {
      setMostUsedItems([]);
    }
  }, [db, userId, isAuthReady, bills, items]);

  // Fetch party-wise items for quick access
  useEffect(() => {
    if (db && userId && isAuthReady && selectedPartyForQuickItems && bills.length > 0) {
      // Filter bills by selected party
      const partyBills = bills.filter(bill => bill.party === selectedPartyForQuickItems);
      
      // Aggregate item usage from party-specific bills
      const itemUsage = {};
      
      partyBills.forEach(bill => {
        // Check both bill.items and bill.rows for item data
        const billItems = bill.items || bill.rows || [];
        if (Array.isArray(billItems)) {
          billItems.forEach(item => {
            if (item.item && (item.qty || item.nos) && item.rate) {
              if (!itemUsage[item.item]) {
                itemUsage[item.item] = {
                  itemId: item.item, // Use itemId for consistency
                  id: item.item,
                  count: 0,
                  lastUsed: null,
                  lastRate: 0,
                  lastQty: 0
                };
              }
              
              itemUsage[item.item].count += 1;
              const billDate = bill.invoiceDate || bill.date ? new Date(bill.invoiceDate || bill.date) : new Date();
              
              if (!itemUsage[item.item].lastUsed || billDate > itemUsage[item.item].lastUsed) {
                itemUsage[item.item].lastUsed = billDate;
                itemUsage[item.item].lastRate = parseFloat(item.rate) || 0;
                itemUsage[item.item].lastQty = parseFloat(item.qty || item.nos) || 0;
              }
            }
          });
        }
      });
      
      // Convert to array and sort by usage count, then by last used date
      const sortedItems = Object.values(itemUsage)
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        })
        .slice(0, 20); // Top 20 party-specific items
      
      // Add item names to the party-wise items data
      const itemsWithNames = sortedItems.map(item => {
        const itemDetails = items.find(it => it.id === item.itemId);
        return {
          ...item,
          itemName: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item',
          name: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item'
        };
      });
      
      setPartyWiseItems(itemsWithNames);
    } else if (!selectedPartyForQuickItems) {
      setPartyWiseItems([]);
    } else {
      setPartyWiseItems([]);
    }
  }, [db, userId, isAuthReady, selectedPartyForQuickItems, bills, items]);

  // Auto-generate number for the selected type
  useEffect(() => {
    if (editingBillId) return; // Do not auto-generate when editing
    const prefixMap = { invoice: 'INV', challan: 'CHA', quotation: 'QUO' };
    const prefix = prefixMap[docType] || 'INV';
    const fy = getFinancialYear(invoiceDate);
    const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
    const serials = bills
      .filter(bill => (bill.number || '').startsWith(prefix + fyShort))
      .map(bill => parseInt((bill.number || '').split('/')[1], 10))
      .filter(n => !isNaN(n));
    const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
    setInvoiceNumber(`${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`);
  }, [invoiceDate, bills, docType, editingBillId]);

  // When party changes, update GST for all rows
  useEffect(() => {
    // Recalculate GST split for all rows when party changes
    const updatedRows = rows.map((row, idx) => {
      let itemObj = items.find(it => it.id === row.item);
      const gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
      const sellerGstin = company.gstin || '';
      const partyObj = parties.find(p => p.id === party);
      const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
      const split = getGstTypeAndSplit(sellerGstin, buyerGstin, gstPercent);
      return {
        ...row,
        sgst: split.sgst,
        cgst: split.cgst,
        igst: split.igst,
      };
    });
    // Update GST type for UI feedback
    const updatedGstTypes = rows.map((row, idx) => {
      let itemObj = items.find(it => it.id === row.item);
      const gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
      const sellerGstin = company.gstin || '';
      const partyObj = parties.find(p => p.id === party);
      const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
      const split = getGstTypeAndSplit(sellerGstin, buyerGstin, gstPercent);
      return split.type;
    });
    setRows(updatedRows);
    setGstTypes(updatedGstTypes);
    
    // Update party search text when party changes
    if (party) {
      const selectedParty = parties.find(p => p.id === party);
      if (selectedParty) {
        setPartySearchText(selectedParty.firmName);
      }
    } else {
      setPartySearchText('');
    }
    // eslint-disable-next-line
  }, [party]);

  // Calculate area, amount, GST, and total for each row
  const handleRowChange = (idx, field, value) => {
    setRows(currentRows => {
      const updatedRows = currentRows.map((row, i) => {
        if (i !== idx) return row;
        let updated = { ...row, [field]: value };

        // If editing an existing bill, never map GST fields from item master, even if other fields change
        if (editingBillId && field !== 'item') {
          // Only update the changed field and recalculate total using current GST values
          let itemObj = items.find(it => it.id === row.item);
          const unit = itemObj ? itemObj.quantityMeasurement : '';
          
          // Ensure unit is set in the row if item has quantityMeasurement
          if (itemObj && itemObj.quantityMeasurement) {
            updated.unit = itemObj.quantityMeasurement;
            updated.quantityMeasurement = itemObj.quantityMeasurement;
          }
          
          // Compute quantity from qtyExpr if present/valid; else fallback to Nos×Length×Height
          const parsed = parseQtyExpression(updated.qtyExpr);
          let computedQty;
          if (parsed.ok) {
            computedQty = parsed.value;
            updated.qtyDisplay = parsed.display;
          } else {
            computedQty = (parseFloat(updated.nos) || 0) * (parseFloat(updated.length) || 1) * (parseFloat(updated.height) || 1);
            if (!updated.qtyExpr) {
              const a = parseFloat(updated.nos) || 0; const b = parseFloat(updated.length) || 1; const c = parseFloat(updated.height) || 1;
              updated.qtyDisplay = `${a}×${b}×${c}`;
            }
          }
          updated.qty = computedQty;
          // Keep nos in sync for legacy stock logic
          updated.nos = computedQty;
          updated.amount = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
          // Compute effective amount after per-line discount
          const rawAmount = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
          let lineDiscAmt = 0;
          if ((updated.lineDiscountType || 'amount') === 'percent') {
            lineDiscAmt = rawAmount * ((parseFloat(updated.lineDiscountValue) || 0) / 100);
          } else {
            lineDiscAmt = parseFloat(updated.lineDiscountValue) || 0;
          }
          lineDiscAmt = Math.min(Math.max(lineDiscAmt, 0), rawAmount);
          updated.amount = roundTo2Decimals(rawAmount - lineDiscAmt);

          // Always use the GST values already present in the row
          updated.sgst = row.sgst;
          updated.cgst = row.cgst;
          updated.igst = row.igst;
          
          // GST calculation based on bill-wise GST calculation type
          let taxableAmount, gstAmount;
          const gstPercent = (parseFloat(row.sgst) || 0) + (parseFloat(row.cgst) || 0) + (parseFloat(row.igst) || 0);
          
          if (gstCalculationType === 'including' && gstPercent > 0) {
            // Rate includes GST - calculate taxable amount first
            const totalAmount = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
            taxableAmount = totalAmount / (1 + gstPercent / 100);
            gstAmount = totalAmount - taxableAmount;
            updated.amount = roundTo2Decimals(taxableAmount);
          } else {
            // Rate excludes GST - calculate GST on taxable amount
            taxableAmount = updated.amount;
            gstAmount = (taxableAmount * gstPercent) / 100;
          }
          
          // Calculate individual GST amounts
          const sgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(row.sgst) || 0)) / gstPercent : 0;
          const cgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(row.cgst) || 0)) / gstPercent : 0;
          const igstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(row.igst) || 0)) / gstPercent : 0;
          
          updated.total = roundTo2Decimals(taxableAmount + gstAmount);
          return updated;
        }

        // If user edits SGST, CGST, or IGST directly, update only that field and recalculate total
        if (["sgst", "cgst", "igst"].includes(field)) {
          // GST calculation based on bill-wise GST calculation type
          let taxableAmount, gstAmount;
          const gstPercent = (parseFloat(updated.sgst) || 0) + (parseFloat(updated.cgst) || 0) + (parseFloat(updated.igst) || 0);
          
          if (gstCalculationType === 'including' && gstPercent > 0) {
            // Rate includes GST - calculate taxable amount first
            const totalAmount = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
            taxableAmount = totalAmount / (1 + gstPercent / 100);
            gstAmount = totalAmount - taxableAmount;
            updated.amount = roundTo2Decimals(taxableAmount);
          } else {
            // Rate excludes GST - calculate GST on taxable amount
            taxableAmount = updated.amount;
            gstAmount = (taxableAmount * gstPercent) / 100;
          }
          
          // Calculate individual GST amounts
          const sgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.sgst) || 0)) / gstPercent : 0;
          const cgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.cgst) || 0)) / gstPercent : 0;
          const igstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.igst) || 0)) / gstPercent : 0;
          
          updated.total = roundTo2Decimals(taxableAmount + gstAmount);
          return updated;
        }

        // If item is changed (new bill or edit), fetch GST from item master
        if (field === 'item') {
          let itemObj = items.find(it => it.id === value);
          updated.gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
          
          // Set unit and quantityMeasurement from item
          if (itemObj) {
            updated.unit = itemObj.quantityMeasurement || '';
            updated.quantityMeasurement = itemObj.quantityMeasurement || '';
            
            // Auto-populate tags from item master: defaultServiceTags (Items form) and legacy item.tags
            const fromDefaults = Array.isArray(itemObj.defaultServiceTags)
              ? itemObj.defaultServiceTags.map(normalizeTagName).filter(Boolean)
              : [];
            const fromLegacy = Array.isArray(itemObj.tags)
              ? itemObj.tags.map((t) => normalizeTagName(t)).filter(Boolean)
              : [];
            const fromItem = [...new Set([...fromDefaults, ...fromLegacy])];
            const existingTags = updated.tags || [];
            const existingLower = new Set(existingTags.map((t) => String(t).toLowerCase()));
            const newTagNames = [];
            fromItem.forEach((t) => {
              if (t && !existingLower.has(t)) {
                newTagNames.push(t);
                existingLower.add(t);
              }
            });
            updated.tags = [...existingTags, ...newTagNames];
            let services = Array.isArray(updated.services) ? [...updated.services] : [];
            const svcNameSet = new Set(
              services.map((s) => normalizeTagName(s.serviceName || ''))
            );
            newTagNames.forEach((tagLower) => {
              if (!svcNameSet.has(tagLower)) {
                services.push(createDefaultServiceEntry(tagLower));
                svcNameSet.add(tagLower);
              }
            });
            updated.services = services;
          } else {
            // If item is cleared, keep existing tags (don't clear them)
            if (!updated.tags) {
              updated.tags = [];
            }
          }
          
          // Default qty and rate on item select
          if (!updated.nos) updated.nos = 1;
          // Prefer last party-wise rate, else item sale price
          const partyId = party;
          let defaultRate = 0;
          try {
            // Search latest sales bill for this party and item
            for (let bIndex = bills.length - 1; bIndex >= 0; bIndex -= 1) {
              const bill = bills[bIndex];
              if ((bill.partyId || bill.party) !== partyId) continue;
              const foundRow = (bill.rows || []).find(r => r.item === value);
              if (foundRow && foundRow.rate) { defaultRate = parseFloat(foundRow.rate) || 0; break; }
            }
          } catch (e) { /* no-op */ }
          if (!defaultRate && itemObj) {
            defaultRate = parseFloat(itemObj.salePrice || itemObj.rate || 0) || 0;
          }
          if (!updated.rate || updated.rate === 0) {
            updated.rate = defaultRate;
          }
          const sellerGstin = company.gstin || '';
          const partyObj = parties.find(p => p.id === party);
          const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
          if (sellerGstin && buyerGstin && sellerGstin.length >= 2 && buyerGstin.length >= 2) {
            if (sellerGstin.substring(0, 2) === buyerGstin.substring(0, 2)) {
              updated.sgst = updated.gstPercent / 2;
              updated.cgst = updated.gstPercent / 2;
              updated.igst = 0;
            } else {
              updated.sgst = 0;
              updated.cgst = 0;
              updated.igst = updated.gstPercent;
            }
          } else {
            updated.sgst = updated.gstPercent / 2;
            updated.cgst = updated.gstPercent / 2;
            updated.igst = 0;
          }
        }

        // For all other fields (new bill), preserve GST values and just recalculate total
        let itemObj = items.find(it => it.id === (field === 'item' ? value : row.item));
        const unit = itemObj ? itemObj.quantityMeasurement : '';
        
        // Set unit and quantityMeasurement in the row whenever item is present
        if (itemObj && (field === 'item' || !updated.unit)) {
          updated.unit = unit;
          updated.quantityMeasurement = unit;
        }
        
        // Compute quantity from qtyExpr if present/valid; else fallback to Nos×Length×Height
        const parsed = parseQtyExpression(updated.qtyExpr);
        let computedQty;
        if (parsed.ok) {
          computedQty = parsed.value;
          updated.qtyDisplay = parsed.display;
        } else {
          computedQty = (parseFloat(updated.nos) || 0) * (parseFloat(updated.length) || 1) * (parseFloat(updated.height) || 1);
          if (!updated.qtyExpr) {
            const a = parseFloat(updated.nos) || 0; const b = parseFloat(updated.length) || 1; const c = parseFloat(updated.height) || 1;
            updated.qtyDisplay = `${a}×${b}×${c}`;
          }
        }
        updated.qty = computedQty;
        // Keep nos in sync for legacy stock logic
        updated.nos = computedQty;
        // Apply per-line discount to amount
        const rawAmount = calculateLineTotal(updated.qty, updated.rate);
        let lineDiscAmt = 0;
        if ((updated.lineDiscountType || 'amount') === 'percent') {
          lineDiscAmt = calculateGSTAmount(rawAmount, updated.lineDiscountValue);
        } else {
          lineDiscAmt = roundTo2Decimals(parseFloat(updated.lineDiscountValue) || 0);
        }
        lineDiscAmt = Math.min(Math.max(lineDiscAmt, 0), rawAmount);
        updated.amount = roundTo2Decimals(rawAmount - lineDiscAmt);
        // GST and total calculation based on bill-wise GST calculation type
        let taxableAmount, gstAmount;
        const gstPercent = (parseFloat(updated.sgst) || 0) + (parseFloat(updated.cgst) || 0) + (parseFloat(updated.igst) || 0);
        
        if (gstCalculationType === 'including' && gstPercent > 0) {
          // Rate includes GST - calculate taxable amount first
          const totalAmount = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
          taxableAmount = totalAmount / (1 + gstPercent / 100);
          gstAmount = totalAmount - taxableAmount;
          updated.amount = roundTo2Decimals(taxableAmount);
        } else {
          // Rate excludes GST - calculate GST on taxable amount
          taxableAmount = updated.amount;
          gstAmount = (taxableAmount * gstPercent) / 100;
        }
        
        // Calculate individual GST amounts
        const sgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.sgst) || 0)) / gstPercent : 0;
        const cgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.cgst) || 0)) / gstPercent : 0;
        const igstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.igst) || 0)) / gstPercent : 0;
        
        updated.total = roundTo2Decimals(taxableAmount + gstAmount);
        return updated;
      });

      // Auto-add trailing row when user fills the last visible row
      const isRowNonEmpty = (r) => {
        return !!(r.item || (parseFloat(r.rate) || 0) || (r.qtyExpr && String(r.qtyExpr).trim()) || (parseFloat(r.qty) || 0) > 0 || (parseFloat(r.nos) || 0) > 1 || (parseFloat(r.length) || 0) || (parseFloat(r.height) || 0) || (parseFloat(r.lineDiscountValue) || 0));
      };
      const last = updatedRows[updatedRows.length - 1];
      if (isRowNonEmpty(last)) {
        updatedRows.push({ ...initialItemRow });
      }
      // Ensure at least 5 rows visible
      while (updatedRows.length < 5) updatedRows.push({ ...initialItemRow });

      return updatedRows;
    });
  };

  const addRow = () => {
    setRows([...rows, { ...initialItemRow }]);
    // Move to the new item in mobile view
    setCurrentMobileItemIndex(rows.length);
  };
  
  // ADD ITEM Modal functions
  const clearAddItemForm = () => {
    setNewItemName('');
    setNewItemMeasurement('');
    setNewItemRate('');
    setNewItemType('Service');
    setNewItemHsnCode('');
    setNewItemGstPercentage('');
    setNewItemDescription('');
    setNewItemPurchasePrice('0');
    setNewItemSalePrice('0');
    setNewItemCurrentStock('0');
    setNewItemIsActive(true);
    setNewItemCustomUnit('');
    setAddItemMessage('');
  };

  const handleAddItem = async () => {
    if (!db || !userId) {
      setAddItemMessage("Firebase not initialized or user not authenticated.");
      return;
    }
    if (!newItemName) {
      setAddItemMessage("Item Name is required.");
      return;
    }
    if (!newItemMeasurement) {
      setAddItemMessage("Quantity Measurement is required.");
      return;
    }

    try {
      const itemsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
      const itemData = {
        itemName: newItemName,
        quantityMeasurement: newItemCustomUnit || newItemMeasurement,
        defaultRate: parseFloat(newItemRate) || 0,
        itemType: newItemType,
        hsnCode: newItemHsnCode,
        gstPercentage: parseFloat(newItemGstPercentage) || 0,
        description: newItemDescription,
        purchasePrice: parseFloat(newItemPurchasePrice) || 0,
        salePrice: parseFloat(newItemSalePrice) || 0,
        currentStock: parseFloat(newItemCurrentStock) || 0,
        sgstRate: 0,
        cgstRate: 0,
        isActive: newItemIsActive,
        timestamp: serverTimestamp()
      };

      await addDoc(itemsCollectionRef, itemData);
      setAddItemMessage("Item added successfully!");
      clearAddItemForm();
      setShowAddItemModal(false);
    } catch (error) {
      console.error("Error adding item:", error);
      setAddItemMessage("Error adding item. Please try again.");
    }
  };

  const removeRow = idx => {
    setRows(rows.filter((_, i) => i !== idx));
    // Adjust mobile carousel index
    if (currentMobileItemIndex >= rows.length - 1) {
      setCurrentMobileItemIndex(Math.max(0, currentMobileItemIndex - 1));
    }
  };

  // Tag management functions
  const getRowServices = (row) => Array.isArray(row?.services) ? row.services.map(normalizeServiceEntry) : [];

  const getServiceForTag = (row, tag) => {
    const tagLower = String(tag || '').trim().toLowerCase();
    if (!tagLower) return null;
    const services = getRowServices(row);
    return services.find(service => service.serviceName === tagLower) || null;
  };

  const getTagDisplayText = (row, tag) => {
    const service = getServiceForTag(row, tag);
    if (service?.vendorName) {
      return `${tag} • ${service.vendorName}`;
    }
    return tag;
  };

  const openServiceEditor = (rowIndex, tag) => {
    const tagLower = String(tag || '').trim().toLowerCase();
    if (!tagLower) return;
    const row = rows[rowIndex] || {};
    const existingService = getServiceForTag(row, tagLower);
    setServiceEditorContext({ rowIndex, serviceName: tagLower });
    setServiceEditorForm(existingService ? normalizeServiceEntry(existingService) : createDefaultServiceEntry(tagLower));
    setShowServiceEditorModal(true);
  };

  const closeServiceEditor = () => {
    setShowServiceEditorModal(false);
    setServiceEditorContext({ rowIndex: null, serviceName: '' });
    setServiceEditorForm(createDefaultServiceEntry());
  };

  const saveServiceEditor = () => {
    const rowIndex = serviceEditorContext.rowIndex;
    const serviceName = String(serviceEditorForm.serviceName || serviceEditorContext.serviceName || '').trim().toLowerCase();
    if (rowIndex === null || rowIndex === undefined || !serviceName) {
      closeServiceEditor();
      return;
    }

    const selectedVendor = parties.find(p => p.id === serviceEditorForm.vendorId);
    const normalizedService = normalizeServiceEntry({
      ...serviceEditorForm,
      serviceName,
      vendorName: selectedVendor?.firmName || serviceEditorForm.vendorName || '',
      internalCost: serviceEditorForm.internalCost === '' ? '' : parseFloat(serviceEditorForm.internalCost) || 0,
      customerCharge: serviceEditorForm.customerCharge === '' ? '' : parseFloat(serviceEditorForm.customerCharge) || 0,
    });

    setRows(currentRows => currentRows.map((row, idx) => {
      if (idx !== rowIndex) return row;
      const normalizedTags = Array.isArray(row.tags) ? row.tags : [];
      const normalizedServices = getRowServices(row);
      const updatedTags = normalizedTags.some(t => String(t).toLowerCase() === serviceName)
        ? normalizedTags
        : [...normalizedTags, serviceName];
      const existingIndex = normalizedServices.findIndex(service => service.serviceName === serviceName);
      let updatedServices = [...normalizedServices];
      if (existingIndex >= 0) {
        updatedServices[existingIndex] = { ...updatedServices[existingIndex], ...normalizedService };
      } else {
        updatedServices.push(normalizedService);
      }
      return { ...row, tags: updatedTags, services: updatedServices };
    }));

    closeServiceEditor();
  };

  const getItemDefaultServiceTags = (row) => {
    if (!row?.item) return [];
    const itemObj = items.find((it) => it.id === row.item);
    const arr = itemObj?.defaultServiceTags;
    if (!Array.isArray(arr)) return [];
    return Array.from(new Set(arr.map((t) => normalizeTagName(t)).filter(Boolean)));
  };

  const getAllKnownTags = () => {
    const tagSet = collectLegacyTagsFromRowsAndBills(rows, bills);
    serviceTagMaster.forEach((t) => {
      if (t.isActive === false) return;
      const n = t.normalizedName ? normalizeTagName(t.normalizedName) : normalizeTagName(t.name);
      if (n) tagSet.add(n);
    });
    return Array.from(tagSet).sort();
  };

  const getTagPickerBuckets = (rowIndex) => {
    const row = rows[rowIndex];
    const search = (tagSearchText[rowIndex] || '').toLowerCase().trim();
    const all = getAllKnownTags();
    const itemDefaults = getItemDefaultServiceTags(row);
    const rowTagsLower = new Set((row?.tags || []).map((t) => String(t).toLowerCase()));
    const suggested = itemDefaults.filter((t) => !rowTagsLower.has(t) && (!search || t.includes(search)));
    const suggestedSet = new Set(suggested);
    const others = all.filter((t) => !suggestedSet.has(t) && (!search || t.includes(search)));
    return { suggested, others };
  };

  const upsertServiceTagToFirestore = async (rawName) => {
    const normalized = normalizeTagName(rawName);
    if (!normalized || !db || !companyId) return;
    const id = tagDocIdFromName(normalized);
    const col = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    if (!col) return;
    const ref = doc(col, id);
    const snap = await getDoc(ref);
    const payload = {
      normalizedName: normalized,
      name: normalized,
      isActive: true,
      source: 'sales-dev',
      updatedAt: serverTimestamp(),
    };
    if (!snap.exists()) payload.createdAt = serverTimestamp();
    await setDoc(ref, payload, { merge: true });
  };

  const pinTagToItemDefault = async (row, tagLower) => {
    if (!row?.item) return false;
    const itemObj = items.find((it) => it.id === row.item);
    const existing = Array.isArray(itemObj?.defaultServiceTags)
      ? itemObj.defaultServiceTags.map(normalizeTagName).filter(Boolean)
      : [];
    if (existing.includes(tagLower)) return false;
    const col = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
    if (!col) return false;
    const itemRef = doc(col, row.item);
    await updateDoc(itemRef, { defaultServiceTags: [...existing, tagLower] });
    return true;
  };

  const handleAddTag = async (rowIndex, tag, openDetails = false) => {
    if (!tag || !tag.trim()) return;
    const tagLower = tag.toLowerCase().trim();
    const rowBefore = rows[rowIndex];
    const shouldPin = !!pinTagForItem[rowIndex] && !!rowBefore?.item;

    let addedOrExists = false;
    setRows((currentRows) =>
      currentRows.map((row, i) => {
        if (i !== rowIndex) return row;
        const existingTags = row.tags || [];
        const existingServices = getRowServices(row);
        const hasServiceForTag = existingServices.some((service) => service.serviceName === tagLower);
        const ensuredServices = hasServiceForTag ? existingServices : [...existingServices, createDefaultServiceEntry(tagLower)];
        if (existingTags.some((t) => t.toLowerCase() === tagLower)) {
          addedOrExists = true;
          return { ...row, services: ensuredServices };
        }
        addedOrExists = true;
        return { ...row, tags: [...existingTags, tagLower], services: ensuredServices };
      })
    );
    setTagSearchText((prev) => ({ ...prev, [rowIndex]: '' }));
    setShowTagPicker((prev) => ({ ...prev, [rowIndex]: false }));
    setPinTagForItem((prev) => ({ ...prev, [rowIndex]: false }));

    try {
      await upsertServiceTagToFirestore(tagLower);
      if (shouldPin) {
        const pinned = await pinTagToItemDefault(rowBefore, tagLower);
        if (pinned) toast.success('Tag saved to item defaults for quick entry.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Could not sync tag to registry');
    }

    if (addedOrExists && openDetails) {
      openServiceEditor(rowIndex, tagLower);
    }
  };

  const handleRemoveTag = (rowIndex, tagIndex) => {
    const row = rows[rowIndex];
    const removedTag = row?.tags?.[tagIndex];
    setRows(currentRows => {
      const updatedRows = currentRows.map((row, i) => {
        if (i !== rowIndex) return row;
        const existingTags = row.tags || [];
        const nextTags = existingTags.filter((_, idx) => idx !== tagIndex);
        const removedTagLower = String(removedTag || '').trim().toLowerCase();
        const services = getRowServices(row).filter(service => service.serviceName !== removedTagLower);
        return { ...row, tags: nextTags, services };
      });
      return updatedRows;
    });
  };

  const handleTagInputChange = (rowIndex, value) => {
    setTagSearchText(prev => ({ ...prev, [rowIndex]: value }));
  };

  const toggleTagPicker = (rowIndex) => {
    const willOpen = !showTagPicker[rowIndex];
    setShowTagPicker((prev) => ({ ...prev, [rowIndex]: !prev[rowIndex] }));
    if (willOpen) {
      setTagSearchText((prev) => ({ ...prev, [rowIndex]: '' }));
      setPinTagForItem((prev) => ({ ...prev, [rowIndex]: false }));
    }
  };

  const deactivateServiceTag = async (tagDoc) => {
    if (!db || !companyId || !tagDoc?.id) return;
    const col = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    if (!col) return;
    try {
      await updateDoc(doc(col, tagDoc.id), { isActive: false, updatedAt: serverTimestamp() });
      toast.success('Tag deactivated.');
    } catch (e) {
      console.error(e);
      toast.error('Could not deactivate tag');
    }
  };

  const restoreServiceTag = async (tagDoc) => {
    if (!db || !companyId || !tagDoc?.id) return;
    const col = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    if (!col) return;
    try {
      await updateDoc(doc(col, tagDoc.id), { isActive: true, updatedAt: serverTimestamp() });
      toast.success('Tag restored.');
    } catch (e) {
      console.error(e);
      toast.error('Could not restore tag');
    }
  };

  const handleManageAddTag = async () => {
    const raw = manageTagsNewName.trim();
    if (!raw) return;
    try {
      await upsertServiceTagToFirestore(raw);
      setManageTagsNewName('');
      toast.success('Tag added.');
    } catch (e) {
      console.error(e);
      toast.error('Could not add tag');
    }
  };

  const renderTagPickerDropdown = (rowIndex) => {
    const buckets = getTagPickerBuckets(rowIndex);
    const empty = buckets.suggested.length === 0 && buckets.others.length === 0;
    return (
      <>
        <div className="px-3 py-2.5 bg-slate-50 border-b border-slate-100 text-xs sm:text-sm text-slate-600 leading-relaxed">
          Search or pick a tag. New names are saved to your company list. Use <span className="font-medium text-slate-800">Add + Details</span> to open vendor and cost fields.
        </div>
        <input
          type="text"
          value={tagSearchText[rowIndex] || ''}
          onChange={(e) => handleTagInputChange(rowIndex, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && tagSearchText[rowIndex]?.trim()) {
              void handleAddTag(rowIndex, tagSearchText[rowIndex]);
            } else if (e.key === 'Escape') {
              setShowTagPicker((prev) => ({ ...prev, [rowIndex]: false }));
            }
          }}
          placeholder="Type to search or create a tag…"
          className="w-full min-h-[44px] sm:min-h-0 px-3 py-2.5 sm:py-2.5 border-b border-gray-200 text-base sm:text-sm touch-manipulation placeholder:text-gray-400"
          autoFocus
        />
        {rows[rowIndex]?.item && (
          <label className="flex items-start gap-3 px-3 py-2.5 sm:py-2 text-xs sm:text-xs text-gray-600 border-b border-gray-100 cursor-pointer hover:bg-gray-50 touch-manipulation">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
              checked={!!pinTagForItem[rowIndex]}
              onChange={(e) => setPinTagForItem((prev) => ({ ...prev, [rowIndex]: e.target.checked }))}
            />
            <span className="leading-snug">Also save to this item&apos;s defaults for quick entry</span>
          </label>
        )}
        <div className="max-h-[min(50dvh,14rem)] sm:max-h-52 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
          {buckets.suggested.length > 0 && (
            <>
              <div className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                Suggested for this item
              </div>
              {buckets.suggested.map((tag) => (
                <button
                  key={`s-${tag}`}
                  type="button"
                  onClick={() => void handleAddTag(rowIndex, tag)}
                  className="w-full min-h-[44px] sm:min-h-0 text-left px-3 py-3 sm:py-2 hover:bg-emerald-50 active:bg-emerald-100 text-base sm:text-sm touch-manipulation"
                >
                  <span className="break-words">{tag}</span>
                </button>
              ))}
            </>
          )}
          {buckets.others.length > 0 && (
            <>
              {buckets.suggested.length > 0 && <div className="border-t border-gray-100" />}
              <div className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">All tags</div>
              {buckets.others.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => void handleAddTag(rowIndex, tag)}
                  className="w-full min-h-[44px] sm:min-h-0 text-left px-3 py-3 sm:py-2 hover:bg-gray-100 active:bg-gray-200 text-base sm:text-sm touch-manipulation"
                >
                  <span className="break-words">{tag}</span>
                </button>
              ))}
            </>
          )}
          {empty && (
            <div className="px-3 py-3 text-sm text-gray-500">No tags found</div>
          )}
        </div>
        {tagSearchText[rowIndex]?.trim() && (
          <div className="p-2 border-t border-gray-200 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => void handleAddTag(rowIndex, tagSearchText[rowIndex], false)}
              className="flex-1 min-h-[44px] sm:min-h-0 text-sm px-3 py-2.5 sm:py-2 rounded border border-gray-300 bg-white hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
            >
              Add Tag Only
            </button>
            <button
              type="button"
              onClick={() => void handleAddTag(rowIndex, tagSearchText[rowIndex], true)}
              className="flex-1 min-h-[44px] sm:min-h-0 text-sm px-3 py-2.5 sm:py-2 rounded bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 touch-manipulation"
            >
              Add + Details
            </button>
          </div>
        )}
      </>
    );
  };

  // Mobile carousel navigation functions
  const goToPreviousItem = () => {
    setCurrentMobileItemIndex(Math.max(0, currentMobileItemIndex - 1));
  };

  const goToNextItem = () => {
    setCurrentMobileItemIndex(Math.min(rows.length - 1, currentMobileItemIndex + 1));
  };

  const goToItem = (index) => {
    setCurrentMobileItemIndex(index);
  };

  // Touch event handlers for swipe gestures
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentMobileItemIndex < rows.length - 1) {
      goToNextItem();
    }
    if (isRightSwipe && currentMobileItemIndex > 0) {
      goToPreviousItem();
    }
  };

  // ADD PARTY Modal functions
  const clearAddPartyForm = () => {
    setNewPartyName('');
    setNewPartyGstin('');
    setNewPartyAddress('');
    setNewPartyPhone('');
    setNewPartyEmail('');
    setNewPartyGstType('Unregistered');
    setNewPartyOpeningBalance('');
    setNewPartyCreditLimit('');
    setNewPartyCreditPeriod('');
    setShowAdvancedFields(false);
    setAddPartyMessage('');
  };

  const handleAddParty = async () => {
    if (!db || !userId) {
      setAddPartyMessage("Firebase not initialized or user not authenticated.");
      return;
    }

    if (!newPartyName.trim()) {
      setAddPartyMessage("Party name is required.");
      return;
    }

    // Validate GSTIN for Regular parties
    if (newPartyGstType === 'Regular' && !newPartyGstin.trim()) {
      setAddPartyMessage("GSTIN is mandatory for Regular parties.");
      return;
    }

    try {
      const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      const partyData = {
        firmName: newPartyName.trim(),
        personName: '',
        address: newPartyAddress.trim(),
        contact: newPartyPhone.trim(),
        email: newPartyEmail.trim(),
        whatsapp: newPartyPhone.trim(),
        gstin: newPartyGstin.trim(),
        gstType: newPartyGstType,
        pan: '',
        city: '',
        stateName: '',
        pincode: '',
        creditPeriod: parseFloat(newPartyCreditPeriod) || 0,
        creditLimit: parseFloat(newPartyCreditLimit) || 0,
        openingBalance: parseFloat(newPartyOpeningBalance) || 0,
        partyType: 'Buyer',
        useManualBalance: true,
        manualBalance: 0,
        manualBalanceDate: new Date().toISOString().split('T')[0],
        manualBalanceRemarks: '',
        createdAt: serverTimestamp(),
        createdBy: userId,
        companyId: companyId
      };

      const docRef = await addDoc(partiesCollectionRef, partyData);
      setAddPartyMessage("Party added successfully!");
      
      // Auto-select the newly added party
      setParty(docRef.id);
      setSelectedPartyForQuickItems(docRef.id);
      setPartySearchText(newPartyName.trim());
      
      clearAddPartyForm();
      setShowAddPartyModal(false);
    } catch (error) {
      console.error("Error adding party:", error);
      setAddPartyMessage("Error adding party. Please try again.");
    }
  };

  // Zoom detection utility
  const getZoomLevel = () => {
    if (typeof window !== 'undefined') {
      return window.devicePixelRatio || 1;
    }
    return 1;
  };

  // Update scrollbar size based on zoom level
  useEffect(() => {
    const updateScrollbarSize = () => {
      const zoomLevel = getZoomLevel();
      const tableContainer = document.querySelector('.custom-scrollbar');
      if (tableContainer) {
        // Add zoom-specific class for CSS targeting
        tableContainer.classList.remove('zoom-1', 'zoom-1-5', 'zoom-2', 'zoom-3');
        if (zoomLevel >= 3) {
          tableContainer.classList.add('zoom-3');
        } else if (zoomLevel >= 2) {
          tableContainer.classList.add('zoom-2');
        } else if (zoomLevel >= 1.5) {
          tableContainer.classList.add('zoom-1-5');
        } else {
          tableContainer.classList.add('zoom-1');
        }
      }
    };

    updateScrollbarSize();
    window.addEventListener('resize', updateScrollbarSize);
    return () => window.removeEventListener('resize', updateScrollbarSize);
  }, []);

  // Calculate modal position based on button click
  const calculateModalPosition = (buttonElement) => {
    if (!buttonElement) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const modalHeight = Math.min(viewportHeight * 0.85, 600); // Estimated modal height
    const modalWidth = Math.min(viewportWidth - 32, 672); // max-w-2xl = 672px, with 16px margin on each side
    
    let top = rect.bottom + 8; // 8px below the button
    let left = rect.left;
    
    // Adjust if modal would go below viewport
    if (top + modalHeight > viewportHeight - 16) {
      top = Math.max(16, rect.top - modalHeight - 8); // 8px above button, min 16px from top
    }
    
    // Adjust if modal would go beyond right edge
    if (left + modalWidth > viewportWidth - 16) {
      left = viewportWidth - modalWidth - 16;
    }
    
    // Adjust if modal would go beyond left edge
    if (left < 16) {
      left = 16;
    }
    
    return {
      top: `${top}px`,
      left: `${left}px`,
      transform: 'none'
    };
  };

  // Quick items helper functions
  const handleQuickItemClick = (itemData, isPartySpecific = false) => {
    // Use itemId consistently - check both itemId and id fields
    const targetItemId = itemData.itemId || itemData.id;
    if (!targetItemId) {
      return;
    }
    
    // Find the item details from items array
    const itemDetails = items.find(item => item.id === targetItemId);
    if (!itemDetails) {
      return;
    }
    
    // Find an empty row or add a new one
    let targetRowIndex = rows.findIndex(row => !row.item && !row.itemId);
    if (targetRowIndex === -1) {
      // Add new row if all rows are filled
      setRows(prev => [...prev, { ...initialItemRow }]);
      targetRowIndex = rows.length;
    }
    
    // Calculate the amount
    const rate = itemData.lastRate || itemDetails.salePrice || itemDetails.rate || 0;
    const qty = itemData.lastQty || 1;
    const amount = rate * qty;
    
    // Update the row with item details using handleRowChange to ensure proper calculations
    // First set the item to trigger GST calculations
    handleRowChange(targetRowIndex, 'item', targetItemId);
    
    // Then set the rate and quantity in sequence to ensure proper calculations
    setTimeout(() => {
      handleRowChange(targetRowIndex, 'rate', rate.toString());
    }, 50);
    
    setTimeout(() => {
      handleRowChange(targetRowIndex, 'qty', qty.toString());
    }, 100);
    
    // Update item search text for this row
    setItemSearchTextByRow(prev => ({
      ...prev,
      [targetRowIndex]: itemDetails.itemName || itemDetails.name || 'Unknown Item'
    }));
  };

  const toggleEwayBill = () => {
    const newValue = !showEwayBill;
    setShowEwayBill(newValue);
    if (typeof window !== 'undefined') {
      localStorage.setItem('showEwayBill', JSON.stringify(newValue));
    }
  };

  const toggleMobileDrawer = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  const isMobileView = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  };

  // Mobile-specific invoice modal handler
  const handleMobileInvoiceView = (bill) => {
    console.log('Company data for seal/sign and GST:', {
      company: company,
      currentCompany: currentCompany,
      companySealUrl: company?.sealUrl,
      companySignUrl: company?.signUrl,
      currentCompanySealUrl: currentCompany?.sealUrl,
      currentCompanySignUrl: currentCompany?.signUrl,
      detailsSealUrl: currentCompany?.details?.sealUrl,
      detailsSignUrl: currentCompany?.details?.signUrl,
      brandingSealUrl: currentCompany?.branding?.sealUrl,
      brandingSignUrl: currentCompany?.branding?.signatureUrl,
      finalSealUrl: (currentCompany || company)?.sealUrl || (currentCompany || company)?.details?.sealUrl || (currentCompany || company)?.branding?.sealUrl,
      finalSignUrl: (currentCompany || company)?.signatureUrl || (currentCompany || company)?.signUrl || (currentCompany || company)?.details?.signUrl || (currentCompany || company)?.branding?.signatureUrl,
      companyGstinType: company?.gstinType,
      currentCompanyGstinType: currentCompany?.gstinType,
      companyGstType: company?.gstType,
      currentCompanyGstType: currentCompany?.gstType,
      companyBasicInfoGstType: company?.basicInfo?.gstType,
      currentCompanyBasicInfoGstType: currentCompany?.basicInfo?.gstType,
      finalGstinType: (currentCompany || company)?.gstType || company?.gstType || (currentCompany || company)?.basicInfo?.gstType || company?.basicInfo?.gstType || (currentCompany || company)?.gstinType || company?.gstinType || 'Unregistered',
      finalSignUrl: (currentCompany || company)?.signUrl || company?.signUrl || (currentCompany || company)?.details?.signUrl || company?.details?.signUrl || (currentCompany || company)?.branding?.signatureUrl || company?.branding?.signatureUrl,
      finalSealUrl: (currentCompany || company)?.sealUrl || company?.sealUrl || (currentCompany || company)?.details?.sealUrl || company?.details?.sealUrl || (currentCompany || company)?.branding?.sealUrl || company?.branding?.sealUrl
    });
    setInvoiceBill(bill);
    setShowInvoiceModal(true);
    
    // Ensure mobile viewport is properly set
    if (isMobileView()) {
      // Force viewport positioning for mobile
      setTimeout(() => {
        const modal = document.querySelector('[class*="fixed"][class*="inset-0"][class*="bg-black"][class*="bg-opacity-50"]');
        if (modal) {
          modal.style.position = 'fixed';
          modal.style.top = '0';
          modal.style.left = '0';
          modal.style.right = '0';
          modal.style.bottom = '0';
          modal.style.width = '100vw';
          modal.style.height = '100vh';
          modal.style.zIndex = '9999';
          modal.style.display = 'flex';
          modal.style.alignItems = 'stretch';
          modal.style.justifyContent = 'center';
          modal.style.margin = '0';
          modal.style.padding = '0';
          modal.style.transform = 'none';
        }
      }, 100);
    }
  };

  const getResponsiveColumns = () => {
    if (typeof window !== 'undefined') {
      const screenWidth = window.innerWidth;
      if (screenWidth >= 1920) return 3; // Large desktop
      if (screenWidth >= 1366) return 2; // Medium desktop
      return 1; // Small desktop/mobile
    }
    return 1; // Default fallback
  };

  // Filter functions for sales bill list
  const clearFilters = () => {
    setFilterSearch('');
    setFilterDate('');
    setFilterAmount('');
    setFilterTag('');
  };

  // Handle filter changes from CompactFilterBar
  const handleFilterChange = (filters) => {
    setFilterSearch(filters.search || '');
    setFilterDate(filters.date || '');
    setFilterAmount(filters.amount || '');
    setFilterTag(filters.tag || '');
  };

  // Get the number label for the current document type
  const numberLabel = docTypeOptions.find(opt => opt.value === docType)?.numberLabel || 'Invoice Number';


  // Update bill list and filters to use bills state
  const getFilteredBills = () => {
    return bills.filter(bill => {
      const partyName = getPartyName(bill.party);
      let billDate = bill.invoiceDate || bill.date || "";
      if (billDate && billDate.includes("-")) {
        const [yyyy, mm, dd] = billDate.split("-");
        billDate = `${dd}/${mm}/${yyyy}`;
      }
      const billAmount = typeof bill.amount === "number" ? bill.amount.toFixed(2) : bill.amount?.toString() || '';
      
      // Combined search for bill number and party name
      const searchMatch = filterSearch === '' || (() => {
        const searchTerms = filterSearch.toLowerCase().trim().split(/\s+/);
        const billNumber = (bill.number || '').toLowerCase();
        const partyNameLower = partyName.toLowerCase();
        
        // Check if all search terms match either bill number or party name
        return searchTerms.every(term => 
          billNumber.includes(term) || partyNameLower.includes(term)
        );
      })();
      
      // Tag filter - check if bill has the tag in tagSummary or in any row
      const tagMatch = filterTag === '' || (() => {
        const tagLower = filterTag.toLowerCase().trim();
        // Check tagSummary first (faster)
        if (bill.tagSummary && Array.isArray(bill.tagSummary)) {
          if (bill.tagSummary.some(t => t.toLowerCase() === tagLower)) {
            return true;
          }
        }
        // Fallback: check rows for tags
        if (bill.rows && Array.isArray(bill.rows)) {
          return bill.rows.some(row => {
            if (row.tags && Array.isArray(row.tags)) {
              return row.tags.some(t => t.toLowerCase() === tagLower);
            }
            return false;
          });
        }
        return false;
      })();
      
      return (
        searchMatch &&
        (filterDate === '' || billDate.includes(filterDate)) &&
        (filterAmount === '' || billAmount.includes(filterAmount)) &&
        tagMatch
      );
    });
  };

  // Table pagination hook - placed after function definitions
  const sortedBills = getSortedData(getFilteredBills().map((bill, idx) => {
    const partyName = getPartyName(bill.party);
    const outstanding = getBillOutstanding(bill);
    const totalAmount = parseFloat(bill.amount) || 0;
    const paid = totalAmount - outstanding;
    return {
      ...bill,
      partyName,
      paid: Math.max(0, paid),
      outstanding: Math.max(0, outstanding)
    };
  }));
  const pagination = useTablePagination(sortedBills, 10);

  // GST calculation logic
  const getRowsForCalculation = () => {
    if (company.gstinType !== 'Regular') {
      // For Composition and Unregistered, set all GST to 0
      return rows.map(row => ({ ...row, sgst: 0, cgst: 0, igst: 0 }));
    }
    return rows;
  };

  const getInvoiceVisibleServiceChargeForRow = (row) => {
    const services = Array.isArray(row?.services) ? row.services : [];
    return roundTo2Decimals(services.reduce((sum, service) => {
      if (!service?.showOnInvoice) return sum;
      const charge = parseFloat(service.customerCharge);
      return sum + (Number.isFinite(charge) ? charge : 0);
    }, 0));
  };

  const totalVisibleServiceCharges = roundTo2Decimals(rows.reduce((sum, row) => {
    return sum + getInvoiceVisibleServiceChargeForRow(row);
  }, 0));

  // With per-line discounts, subtotalBeforeDiscount is sum of line net amounts, and overall discount is derived
  const subtotalBeforeDiscount = getRowsForCalculation().reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const derivedDiscount = getRowsForCalculation().reduce((sum, row) => {
    const qty = parseFloat(row.qty) || 0;
    const rate = parseFloat(row.rate) || 0;
    const rawAmount = qty * rate;
    let disc = 0;
    if ((row.lineDiscountType || 'amount') === 'percent') {
      disc = rawAmount * ((parseFloat(row.lineDiscountValue) || 0) / 100);
    } else {
      disc = parseFloat(row.lineDiscountValue) || 0;
    }
    disc = Math.min(Math.max(disc, 0), rawAmount);
    return sum + disc;
  }, 0);
  const discount = derivedDiscount; // for display
  const discountedSubtotal = Math.max(0, subtotalBeforeDiscount);
  // No proportional adjustment needed; amounts are already net of line discounts
  const discountRatio = 0;
  const totalSGST = company.gstinType === 'Regular' ? roundTo2Decimals(getRowsForCalculation().reduce((sum, row) => {
    const amt = parseFloat(row.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, row.sgst);
  }, 0)) : 0;
  const totalCGST = company.gstinType === 'Regular' ? roundTo2Decimals(getRowsForCalculation().reduce((sum, row) => {
    const amt = parseFloat(row.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, row.cgst);
  }, 0)) : 0;
  const totalIGST = company.gstinType === 'Regular' ? roundTo2Decimals(getRowsForCalculation().reduce((sum, row) => {
    const amt = parseFloat(row.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, row.igst);
  }, 0)) : 0;
  const grandTotalBeforeRoundOff = roundTo2Decimals(discountedSubtotal + totalSGST + totalCGST + totalIGST + totalVisibleServiceCharges);
  const grandTotal = roundToNearestInteger(grandTotalBeforeRoundOff);
  const roundOffAmount = roundTo2Decimals(grandTotal - grandTotalBeforeRoundOff);

  const subPartyLineRefs = getSubPartyLineRefsForRows(rows);
  const subPartyLineRefCount = subPartyLineRefs.length;

  const openSubPartyModal = () => {
    const lineRefs = getSubPartyLineRefsForRows(rows);
    const cap = lineRefs.length;
    const base =
      subPartyBreakdown.length > 0
        ? subPartyBreakdown.map((r) => ({
            name: r.name || '',
            lineIndices: sanitizeSubPartyLineIndices(r.lineIndices, cap),
            amount: '',
          }))
        : [
            { name: '', lineIndices: [], amount: '' },
            { name: '', lineIndices: [], amount: '' },
          ];
    // Amount always follows selected lines only (avoids stale totals when lines move between sub parties)
    const draft = base.map((r) => ({
      ...r,
      amount: String(sumLineTotalsForSubPartyIndices(r.lineIndices || [], lineRefs, company.gstinType)),
    }));
    setSubPartyDraft(applySubPartyDraftInvoiceRoundOff(draft, lineRefs, company.gstinType, grandTotal, grandTotalBeforeRoundOff));
    setShowSubPartyModal(true);
  };

  const updateSubPartyDraftRow = (index, field, value) => {
    setSubPartyDraft((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = {
        ...next[index],
        [field]: value,
        ...(field === 'amount' ? { _invoiceRoundOffAdj: undefined } : {}),
      };
      return next;
    });
  };

  const toggleSubPartyDraftLine = (spIdx, cleanedIdx) => {
    const lineRefs = getSubPartyLineRefsForRows(rows);
    setSubPartyDraft((prev) => {
      const next = prev.map((r) => ({
        ...r,
        lineIndices: Array.isArray(r.lineIndices) ? [...r.lineIndices] : [],
      }));
      const cur = next[spIdx];
      if (!cur) return prev;
      const has = cur.lineIndices.includes(cleanedIdx);
      if (has) {
        cur.lineIndices = cur.lineIndices.filter((i) => i !== cleanedIdx);
      } else {
        next.forEach((r, i) => {
          if (i !== spIdx) {
            r.lineIndices = r.lineIndices.filter((j) => j !== cleanedIdx);
          }
        });
        cur.lineIndices = [...cur.lineIndices, cleanedIdx].sort((a, b) => a - b);
      }
      return applySubPartyDraftInvoiceRoundOff(next, lineRefs, company.gstinType, grandTotal, grandTotalBeforeRoundOff);
    });
  };

  /** Recalculate every sub party amount from its selected lines only (recovery / explicit sync) */
  const fillSubPartyDraftAmountFromLines = () => {
    const lineRefs = getSubPartyLineRefsForRows(rows);
    setSubPartyDraft((prev) => {
      const next = prev.map((r) => ({ ...r, lineIndices: [...(r.lineIndices || [])] }));
      return applySubPartyDraftInvoiceRoundOff(next, lineRefs, company.gstinType, grandTotal, grandTotalBeforeRoundOff);
    });
  };

  const addSubPartyDraftRow = () => {
    setSubPartyDraft((prev) => [...prev, { name: '', amount: '', lineIndices: [] }]);
  };

  const removeSubPartyDraftRow = (index) => {
    setSubPartyDraft((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const applySubPartyModal = () => {
    const n = getSubPartyLineRefsForRows(rows).length;
    setSubPartyBreakdown(normalizeSubPartyBreakdownForSave(subPartyDraft, n));
    setShowSubPartyModal(false);
  };

  const subPartyDraftSum = sumSubPartyBreakdownAmounts(
    normalizeSubPartyBreakdownForSave(subPartyDraft, subPartyLineRefCount)
  );
  const subPartyDraftMatchesGrand = Math.abs(subPartyDraftSum - grandTotal) < 0.02;
  const subPartyAppliedSum = sumSubPartyBreakdownAmounts(
    normalizeSubPartyBreakdownForSave(subPartyBreakdown, subPartyLineRefCount)
  );
  const subPartyAppliedMatches = subPartyBreakdown.length === 0 || Math.abs(subPartyAppliedSum - grandTotal) < 0.02;

  // Seller details from company
  const seller = {
    name: company.firmName || '',
    address: `${company.address || ''}${company.city ? ', ' + company.city : ''}${company.pincode ? ' - ' + company.pincode : ''}`,
    contact: company.contactNumber || '',
    gstin: company.gstin || '',
    state: company.state || '',
    jurisdiction: company.city || '',
    bankName: company.bankName || '',
    bankAccount: company.bankAccount || '',
    bankIfsc: company.bankIfsc || '',
    upiId: company.upiId || '',
  };
  // Amount in words utility
  function numToWords(num) {
    // Simple INR words (for demo, can be replaced with a library)
    const a = [ '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen' ];
    const b = [ '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety' ];
    function inWords(n) {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n/10)] + (n%10 ? ' ' + a[n%10] : '');
      if (n < 1000) return a[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' and ' + inWords(n%100) : '');
      if (n < 100000) return inWords(Math.floor(n/1000)) + ' Thousand' + (n%1000 ? ' ' + inWords(n%1000) : '');
      if (n < 10000000) return inWords(Math.floor(n/100000)) + ' Lakh' + (n%100000 ? ' ' + inWords(n%100000) : '');
      return inWords(Math.floor(n/10000000)) + ' Crore' + (n%10000000 ? ' ' + inWords(n%10000000) : '');
    }
    return inWords(Math.floor(num)) + ' Rupees Only';
  }

  // Remove handlePrint function

  // Add new state for success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [savedBillId, setSavedBillId] = useState(null);
  
  // Click outside refs for modals
  const successModalRef = useClickOutside(() => setShowSuccessModal(false), showSuccessModal);

  // Update handleSaveInvoice function
  const handleSaveInvoice = async () => {
    if (!db || !userId || !appId) return;
    // Remove empty placeholder rows before validation/save
    const cleanedRows = rows.filter((r) => {
      const hasItem = !!(r.item && String(r.item).trim());
      const hasNumbers = (parseFloat(r.qty) || 0) > 0 || (parseFloat(r.rate) || 0) > 0 || (parseFloat(r.amount) || 0) > 0 || (parseFloat(r.total) || 0) > 0 || (parseFloat(r.length) || 0) > 0 || (parseFloat(r.height) || 0) > 0 || ((parseFloat(r.nos) || 0) > 1);
      return hasItem || hasNumbers;
    });
    if (!party || cleanedRows.length === 0) {
      alert("Please select a party and add at least one item.");
      return;
    }
    // Validation logic for duplicate number and date/number order
    const allBillCollections = ['salesBills', 'challans', 'quotations', 'purchaseBills', 'purchaseOrders'];
    let allBills = [];
    for (const coll of allBillCollections) {
      const billsSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, coll));
      billsSnap.forEach(doc => allBills.push({ ...doc.data(), id: doc.id, collection: coll }));
    }
    // 1. Check for duplicate number
    if (allBills.some(b => b.number === invoiceNumber && (editingBillId ? b.id !== editingBillId : true))) {
      setErrorMessage('Bill number already exists in another document. Please use a unique number.');
      return;
    }
    // 2. Gap/date check: allow if any bill in FY has the same date (match current doc type and FY)
    const fy = getFinancialYear(invoiceDate);
    const fyShort = String(fy)
      .split('-')
      .map(y => String(y).slice(-2))
      .join('-');
    const prefixMap = { invoice: 'INV', challan: 'CHA', quotation: 'QUO' };
    const typePrefix = prefixMap[docType] || 'INV';
    const fyBills = allBills.filter(b => (b.number || '').startsWith(typePrefix + fyShort));
    const thisSerial = parseInt((invoiceNumber || '').split('/')[1], 10);
    const thisDate = new Date(invoiceDate);
    const sameDateExists = fyBills.some(b => {
      if (!b.number || (!b.invoiceDate && !b.billDate)) return false;
      if (b.id === editingBillId) return false;
      const date = new Date(b.invoiceDate || b.billDate);
      return date.getTime() === thisDate.getTime();
    });
    if (!sameDateExists) {
      // Find previous and next serials
      const sortedBills = fyBills
        .filter(b => b.id !== editingBillId && b.number)
        .map(b => ({
          serial: parseInt((b.number || '').split('/')[1], 10),
          date: new Date(b.invoiceDate || b.billDate),
        }))
        .sort((a, b) => a.serial - b.serial);
      let prev = null, next = null;
      for (let i = 0; i < sortedBills.length; i++) {
        if (sortedBills[i].serial < thisSerial) prev = sortedBills[i];
        if (sortedBills[i].serial > thisSerial) { next = sortedBills[i]; break; }
      }
      if (prev && thisDate.getTime() < prev.date.getTime()) {
        setErrorMessage(`Date must be on or after previous bill (${prev.serial}) date: ${prev.date.toLocaleDateString()}`);
        return;
      }
      if (next && thisDate.getTime() > next.date.getTime()) {
        setErrorMessage(`Date must be on or before next bill (${next.serial}) date: ${next.date.toLocaleDateString()}`);
        return;
      }
    }
    // 2. Block if any lower bill number exists with a later date
    for (const b of fyBills) {
      if (!b.number || (!b.invoiceDate && !b.billDate)) continue;
      if (b.id === editingBillId) continue;
      const serial = parseInt((b.number || '').split('/')[1], 10);
      const date = new Date(b.invoiceDate || b.billDate);
      if (serial < thisSerial && date.getTime() > thisDate.getTime()) {
        setErrorMessage('A lower bill number exists with a later date. Please correct the date or number.');
        return;
      }
    }
    // 3. Block if any higher bill number exists with an earlier date
    for (const b of fyBills) {
      if (!b.number || (!b.invoiceDate && !b.billDate)) continue;
      if (b.id === editingBillId) continue;
      const serial = parseInt((b.number || '').split('/')[1], 10);
      const date = new Date(b.invoiceDate || b.billDate);
      if (serial > thisSerial && date.getTime() < thisDate.getTime()) {
        setErrorMessage('A higher bill number exists with an earlier date. Please correct the date or number.');
        return;
      }
    }
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return;
    const collectionRef = getCollectionForCompany(db, { companyId, appId, userId }, selected.collection);
    const stockCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'stock');
    
    // Compute tagSummary from all rows' tags (flattened unique tags)
    const allTags = new Set();
    cleanedRows.forEach(row => {
      if (row.tags && Array.isArray(row.tags)) {
        row.tags.forEach(tag => {
          if (tag && tag.trim()) {
            allTags.add(tag.toLowerCase().trim());
          }
        });
      }
    });
    const tagSummary = Array.from(allTags).sort();
    
    // Ensure each row has tags/services arrays (for backward compatibility)
    const rowsWithTags = cleanedRows.map((row) => {
      const { subParty: _legacySubParty, ...rest } = row;
      return {
        ...rest,
        tags: row.tags || [],
        services: (Array.isArray(row.services) ? row.services : []).map(normalizeServiceEntry),
      };
    });

    const normalizedSubParties = normalizeSubPartyBreakdownForSave(
      subPartyBreakdown,
      getSubPartyLineRefsForRows(rows).length
    );
    if (normalizedSubParties.length > 0) {
      const spSum = sumSubPartyBreakdownAmounts(normalizedSubParties);
      if (Math.abs(spSum - grandTotal) > 0.02) {
        alert(
          `Sub party amounts must add up to the invoice Grand Total (${formatCurrency(grandTotal, companyCurrency)}). Current sum: ${formatCurrency(spSum, companyCurrency)}.`
        );
        return;
      }
    }
    
    const billData = {
      number: invoiceNumber,
      invoiceDate,
      party,
      notes,
      rows: rowsWithTags,
      amount: grandTotal,
      totalAmount: grandTotal,
      gstCalculationType,
      roundOffAmount,
      grandTotalBeforeRoundOff,
      createdAt: serverTimestamp(),
      customFields,
      discountType,
      discountValue,
      tagSummary, // Add tag summary for easy filtering
      subPartyBreakdown: normalizedSubParties,
    };
    if (docType === 'quotation') billData.quotationTermsOverride = quotationTermsOverride;
    try {
      let savedBill;
      if (editingBillId) {
        const billRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), editingBillId);
        await setDoc(billRef, billData, { merge: true });
        savedBill = { id: editingBillId, ...billData };
      } else {
        const docRef = await addDoc(collectionRef, billData);
        savedBill = { id: docRef.id, ...billData };
      }

      // --- ADVANCE ALLOCATION LOGIC ---
      // Only allocate advance for invoices (not quotations, etc.)
      if (docType === 'invoice') {
        const availableAdvance = getPartyAdvance(party, payments);
        if (availableAdvance > 0) {
          const { allocatedAdvance, advanceAllocations, remainingBillAmount } = allocateAdvanceToBill(party, grandTotal, payments);
          if (allocatedAdvance > 0) {
            // Mark advances as used
            await markAdvanceUsed(advanceAllocations, db, appId, userId, payments);
            // Optionally, create a payment record for advance allocation
            const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
            await addDoc(paymentsRef, {
              receiptNumber: `ADV-${invoiceNumber}`,
              paymentDate: new Date().toISOString().split('T')[0],
              partyId: party,
              partyName: parties.find(p => p.id === party)?.firmName || '',
              totalAmount: allocatedAdvance,
              paymentMode: 'Advance Allocation',
              reference: 'Auto-advance',
              notes: 'Advance automatically allocated to new invoice',
              type: 'invoice',
              paymentType: 'advance-allocation',
              billId: savedBill.id,
              billNumber: invoiceNumber,
              allocations: [{
                billType: 'invoice',
                billId: savedBill.id,
                billNumber: invoiceNumber,
                allocatedAmount: allocatedAdvance,
                billOutstanding: grandTotal,
                isFullPayment: allocatedAdvance >= grandTotal
              }],
              advanceAllocations,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
      }

      // Update stock for each sold item
      if (docType === 'invoice') {
        for (const row of cleanedRows) {
          if (!row.item) continue;
          const stockDocRef = doc(stockCollectionRef, row.item);
          const stockDocSnap = await getDoc(stockDocRef);
          let currentStock = 0;
          if (stockDocSnap.exists()) {
            currentStock = stockDocSnap.data().itemQuantity || 0;
          }
          const updatedStock = currentStock - (parseFloat(row.nos) || 0);
          await setDoc(stockDocRef, {
            itemQuantity: updatedStock,
            lastSaleDate: invoiceDate,
          }, { merge: true });
        }
      }
      // Show success modal
                            setSavedBillId(savedBill.id);
                      setShowSuccessModal(true);
                      // Form reset will be handled by useEffect when modal is shown
    } catch (err) {
      alert("Error saving invoice: " + err.message);
    }
  };

  // Reset form to initial state
  const resetFormToInitialState = () => {
    setIsResettingForm(true);
    
    // Force a re-render by updating a timestamp
    const timestamp = Date.now();
    setForceUpdate(timestamp);
    
    setInvoiceNumber('');
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setParty('');
    setPaymentStatus('Pending');
    setNotes('');
    setRows(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
    setItemSearchTextByRow({});
    setCurrentMobileItemIndex(0);
    setGstCalculationType('excluding'); // Reset to default
    setCustomFields({
      ewayBillNo: '',
      ewayQr: '',
      ewayDate: ''
    });
    setDiscountType('amount');
    setDiscountValue('');
    setQuotationTermsOverride('');
    setEditingBillId(null);
    setErrorMessage('');
    setGstTypes(Array.from({ length: 5 }, () => ''));
    setSubPartyBreakdown([]);
    // Reset quick items selection
    setSelectedPartyForQuickItems('');
    
    // Calculate next invoice number after reset
    setTimeout(() => {
      const prefixMap = { invoice: 'INV', challan: 'CHA', quotation: 'QUO' };
      const fy = getFinancialYear(new Date().toISOString().split('T')[0]);
      const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
      const prefix = prefixMap[docType] || 'INV';
      const serials = bills
        .filter(bill => (bill.number || '').startsWith(`${prefix}${fyShort}/`))
        .map(bill => parseInt((bill.number || '').split('/')[1], 10))
        .filter(n => !isNaN(n));
              const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
        const nextInvoiceNumber = `${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`;
        setInvoiceNumber(nextInvoiceNumber);
    }, 100);
    
    // Clear loading state after a short delay
    setTimeout(() => {
      setIsResettingForm(false);
    }, 200);
  };

  // Add a force update state to ensure re-renders
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isResettingForm, setIsResettingForm] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);



  // useEffect to trigger form reset when success modal is shown
  useEffect(() => {
    if (showSuccessModal) {
      // Small delay to ensure modal is fully rendered before resetting
      const timer = setTimeout(() => {
        resetFormToInitialState();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      // Force re-render when screen size changes
      setForceUpdate(Date.now());
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Save showFilters state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sales_showFilters', JSON.stringify(showFilters));
    }
  }, [showFilters]);

  // Modal and action state for View/Edit/Delete
  const [viewBill, setViewBill] = useState(null); // Bill to view in modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [deletingBillId, setDeletingBillId] = useState(null); // Bill id being deleted
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Add import for BillTemplates and modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceBill, setInvoiceBill] = useState(null);

  // ESC Key Functionality: Press ESC key to close modals in LIFO order (Last In, First Out)
  // Order: Service Editor -> Add Party Modal -> Receipt Modal -> Payment Details Modal -> Payment Modal -> Invoice Modal -> View Modal -> Success Modal -> Add Item Modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showServiceEditorModal) {
          closeServiceEditor();
        } else if (showAddPartyModal) {
          setShowAddPartyModal(false);
          clearAddPartyForm();
        } else if (showSubPartyModal) {
          setShowSubPartyModal(false);
        } else if (showReceiptModal) {
          setShowReceiptModal(false);
        } else if (showPaymentDetailsModal) {
          setShowPaymentDetailsModal(false);
        } else if (showPaymentModal) {
          setShowPaymentModal(false);
        } else if (showInvoiceModal) {
          setShowInvoiceModal(false);
        } else if (showViewModal) {
          setShowViewModal(false);
        } else if (showSuccessModal) {
          setShowSuccessModal(false);
        } else if (showAddItemModal) {
          setShowAddItemModal(false);
          clearAddItemForm();
        }
      }
    };
    
    // Add event listener if any modal is open
    if (showServiceEditorModal || showAddPartyModal || showSubPartyModal || showReceiptModal || showPaymentDetailsModal || showPaymentModal || showInvoiceModal || showViewModal || showSuccessModal || showAddItemModal) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showServiceEditorModal, showAddPartyModal, showSubPartyModal, showReceiptModal, showPaymentDetailsModal, showPaymentModal, showInvoiceModal, showViewModal, showSuccessModal, showAddItemModal]);

  useEffect(() => {
    if (!showServiceEditorModal) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showServiceEditorModal]);

  // Handler to view bill details
  const handleViewBill = (bill) => {
    setViewBill(bill);
    setShowViewModal(true);
  };

  // Toggle function for dropdown form rows
  const toggleRowExpansion = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  // Handler to edit bill (load into form)
  const handleEditBill = (bill) => {
    setEditingBillId(bill.id);
    setInvoiceNumber(bill.number || '');
    setInvoiceDate(bill.invoiceDate || '');
    setParty(bill.party || '');
    setNotes(bill.notes || '');
    // Always use GST values from bill.rows when editing, ensure tags/services are initialized
    setRows((bill.rows && bill.rows.length > 0) ? bill.rows.map(row => ({ ...initialItemRow, ...row, tags: row.tags || [], services: (Array.isArray(row.services) ? row.services : []).map(normalizeServiceEntry) })) : [{ ...initialItemRow }]);
    if (Array.isArray(bill.subPartyBreakdown) && bill.subPartyBreakdown.length > 0) {
      const cap = getSubPartyLineRefsForRows(bill.rows || []).length;
      setSubPartyBreakdown(
        bill.subPartyBreakdown.map((r) => ({
          name: String(r.name || '').trim(),
          amount: typeof r.amount === 'number' && !Number.isNaN(r.amount) ? r.amount : roundTo2Decimals(parseFloat(r.amount) || 0),
          lineIndices: sanitizeSubPartyLineIndices(r.lineIndices, cap),
        }))
      );
    } else {
      setSubPartyBreakdown([]);
    }
    // Load GST calculation type from bill data
    setGstCalculationType(bill.gstCalculationType || 'excluding');
    // Update quick items selection to match the party being edited
    setSelectedPartyForQuickItems(bill.party || '');
    
    // Initialize itemSearchTextByRow for mobile view
    const newItemSearchText = {};
    if (bill.rows && bill.rows.length > 0) {
      bill.rows.forEach((row, index) => {
        if (row.item) {
          const item = items.find(it => it.id === row.item);
          if (item) {
            newItemSearchText[index] = item.itemName || item.name || '';
          }
        }
      });
    }
    setItemSearchTextByRow(newItemSearchText);
    
    // Reset mobile carousel to first item
    setCurrentMobileItemIndex(0);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (bill.quotationTermsOverride !== undefined) setQuotationTermsOverride(bill.quotationTermsOverride);
    else setQuotationTermsOverride('');
  };
  // Handler to delete bill
  const handleDeleteBill = (billId) => {
    setDeletingBillId(billId);
    setShowDeleteConfirm(true);
  };
  // Confirm delete
  const confirmDeleteBill = async () => {
    if (!db || !userId || !appId || !deletingBillId) return;
    try {
      const selected = docTypeOptions.find(opt => opt.value === docType);
      if (!selected) return;
      const billRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), deletingBillId);
      await deleteDoc(billRef);
      setShowDeleteConfirm(false);
      setDeletingBillId(null);
    } catch (err) {
      alert('Error deleting bill: ' + err.message);
    }
  };
  // Cancel delete
  const cancelDeleteBill = () => {
    setShowDeleteConfirm(false);
    setDeletingBillId(null);
  };

  // Payment handling functions
  const handleAddPayment = async (bill) => {
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMode('Cash');
    setPaymentReference('');
    setPaymentNotes('');
    setReceiptNumber('');
    
    // Generate receipt number
    const receiptNum = await generateReceiptNumber();
    setReceiptNumber(receiptNum);
    
    // Pre-fill with bill details
    setPaymentAmount(getBillOutstanding(bill).toString());
    
    // Store the bill information for payment
    setSelectedBillForPayment(bill);
    setShowPaymentModal(true);
  };

  // Get bill payments for receipt preview
  const getBillPayments = (bill) => {
    return payments.filter(payment => {
      // Check if payment is for the same party
      const paymentPartyId = payment.partyId || payment.party;
      if (paymentPartyId !== bill.party) {
        return false;
      }
      
      // Special handling for advance receipts
      if (payment.receiptNumber && payment.receiptNumber.startsWith('ADV-')) {
        // Extract invoice number from advance receipt (e.g., ADV-INV25-26/12 -> INV25-26/12)
        const targetInvoiceNo = payment.receiptNumber.replace('ADV-', '');
        const currentInvoiceNo = bill.number || bill.invoiceNumber || bill.id;
        return targetInvoiceNo === currentInvoiceNo;
      }
      
      // Check if payment has allocations for this invoice
      if (payment.allocations && Array.isArray(payment.allocations)) {
        return payment.allocations.some(allocation => 
          allocation.billId === bill.id && allocation.billType === docType
        );
      }
      
      // Check if payment has advance allocations for this invoice
      if (payment.advanceAllocations && Array.isArray(payment.advanceAllocations)) {
        return payment.advanceAllocations.some(allocation => 
          allocation.billId === bill.id
        );
      }
      
      // Check direct billId match
      if (payment.billId === bill.id) {
        return true;
      }
      
      return false;
    });
  };

  // Handle payment receipt preview
  const handlePreviewReceipt = (payment) => {
    setSelectedPaymentForReceipt(payment);
    setShowReceiptModal(true);
  };

  // Handle payment receipt preview from bill
  const handleViewPaymentReceipts = (bill) => {
    const billPayments = getBillPayments(bill);
    if (billPayments.length > 0) {
      setSelectedBillForReceipts(bill);
      setShowPaymentDetailsModal(true);
    }
  };

  // Image management functions
  const handleImageSelect = (image) => {
    setSelectedImages(prev => ({
      ...prev,
      [image.category]: image.data
    }));
  };

  const loadSavedImages = async () => {
    try {
      const logoImages = await imageManager.getImagesByCategory('logo', userId);
      const sealImages = await imageManager.getImagesByCategory('seal', userId);
      const signatureImages = await imageManager.getImagesByCategory('signature', userId);
      const qrImages = await imageManager.getImagesByCategory('qr', userId);

      setSelectedImages({
        logo: logoImages.length > 0 ? logoImages[0].data : null,
        seal: sealImages.length > 0 ? sealImages[0].data : null,
        signature: signatureImages.length > 0 ? signatureImages[0].data : null,
        qr: qrImages.length > 0 ? qrImages[0].data : null
      });
    } catch (error) {
      console.error('Error loading saved images:', error);
    }
  };

  // Load saved images on component mount
  useEffect(() => {
    if (userId) {
      loadSavedImages();
    }
  }, [userId]);

  // Print receipt
  const printReceipt = () => {
    if (!selectedPaymentForReceipt) {
      alert('No receipt selected for printing');
      return;
    }

    if (!receiptRef.current) {
      alert('Receipt content not available');
      return;
    }

    const printContents = receiptRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'height=800,width=1000');
    
    if (!printWindow) {
      alert('Please allow popups to print the receipt');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt</title>
          <meta charset="utf-8">
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            @media print {
              body { margin: 0; padding: 0; }
              .print-content { 
                width: 210mm; 
                height: 297mm; 
                margin: 0 auto; 
                background: white; 
                padding: 20mm;
                box-sizing: border-box;
              }
              .no-print { display: none !important; }
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: #f5f5f5; 
            }
            .print-content { 
              background: white; 
              padding: 40px; 
              border-radius: 8px; 
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              max-width: 800px;
              margin: 0 auto;
            }
            .receipt-header { text-align: center; margin-bottom: 30px; }
            .receipt-title { font-size: 28px; font-weight: bold; color: #1f2937; margin-bottom: 10px; }
            .receipt-number { font-size: 18px; color: #2563eb; font-family: monospace; }
            .company-info { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #d1d5db; }
            .company-name { font-size: 20px; font-weight: bold; color: #1f2937; margin-bottom: 5px; }
            .company-details { font-size: 14px; color: #6b7280; }
            .receipt-details { margin-bottom: 30px; }
            .detail-row { display: flex; justify-content: space-between; margin-bottom: 15px; }
            .detail-label { font-weight: 600; color: #374151; }
            .detail-value { font-size: 16px; }
            .amount { font-size: 24px; font-weight: bold; color: #16a34a; }
            .allocation-section { margin-top: 30px; }
            .allocation-title { font-weight: 600; color: #2563eb; margin-bottom: 15px; }
            .allocation-item { border: 1px solid #e5e7eb; border-radius: 4px; padding: 15px; margin-bottom: 10px; background: #f9fafb; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #d1d5db; display: flex; justify-content: space-between; align-items: flex-end; }
            .footer-left { font-size: 12px; color: #6b7280; }
            .signature { text-align: center; }
            .signature-line { border-top: 2px solid #9ca3af; width: 120px; margin: 0 auto 10px; }
            .signature-text { font-weight: 600; color: #374151; }
          </style>
        </head>
        <body>
          <div class="print-content">
            ${printContents}
          </div>
          <div class="no-print" style="text-align: center; margin-top: 20px;">
            <button onclick="window.print()" style="background: #2563eb; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; margin-right: 10px;">Print</button>
            <button onclick="window.close()" style="background: #6b7280; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer;">Close</button>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load before printing
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  };

  // Generate PDF receipt
  const generatePDFReceipt = async () => {
    if (!selectedPaymentForReceipt) {
      alert('No receipt selected for PDF generation');
      return;
    }
    
    // Ensure jsPDF is available (try global first, then dynamic import)
    let jsPDF;
    if (window.jspdf && window.jspdf.jsPDF) {
      jsPDF = window.jspdf.jsPDF;
    } else if (window.jsPDF) {
      jsPDF = window.jsPDF;
    } else {
      try {
        const mod = await import('jspdf');
        jsPDF = (mod && (mod.jsPDF || mod.default)) || undefined;
      } catch (e) {
        console.error('Failed to load jsPDF:', e);
      }
    }
    if (!jsPDF) {
      alert('PDF generation library not available. Please refresh the page and try again.');
      return;
    }
    
    const { jsPDF: _unused } = { jsPDF };
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;
    
    // Helper function to get company name safely
    const getCompanyName = () => {
      if (company?.firmName) return company.firmName;
      if (company?.name) return company.name;
      if (company?.companyName) return company.companyName;
      return 'Company Name';
    };
    
    // Helper function to get party name safely
    const getPartyName = () => {
      if (selectedPaymentForReceipt?.partyName) return selectedPaymentForReceipt.partyName;
      if (selectedPaymentForReceipt?.party?.firmName) return selectedPaymentForReceipt.party.firmName;
      if (selectedPaymentForReceipt?.party?.name) return selectedPaymentForReceipt.party.name;
      return 'Party Name';
    };
    
    // Helper function to format date according to company settings
    const formatDate = (dateField) => {
      if (!dateField) return 'Unknown date';
      return formatDateToCompanyFormat(dateField, companyProfile);
    };
    
    // Company details
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(getCompanyName(), pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;
    
    // Add company logo if available
    if (selectedImages.logo) {
      try {
        const logoImg = new Image();
        logoImg.src = selectedImages.logo;
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
        });
        
        const logoWidth = 40;
        const logoHeight = 30;
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = yPosition - 35;
        
        doc.addImage(logoImg, 'JPEG', logoX, logoY, logoWidth, logoHeight);
        yPosition += 5;
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
      }
    }
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    if (company?.address) {
      doc.text(company.address, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 5;
    }
    if (company?.gstin) {
      doc.text(`GSTIN: ${company.gstin}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 5;
    }
    
    yPosition += 10;
    
    // Receipt title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('PAYMENT RECEIPT', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;
    
    // Receipt details
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    
    const receiptNumber = selectedPaymentForReceipt?.receiptNumber || 'N/A';
    const paymentDate = formatDate(selectedPaymentForReceipt?.date || selectedPaymentForReceipt?.paymentDate);
    const partyName = getPartyName();
    const paymentMode = selectedPaymentForReceipt?.mode || selectedPaymentForReceipt?.paymentMode || 'Cash';
    const amount = selectedPaymentForReceipt?.totalAmount || selectedPaymentForReceipt?.amount || 0;
    const reference = selectedPaymentForReceipt?.reference || '-';
    
    const receiptData = [
      ['Receipt Number:', receiptNumber],
      ['Date:', paymentDate],
      ['Party Name:', partyName],
      ['Payment Mode:', paymentMode],
      ['Amount:', formatCurrency(parseFloat(amount), companyCurrency)],
      ['Reference:', reference],
    ];
    
    receiptData.forEach(([label, value]) => {
      doc.setFont(undefined, 'bold');
      doc.text(label, margin, yPosition);
      doc.setFont(undefined, 'normal');
      doc.text(value, margin + 60, yPosition);
      yPosition += 8;
    });
    
    // Payment Allocations
    const allocations = selectedPaymentForReceipt?.allocations || [];
    if (allocations.length > 0) {
      yPosition += 10;
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Payment Allocations:', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      allocations.forEach((allocation, index) => {
        const billType = allocation?.billType?.toUpperCase() || 'INVOICE';
        const billNumber = allocation?.billNumber || allocation?.billId || 'N/A';
        const allocatedAmount = allocation?.allocatedAmount || 0;
        doc.text(`${index + 1}. ${billType} ${billNumber}: ${formatCurrency(parseFloat(allocatedAmount), companyCurrency)}`, margin + 5, yPosition);
        yPosition += 6;
      });
    }
    
    // Advance allocations
    const advanceAllocations = selectedPaymentForReceipt?.advanceAllocations || [];
    if (advanceAllocations.length > 0) {
      yPosition += 10;
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Advance Allocations:', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      advanceAllocations.forEach((advance, index) => {
        const amountUsed = advance?.amountUsed || 0;
        doc.text(`${index + 1}. Advance Payment: ${formatCurrency(parseFloat(amountUsed), companyCurrency)}`, margin + 5, yPosition);
        yPosition += 6;
      });
    }
    
    // Footer
    yPosition = pageHeight - 40;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, yPosition);
    yPosition += 5;
    doc.text(`Time: ${new Date().toLocaleTimeString()}`, margin, yPosition);
    
    // Signature area
    yPosition = pageHeight - 30;
    doc.text('Authorized Signatory', pageWidth - margin - 50, yPosition, { align: 'right' });
    
      // Save PDF with initials and compact date
      const fmt = (d) => {
        try { const x = new Date(d); const day = String(x.getDate()); const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()]; const yr = x.getFullYear(); return `${day}${mon}${yr}`; } catch { return d; }
      };
      const fileName = `REC_${receiptNumber.replace(/[^a-zA-Z0-9/]/g, '_')}_${fmt(paymentDate)}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleSavePayment = async () => {
    if (!selectedBillForPayment || !paymentAmount) {
      alert('Please fill in amount field.');
      return;
    }

    try {
      const paymentAmountNum = parseFloat(paymentAmount);
      const billOutstanding = getBillOutstanding(selectedBillForPayment);
      
      // Check if payment amount exceeds bill outstanding
      if (paymentAmountNum > billOutstanding) {
        alert(`Payment amount (${formatCurrency(paymentAmountNum, companyCurrency)}) cannot exceed the bill's outstanding amount (${formatCurrency(billOutstanding, companyCurrency)}). Please use the Payments page for amounts greater than the bill outstanding.`);
        return;
      }
      
      // First, try to allocate available advance
      const availableAdvance = getPartyAdvance(selectedBillForPayment.party, payments);
      let advanceAllocations = [];
      let advanceUsed = 0;
      let remainingPaymentAmount = paymentAmountNum;
      
      if (availableAdvance > 0) {
        // Allocate advance to this bill
        const advanceResult = allocateAdvanceToBill(selectedBillForPayment.party, billOutstanding, payments);
        if (advanceResult.allocatedAdvance > 0) {
          advanceAllocations = advanceResult.advanceAllocations;
          advanceUsed = advanceResult.allocatedAdvance;
          remainingPaymentAmount = paymentAmountNum - advanceUsed;
          
          // Mark advances as used
          if (Array.isArray(advanceResult.advanceAllocations) && advanceResult.advanceAllocations.length > 0) {
            await markAdvanceUsed(advanceResult.advanceAllocations, db, appId, userId, payments);
          }
        }
      }
      
      // Simple bill payment - no excess amount handling since it's restricted above
      const paymentData = {
        receiptNumber: receiptNumber,
        paymentDate: paymentDate,
        partyId: selectedBillForPayment.party,
        partyName: getPartyName(selectedBillForPayment.party),
        totalAmount: paymentAmountNum,
        paymentMode: paymentMode,
        reference: paymentReference,
        notes: paymentNotes,
        type: docType,
        paymentType: 'bill',
        billId: selectedBillForPayment.id,
        billNumber: selectedBillForPayment.number,
        allocations: [{
          billType: docType,
          billId: selectedBillForPayment.id,
          billNumber: selectedBillForPayment.number,
          allocatedAmount: paymentAmountNum,
          billOutstanding: billOutstanding,
          isFullPayment: paymentAmountNum >= billOutstanding
        }],
        remainingAmount: 0, // No remaining amount since payment cannot exceed bill outstanding
        fifoAllocationUsed: 0,
        advanceAllocations: advanceAllocations,
        advanceUsed: advanceUsed,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Add to payments collection
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      await addDoc(paymentsRef, paymentData);
      
      // Reset payment form
      setPaymentAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMode('Cash');
      setPaymentReference('');
      setPaymentNotes('');
      setReceiptNumber('');
      setSelectedBillForPayment(null);
      setShowPaymentModal(false);
      
      alert('Payment added successfully!');
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('Error saving payment. Please try again.');
    }
  };



  // For SunriseTemplate, ensure A4 size and fixed layout
  // Remove a4Style

  const [invoiceZoom, setInvoiceZoom] = useState(1);
  const invoiceRef = useRef();

  // Update handleInvoicePrint and handleInvoiceDownload to properly handle print:hidden elements:
  const handleInvoicePrint = async () => {
    if (invoiceRef.current) {
      const temp = document.createElement('div');
      temp.innerHTML = invoiceRef.current.innerHTML;
      await inlineImagesForPrint(temp);
      const printContents = temp.innerHTML;
      const printWindow = window.open('', '', 'height=800,width=1000');
      printWindow.document.write('<html><head><title>Invoice</title>');
      printWindow.document.write('<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">');
      printWindow.document.write('<style>');
      printWindow.document.write('html,body{margin:0;padding:0;background:#fff;}');
      printWindow.document.write('@media print{html,body{width:210mm!important;max-width:210mm!important;box-sizing:border-box!important;}}');
      printWindow.document.write('@page{size:A4;margin:8mm;}');
      printWindow.document.write('.print\\:hidden { display: none !important; }');
      printWindow.document.write('@media print { .print\\:hidden { display: none !important; } }');
      printWindow.document.write('.print\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }');
      printWindow.document.write('.print\\:bg-white { background-color: white !important; }');
      printWindow.document.write('.print\\:border { border-width: 1px !important; }');
      printWindow.document.write('.print\\:border-gray-300 { border-color: #d1d5db !important; }');
      printWindow.document.write('.print\\:flex-row { flex-direction: row !important; }');
      printWindow.document.write('.print\\:bg-yellow-100 { background-color: #fef3c7 !important; }');
      printWindow.document.write('.print\\:bg-green-100 { background-color: #dcfce7 !important; }');
      printWindow.document.write('.print\\:transform-none { transform: none !important; }');
      printWindow.document.write('@media print { .grid { display: grid !important; } }');
      printWindow.document.write('@media print { .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }');
      printWindow.document.write('@media print { .flex { display: flex !important; } }');
      printWindow.document.write('@media print { .flex-row { flex-direction: row !important; } }');
      printWindow.document.write('@media print { [class*="print:hidden"] { display: none !important; } }');
      printWindow.document.write('@media print { button { display: none !important; } }');
      printWindow.document.write('@media print { .sticky { position: static !important; } }');
      printWindow.document.write('@media print { img { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }');
      printWindow.document.write('</style>');
      printWindow.document.write('</head><body style="background:white;">');
      printWindow.document.write(printContents);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      // Ensure images are loaded before printing
      const ensureImages = () => {
        try {
          const imgs = Array.from(printWindow.document.images || []);
          if (imgs.length === 0) return Promise.resolve();
          return Promise.all(imgs.map(img => new Promise(resolve => {
            if (img.complete && img.naturalWidth > 0) return resolve();
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })));
        } catch (_) {
          return Promise.resolve();
        }
      };
      printWindow.onload = async () => {
        await ensureImages();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 300);
      };
    }
  };

  // Helper function to build bill data for different document types
  const buildBillDataForTemplate = (invoiceBill) => {
    // Enhance items with proper measurement units from items collection
    const enhancedItems = (invoiceBill.rows || invoiceBill.items || []).map(row => {
      if (row.item) {
        const itemObj = items.find(it => it.id === row.item);
        if (itemObj) {
          return {
            ...row,
            description: itemObj.itemName || row.description || '',
            unit: itemObj.quantityMeasurement || row.unit || '',
            quantityMeasurement: itemObj.quantityMeasurement || row.quantityMeasurement || ''
          };
        }
      }
      return row;
    });
    
    const baseData = {
      items: enhancedItems,
      discountType: invoiceBill.discountType || discountType,
      discountValue: invoiceBill.discountValue || discountValue,
      subPartyBreakdown: Array.isArray(invoiceBill.subPartyBreakdown) ? invoiceBill.subPartyBreakdown : [],
    };
    
    if (invoiceBill.docType === 'challan') {
      return {
        ...baseData,
        challanNumber: invoiceBill.number || invoiceBill.challanNumber,
        challanDate: invoiceBill.invoiceDate || invoiceBill.date
      };
    } else if (invoiceBill.docType === 'quotation') {
      return {
        ...baseData,
        quotationNumber: invoiceBill.number || invoiceBill.quotationNumber,
        date: invoiceBill.invoiceDate || invoiceBill.date
      };
    } else {
      return {
        ...baseData,
        invoiceNumber: invoiceBill.number || invoiceBill.invoiceNumber,
        date: invoiceBill.invoiceDate || invoiceBill.date
      };
    }
  };

  // Helper function to get the correct template component
  const getTemplateComponent = async (docType) => {
    if (docType === 'challan') {
      const { default: ChallanTemplate } = await import('./BillTemplates/ChallanTemplate');
      return ChallanTemplate;
    } else if (docType === 'quotation') {
      const { default: QuotationTemplate } = await import('./BillTemplates/QuotationTemplate');
      return QuotationTemplate;
    } else {
      const { default: InvoiceTemplate } = await import('./BillTemplates/InvoiceTemplate');
      return InvoiceTemplate;
    }
  };

  // PDF download handler - Fixed for all document types with helper functions
  const handleInvoiceDownloadWithHelpers = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      alert('Please select a bill to download as PDF');
      return;
    }
    
    try {
      // Preload images before PDF generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signatureUrl || currentCompany?.signUrl || currentCompany?.details?.signUrl || currentCompany?.branding?.signatureUrl
      };
      
      const bankDetails = {
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signatureUrl || currentCompany?.signUrl || currentCompany?.details?.signUrl || currentCompany?.branding?.signatureUrl
      };
      
      console.log('Preloading images for PDF generation...');
      await preloadBillImages(companyDetails, bankDetails);
      
      const html2pdf = await import('html2pdf.js');
      
      // Create a temporary container and render the correct template with proper data
      const tempContainer = document.createElement('div');
      
      // Get the correct template component
      const TemplateComponent = await getTemplateComponent(invoiceBill.docType);
      
      const ReactDOM = await import('react-dom');
      
      // Create a temporary div to render the React component
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      document.body.appendChild(tempDiv);
      
      // Render the correct template with the same props as the main component
      ReactDOM.render(
        React.createElement(TemplateComponent, {
          billData: buildBillDataForTemplate(invoiceBill),
          companyDetails: billPreviewCompanyDetails,
          partyDetails: parties.find(p => p.id === invoiceBill.party) || {},
          bankDetails: {},
          pageSize: 'a4',
          orientation: 'portrait',
          previewMode: false
        }),
        tempDiv
      );
      
      // Wait for the component to render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get the rendered HTML and attach offscreen for accurate layout/image loading
      tempContainer.innerHTML = tempDiv.innerHTML;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.width = '210mm';
      document.body.appendChild(tempContainer);

      // Clean up the React render container
      ReactDOM.unmountComponentAtNode(tempDiv);
      document.body.removeChild(tempDiv);
      
      // Hide print:hidden elements
      const hiddenElements = tempContainer.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
      hiddenElements.forEach(el => el.style.display = 'none');

      // Fast image processing for PDF generation
      const imgs = Array.from(tempContainer.querySelectorAll('img'));
      const imagePromises = imgs.map(async (img) => {
        const src = img.getAttribute('src');
        if (!src) return;
        
        try {
          const imageData = await convertImageToDataUrl(src);
          if (imageData) {
            img.setAttribute('src', imageData);
            img.removeAttribute('crossorigin');
          } else if (window.imageManager) {
            const imageRecord = await window.imageManager.getImage(src);
            if (imageRecord?.data) {
              img.setAttribute('src', imageRecord.data);
              img.removeAttribute('crossorigin');
            }
          }
        } catch (error) {
          img.style.display = 'none';
        }
      });
      
      // Process all images in parallel
      await Promise.all(imagePromises);
      
      // Add delay to ensure images are fully loaded and DOM is ready for PDF generation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate filename
      const fmt = (d) => {
        try {
          const x = new Date(d);
          const day = String(x.getDate());
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()];
          const yr = x.getFullYear();
          return `${day}${mon}${yr}`;
        } catch {
          return d;
        }
      };
      
      const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document').toUpperCase()}_${invoiceBill.number || 'Bill'}_${fmt(invoiceBill.invoiceDate || invoiceBill.date)}.pdf`;
      
      // Generate PDF with optimized settings
      const worker = html2pdf.default().from(tempContainer).set({
        margin: 0.5,
        filename: filename,
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          allowTaint: true,
          backgroundColor: '#ffffff',
          imageTimeout: 10000, // Increased timeout for mobile
          removeContainer: true,
          logging: false, // Disable logging for better performance
          foreignObjectRendering: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: tempContainer.scrollWidth,
          windowHeight: tempContainer.scrollHeight
        },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      });
      
      await worker.save();
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Optimized PDF Share Handler - generates PDF as blob and shares via system - Fixed for all document types
  const handleInvoiceSharePdf = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      alert('Please select a bill to share as PDF');
      return;
    }
    
    try {
      // Preload images before PDF generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signatureUrl || currentCompany?.signUrl || currentCompany?.details?.signUrl || currentCompany?.branding?.signatureUrl
      };
      
      const bankDetails = {
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signatureUrl || currentCompany?.signUrl || currentCompany?.details?.signUrl || currentCompany?.branding?.signatureUrl
      };
      
      console.log('Preloading images for PDF share...');
      await preloadBillImages(companyDetails, bankDetails);
      
      const html2pdf = await import('html2pdf.js');
      
      // Create a temporary container and render the correct template with proper data
      const tempContainer = document.createElement('div');
      
      // Get the correct template component
      const TemplateComponent = await getTemplateComponent(invoiceBill.docType);
      
      const ReactDOM = await import('react-dom');
      
      // Create a temporary div to render the React component
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      document.body.appendChild(tempDiv);
      
      // Render the correct template with the same props as the main component
      ReactDOM.render(
        React.createElement(TemplateComponent, {
          billData: buildBillDataForTemplate(invoiceBill),
          companyDetails: billPreviewCompanyDetails,
          partyDetails: parties.find(p => p.id === invoiceBill.party) || {},
          bankDetails: {},
          pageSize: 'a4',
          orientation: 'portrait',
          previewMode: false
        }),
        tempDiv
      );
      
      // Wait for the component to render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get the rendered HTML
      tempContainer.innerHTML = tempDiv.innerHTML;
      
      // Clean up
      ReactDOM.unmountComponentAtNode(tempDiv);
      document.body.removeChild(tempDiv);
      
      // Hide print:hidden elements
      const hiddenElements = tempContainer.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
      hiddenElements.forEach(el => el.style.display = 'none');

      // Fast image processing for PDF generation
      const imgs = Array.from(tempContainer.querySelectorAll('img'));
      const imagePromises = imgs.map(async (img) => {
        const src = img.getAttribute('src');
        if (!src) return;
        
        try {
          const imageData = await convertImageToDataUrl(src);
          if (imageData) {
            img.setAttribute('src', imageData);
            img.removeAttribute('crossorigin');
          } else if (window.imageManager) {
            const imageRecord = await window.imageManager.getImage(src);
            if (imageRecord?.data) {
              img.setAttribute('src', imageRecord.data);
              img.removeAttribute('crossorigin');
            }
          }
        } catch (error) {
          img.style.display = 'none';
        }
      });
      
      // Process all images in parallel
      await Promise.all(imagePromises);
      
      // Add delay to ensure images are fully loaded and DOM is ready for PDF generation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate filename
      const fmt = (d) => {
        try {
          const x = new Date(d);
          const day = String(x.getDate());
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()];
          const yr = x.getFullYear();
          return `${day}${mon}${yr}`;
        } catch {
          return d;
        }
      };
      
      const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document').toUpperCase()}_${invoiceBill.number || 'Bill'}_${fmt(invoiceBill.invoiceDate || invoiceBill.date)}.pdf`;
      
      // Generate PDF with optimized settings
      const worker = html2pdf.default().from(tempContainer).set({
        margin: 0.5,
        filename: filename,
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          allowTaint: true,
          backgroundColor: '#ffffff',
          imageTimeout: 10000, // Increased timeout for mobile
          removeContainer: true,
          logging: false, // Disable logging for better performance
          foreignObjectRendering: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: tempContainer.scrollWidth,
          windowHeight: tempContainer.scrollHeight
        },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      });
      
      const pdfBlob = await worker.outputPdf('blob');
      
      // Share PDF via system share or download
      try {
        if (navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], filename, { type: 'application/pdf' })] })) {
          await navigator.share({
            title: `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill.number || 'Bill'}`,
            text: `Sharing ${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill.number || 'Bill'}`,
            files: [new File([pdfBlob], filename, { type: 'application/pdf' })]
          });
        } else {
          // Download fallback
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
        }
      } catch (shareError) {
        // Download fallback if sharing fails
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error generating PDF for sharing:', error);
      alert('Error generating PDF for sharing. Please try again.');
    }
  };

  // Share: link to open this bill directly in preview (same as reports flow)
  const handleInvoiceShareLink = async () => {
    try {
      if (!invoiceBill || !invoiceRef.current) {
        alert('Please select a bill to share');
        return;
      }
      // Render a static HTML for public viewing (no app chrome)
      const container = document.createElement('html');
      const head = document.createElement('head');
      head.innerHTML = '<meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Document</title><link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">';
      const body = document.createElement('body');
      const doc = document.createElement('div');
      doc.style.background = 'white';
      doc.style.margin = '0 auto';
      doc.style.padding = '0';
      doc.innerHTML = invoiceRef.current.innerHTML;
      // Strip interactive controls
      Array.from(doc.querySelectorAll('button')).forEach(b => b.remove());
      // Keep images as-is (reports flow keeps minimal HTML and uploads)
      body.appendChild(doc);
      container.appendChild(head);
      container.appendChild(body);

      const blob = new Blob([container.outerHTML || container.innerHTML], { type: 'text/html' });
      const storage = getStorage(app);
      const token = Math.random().toString(36).slice(2);
      const path = `publicDocs/${userId || 'anon'}/${invoiceBill.id}-${token}.html`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, blob, { contentType: 'text/html' });
      const storageUrl = await getDownloadURL(sref);
      const hostingBase = 'https://acctoo.com';
      const wrapped = `${hostingBase}/doc-viewer.html?u=${encodeURIComponent(storageUrl)}`;
      if (navigator.share) {
        await navigator.share({ title: (docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'), text: (docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'), url: wrapped });
      } else {
        await navigator.clipboard.writeText(wrapped);
        alert('Public link copied to clipboard');
      }
    } catch (e) {
      console.error('Share link failed:', e);
      alert('Error sharing link. Please try again.');
    }
  };

  // Share: Image (PNG attachment) same behavior as reports
  // Optimized Image Share Handler - generates image as blob and shares via system - Fixed for all document types
  const handleInvoiceShareImage = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      alert('Please select a bill to share as image');
      return;
    }
    
    let tempContainer;
    try {
      // Preload images before image generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signatureUrl || currentCompany?.signUrl || currentCompany?.details?.signUrl || currentCompany?.branding?.signatureUrl
      };
      
      const bankDetails = {
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signatureUrl || currentCompany?.signUrl || currentCompany?.details?.signUrl || currentCompany?.branding?.signatureUrl
      };
      
      console.log('Preloading images for image share...');
      await preloadBillImages(companyDetails, bankDetails);
      
      const html2canvas = await import('html2canvas');
      
      // Create a temporary container and render the correct template with proper data
      tempContainer = document.createElement('div');
      
      // Get the correct template component
      const TemplateComponent = await getTemplateComponent(invoiceBill.docType);
      
      const ReactDOM = await import('react-dom');
      
      // Create a temporary div to render the React component
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '-9999px';
      document.body.appendChild(tempDiv);
      
      // Render the correct template with the same props as the main component
      ReactDOM.render(
        React.createElement(TemplateComponent, {
          billData: buildBillDataForTemplate(invoiceBill),
          companyDetails: billPreviewCompanyDetails,
          partyDetails: parties.find(p => p.id === invoiceBill.party) || {},
          bankDetails: {},
          pageSize: 'a4',
          orientation: 'portrait',
          previewMode: false
        }),
        tempDiv
      );
      
      // Wait for the component to render
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Get the rendered HTML and attach offscreen for accurate layout/image loading
      tempContainer.innerHTML = tempDiv.innerHTML;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.width = '210mm';
      document.body.appendChild(tempContainer);

      // Clean up the React render container
      ReactDOM.unmountComponentAtNode(tempDiv);
      document.body.removeChild(tempDiv);
      
      // Generate image with optimized settings for mobile
      const canvas = await html2canvas.default(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        imageTimeout: 10000, // Increased timeout for mobile
        logging: false, // Disable logging for better performance
        removeContainer: true,
        // Additional mobile-friendly settings
        foreignObjectRendering: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: tempContainer.scrollWidth,
        windowHeight: tempContainer.scrollHeight
      });
      
      canvas.toBlob(async (blob) => {
        const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')}_${invoiceBill.number || 'Bill'}.png`;
        
        try {
          // Try to share via system share
          if (navigator.canShare && navigator.canShare({ files: [new File([blob], filename, { type: 'image/png' })] })) {
            await navigator.share({
              title: `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill.number || 'Bill'}`,
              text: `Sharing ${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill.number || 'Bill'}`,
              files: [new File([blob], filename, { type: 'image/png' })]
            });
          } else {
            // Download fallback
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
          }
        } catch (shareError) {
          // Download fallback if sharing fails
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
        }
      }, 'image/png');
    } catch (error) {
      console.error('Error generating image for sharing:', error);
      alert('Error generating image for sharing. Please try again.');
    }
    finally {
      // Ensure offscreen container is removed
      try { if (tempContainer && tempContainer.parentNode) document.body.removeChild(tempContainer); } catch {}
    }
  };






  const handleInvoiceWheel = (e) => {
    // Only zoom when Ctrl key is held down, otherwise allow normal scrolling
    if (e.ctrlKey && showInvoiceModal) {
      e.preventDefault();
      setInvoiceZoom(z => {
        let next = z + (e.deltaY < 0 ? 0.1 : -0.1);
        next = Math.max(0.5, Math.min(2, next));
        return Math.round(next * 100) / 100;
      });
    }
    // If Ctrl is not held, let the browser handle normal scrolling
  };









    // Show loading state if authentication or database is not ready
    if (!isAuthReady || !db || !userId) {
      return (
        <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center py-4 sm:py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading Sales page...</p>
          </div>
        </div>
      );
    }

    return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center py-4 sm:py-8 mobile-content">
      <style>
        {`
          /* Fix datalist dropdown z-index issues */
          input[list]::-webkit-calendar-picker-indicator {
            z-index: 9999 !important;
          }
          
          /* Ensure datalist dropdown appears above other elements */
          input[list] + datalist {
            z-index: 9999 !important;
            position: relative !important;
          }
          
          /* Fix table cell positioning context for dropdowns */
          .mobile-table td {
            position: relative;
          }
          
          .mobile-table td .relative {
            z-index: auto;
          }
          
          /* Ensure dropdowns in table cells appear above */
          .mobile-table td input[list] {
            position: relative;
            z-index: 10;
          }
          
          /* Custom dropdown styling */
          select[style*="backgroundImage"] {
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          
          select[style*="backgroundImage"]::-ms-expand {
            display: none;
          }
          
          /* Ensure all select dropdowns appear above other content */
          select {
            z-index: 10 !important;
            position: relative;
          }
        `}
      </style>
      {errorMessage && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 bg-red-100 border border-red-400 text-red-700 px-4 sm:px-6 py-3 rounded shadow-lg z-50 flex items-center gap-4 max-w-[90vw]">
          <span className="text-sm sm:text-base">{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className="ml-4 text-red-700 font-bold text-lg min-w-[44px] min-h-[44px] flex items-center justify-center">&times;</button>
        </div>
      )}
      
      <div className="w-full max-w-5xl mb-4 sm:mb-6 px-4 sm:px-0 mobile-content">
        {/* Desktop: Full-width Tabs, Mobile: Dropdown */}
        <div className="mb-2">
          {/* Desktop View - Full-width Tabs */}
          <div className="hidden sm:block">
            <div className="flex bg-gray-100 rounded-lg p-1 justify-center">
              {docTypeOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDocType(opt.value)}
                  className={`flex-initial flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                    docType === opt.value
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {/* Icon for each document type */}
                  {opt.value === 'invoice' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {opt.value === 'challan' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  )}
                  {opt.value === 'quotation' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  )}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile View - Dropdown */}
          <div className="sm:hidden flex items-center justify-center">
            <div className="relative">
              <button
                onClick={() => setShowDocTypeDropdown(!showDocTypeDropdown)}
                className="bg-white rounded-lg shadow-md px-4 py-2 flex items-center gap-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200 min-w-[140px] justify-center"
              >
                {/* Icon for current document type */}
                {docType === 'invoice' && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                {docType === 'challan' && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                )}
                {docType === 'quotation' && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                )}
                <span>{docTypeOptions.find(opt => opt.value === docType)?.label || 'INVOICE'}</span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${showDocTypeDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showDocTypeDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  {docTypeOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setDocType(opt.value);
                        setShowDocTypeDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm font-medium transition-all duration-200 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg ${
                        docType === opt.value
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {/* Icon for each document type */}
                      {opt.value === 'invoice' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                      {opt.value === 'challan' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      )}
                      {opt.value === 'quotation' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      )}
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full px-4 sm:px-6">
        {/* Quick Items Section - 20-60-20 Layout */}
        <div className="mb-6">
          <div className="grid grid-cols-12 gap-4 items-stretch">
            {/* Left Panel - Most Used Items (20%) */}
            <div className="hidden lg:block col-span-12 lg:col-span-2 bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 flex flex-col shadow-sm">
              <h3 className="text-lg font-bold text-blue-800 mb-4 text-center bg-white bg-opacity-70 rounded-lg py-2 px-3 shadow-sm">Most Used Items</h3>

              <div className={`grid grid-cols-${getResponsiveColumns()} gap-2 flex-1 overflow-y-auto`}>
                {mostUsedItems.map((itemData) => {
                  const item = items.find(it => it.id === itemData.id);
                  if (!item) return null;
                  
                  return (
                    <button
                      key={itemData.itemId || itemData.id}
                      onClick={() => handleQuickItemClick(itemData)}
                      className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded-md p-1.5 text-left transition-all duration-200 shadow-sm hover:shadow-md"
                      title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                    >
                      <div className="font-semibold text-sm text-blue-900 truncate">
                        {itemData.itemName || itemData.name || 'Unknown Item'}
                      </div>
                      <div className="text-xs text-blue-700 font-medium">
                        {formatCurrency(itemData.lastRate || 0, companyCurrency)} × {itemData.lastQty || 1}
                      </div>
                      <div className="text-xs text-blue-600">
                        Used {itemData.count} times
                      </div>
                    </button>
                  );
                })}
              </div>
              {mostUsedItems.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-4">
                  No items used yet
                </div>
              )}
            </div>

            {/* Center Panel - Sales Entry Form (60%) */}
            <div className="col-span-12 lg:col-span-8 bg-white rounded-lg shadow-md p-4 sm:p-6">
              {/* Form Reset Indicator */}
              {isResettingForm && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                  <span className="text-blue-700 font-medium">Resetting form to initial state...</span>
                </div>
              )}
              {/* Fixed Header - Mobile & Desktop */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <div className="text-xs text-gray-500 mb-1">{numberLabel}</div>
                    <div className="text-sm font-semibold text-gray-800">{invoiceNumber || 'Auto'}</div>
                  </div>
                  <div className="flex-1 ml-4">
                    <div className="text-xs text-gray-500 mb-1">Date</div>
                    <div className="text-sm font-semibold text-gray-800">{formatDateToCompanyFormat(invoiceDate, companyProfile)}</div>
                  </div>
                  {company.gstinType === 'Regular' && (
                    <div className="flex-1 ml-4">
                      <div className="text-xs text-gray-500 mb-1">GST Calculation</div>
                      <div className="text-sm font-semibold text-gray-800">
                        {gstCalculationType === 'excluding' ? 'Excluding GST' : 'Including GST'}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setShowMobileEditModal(true)}
                    className="ml-2 p-1 text-gray-600 hover:text-blue-600"
                    title="Edit Details"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Party Selection - Mobile & Desktop */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Party (Buyer)</label>
                <div className="relative" ref={partyDropdownRef}>
                  <input
                    type="text"
                    value={partySearchText}
                    onChange={(e) => {
                      setPartySearchText(e.target.value);
                      if (e.target.value === '') {
                        setParty('');
                        setSelectedPartyForQuickItems('');
                      }
                    }}
                    onFocus={() => setShowPartyDropdown(true)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                    placeholder="Search or type new party name..."
                  />
                  {showPartyDropdown && (
                    <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filteredParties.map(p => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setParty(p.id);
                            setSelectedPartyForQuickItems(p.id);
                            setPartySearchText(p.firmName);
                            setShowPartyDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                        >
                          {p.firmName}
                        </button>
                      ))}
                      {partySearchText && !filteredParties.some(p => p.firmName.toLowerCase() === partySearchText.toLowerCase()) && (
                        <button
                          onClick={() => {
                            setNewPartyName(partySearchText);
                            setShowAddPartyModal(true);
                            setShowPartyDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-200"
                        >
                          + Add "{partySearchText}" as new party
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
          <div className="flex items-center justify-between mb-2">
            <h3 id="invoice-items-title" className="text-xl font-bold text-gray-800">Invoice Items</h3>
            <button
              ref={quickItemButtonRef}
              onClick={(e) => {
                const position = calculateModalPosition(e.currentTarget);
                setQuickItemModalPosition(position);
                setShowQuickItemsModal(true);
              }}
              className="lg:hidden bg-orange-500 hover:bg-orange-600 text-white font-bold py-1 px-3 rounded-md text-sm flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Quick Item
            </button>
          </div>
          
          
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 mb-4 max-h-[600px] custom-scrollbar touch-scrollbar">
            <table ref={tableRef} className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                                    <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Item</th>
                  <th className="px-3 py-2.5 text-left align-top min-w-[12rem] max-w-[16rem] w-[14rem]">
                    <span className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Service tags</span>
                    <span className="mt-1 block text-[11px] leading-snug font-normal text-gray-500 normal-case tracking-normal">Add a tag, then click it for vendor, costs &amp; status (Service Operations).</span>
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Measurement / numbers</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Qty</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Rate</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Discount</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Amount</th>
                  {company.gstinType === 'Regular' && <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">SGST</th>}
                  {company.gstinType === 'Regular' && <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">CGST</th>}
                  {company.gstinType === 'Regular' && <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">IGST</th>}
                  {company.gstinType === 'Regular' && <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Total</th>}
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Remove</th>
                                    </tr>
                                </thead>
              <tbody>
                {rows.map((row, idx) => {
  // Determine GST type for helper text
  const itemObj = items.find(it => it.id === row.item);
  const gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
  const sellerGstin = company.gstin || '';
  const partyObj = parties.find(p => p.id === party);
  const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
  let gstType = '';
  if (sellerGstin && buyerGstin && sellerGstin.length >= 2 && buyerGstin.length >= 2) {
    if (sellerGstin.substring(0, 2) === buyerGstin.substring(0, 2)) {
      gstType = 'intra';
    } else {
      gstType = 'inter';
    }
  }
  return (
    <tr key={idx}>
                    <td className="px-2 py-1">
                      <div className="relative">
                        <input
                          type="text"
                          list={`items-list-${idx}`}
                          className="border border-gray-300 rounded-md p-1 w-48 relative"
                          style={{ zIndex: 1 }}
                          placeholder={t('selectItem')}
                          value={
                            itemSearchTextByRow[idx] !== undefined
                              ? itemSearchTextByRow[idx]
                              : (items.find(it => it.id === row.item)?.itemName || '')
                          }
                          onChange={(e) => {
                            const text = e.target.value;
                            setItemSearchTextByRow(prev => ({ ...prev, [idx]: text }));
                            
                            // If the field is empty, clear the item selection
                            if (!text.trim()) {
                              handleRowChange(idx, 'item', '');
                              return;
                            }
                            
                            // Try to match exactly by name/code/hsn
                            const lower = text.toLowerCase();
                            const match = items.find(it =>
                              (it.itemName || '').toLowerCase() === lower ||
                              (it.code || '').toLowerCase() === lower ||
                              (it.hsn || it.hsnCode || '').toLowerCase() === lower
                            );
                            if (match) {
                              handleRowChange(idx, 'item', match.id);
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: match.itemName }));
                            }
                          }}
                          onBlur={(e) => {
                            const text = e.target.value;
                            
                            // If the field is empty, clear the item selection
                            if (!text.trim()) {
                              handleRowChange(idx, 'item', '');
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: '' }));
                              return;
                            }
                            
                            const lower = text.toLowerCase();
                            const match = items.find(it =>
                              (it.itemName || '').toLowerCase().startsWith(lower) ||
                              (it.code || '').toLowerCase().startsWith(lower) ||
                              (it.hsn || it.hsnCode || '').toLowerCase().startsWith(lower)
                            );
                            if (match) {
                              handleRowChange(idx, 'item', match.id);
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: match.itemName }));
                            }
                          }}
                        />
                        <datalist id={`items-list-${idx}`}>
                          {items.slice(0, 200).map(it => (
                            <option key={it.id} value={it.itemName} />
                          ))}
                        </datalist>
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top min-w-[12rem] max-w-[18rem]">
                      <div className="relative min-w-0">
                        <div className="flex flex-wrap gap-2 mb-2 min-h-[28px] max-w-full">
                          {(row.tags || []).map((tag, tagIdx) => (
                            <span
                              key={tagIdx}
                              className="inline-flex items-center max-w-full min-h-[32px] sm:min-h-[28px] px-2.5 py-1.5 rounded-md text-sm font-medium bg-blue-100 text-blue-900 cursor-pointer hover:bg-blue-200 border border-blue-200/60 touch-manipulation"
                              onClick={() => openServiceEditor(idx, tag)}
                              title="Click to edit vendor, costs & status"
                            >
                              <span className="truncate">{getTagDisplayText(row, tag)}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveTag(idx, tagIdx);
                                }}
                                className="ml-1.5 shrink-0 min-w-[1.25rem] text-center text-blue-700 hover:text-blue-900 text-lg leading-none"
                                aria-label="Remove tag"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <div
                          className="relative min-w-0"
                          data-tag-picker-container="true"
                          ref={(el) => {
                            if (el) tagPickerContainerRefs.current[idx] = el;
                            else delete tagPickerContainerRefs.current[idx];
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => toggleTagPicker(idx)}
                            className="text-sm text-blue-700 hover:text-blue-900 border border-blue-300 rounded-lg px-3 py-2 bg-blue-50 min-h-[40px] sm:min-h-[36px] touch-manipulation w-full sm:w-auto text-left sm:text-center font-medium shadow-sm"
                          >
                            + Add tag
                          </button>
                          {showTagPicker[idx] && (
                            <div className="absolute z-50 mt-2 left-0 right-auto w-[min(100%,20rem)] sm:w-80 max-w-[calc(100vw-1.5rem)] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden touch-manipulation ring-1 ring-black/5">
                              {renderTagPickerDropdown(idx)}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex flex-col">
                        <input
                          type="text"
                          value={row.qtyExpr}
                          onChange={e => handleRowChange(idx, 'qtyExpr', e.target.value)}
                          placeholder="e.g. 5x3x2 or 10*2"
                          className="border border-gray-300 rounded-md p-1 w-40"
                        />
                        {row.qtyDisplay && (
                          <span className="text-xs text-gray-500 mt-1">= {row.qty} ({row.qtyDisplay})</span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" value={row.qty} min={0} readOnly
                        className="border border-gray-300 rounded-md p-1 w-16 bg-gray-100" />
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" value={row.rate} min={0} onChange={e => handleRowChange(idx, 'rate', e.target.value)}
                        className="border border-gray-300 rounded-md p-1 w-16" />
                    </td>
                    {/* Line Discount controls */}
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          value={row.lineDiscountValue}
                          onChange={e => handleRowChange(idx, 'lineDiscountValue', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-20"
                          placeholder={row.lineDiscountType === 'percent' ? 'Percent' : 'Amount'}
                        />
                        <select
                          value={row.lineDiscountType || 'amount'}
                          onChange={e => handleRowChange(idx, 'lineDiscountType', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 relative z-10 w-12 text-center appearance-none cursor-pointer bg-white"
                          style={{
                            backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 4px center',
                            backgroundSize: '12px',
                            paddingRight: '20px'
                          }}
                        >
                          <option value="amount">{getCurrencySymbol(companyCurrency)}</option>
                          <option value="percent">%</option>
                        </select>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" value={row.amount} min={0} readOnly
                        className="border border-gray-300 rounded-md p-1 w-20 bg-gray-100" />
                    </td>
                    {company.gstinType === 'Regular' && (
                      <td className="px-2 py-1">
                        <input type="number" value={row.sgst} min={0} onChange={e => handleRowChange(idx, 'sgst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                        {gstType === 'intra' && <div className="text-xs text-green-600">Auto: Intra-state (SGST)</div>}
                        {gstType === 'inter' && <div className="text-xs text-gray-400">(Not used for Inter-state)</div>}
                      </td>
                    )}
                    {company.gstinType === 'Regular' && (
                      <td className="px-2 py-1">
                        <input type="number" value={row.cgst} min={0} onChange={e => handleRowChange(idx, 'cgst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                        {gstType === 'intra' && <div className="text-xs text-green-600">Auto: Intra-state (CGST)</div>}
                        {gstType === 'inter' && <div className="text-xs text-gray-400">(Not used for Inter-state)</div>}
                      </td>
                    )}
                    {company.gstinType === 'Regular' && (
                      <td className="px-2 py-1">
                        <input type="number" value={row.igst} min={0} onChange={e => handleRowChange(idx, 'igst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                        {gstType === 'inter' && <div className="text-xs text-blue-600">Auto: Inter-state (IGST)</div>}
                        {gstType === 'intra' && <div className="text-xs text-gray-400">(Not used for Intra-state)</div>}
                      </td>
                    )}
                    {company.gstinType === 'Regular' && (
                      <td className="px-2 py-1">
                        <input type="number" value={row.total} min={0} readOnly
                          className="border border-gray-300 rounded-md p-1 w-20 bg-green-50 font-semibold" />
                      </td>
                    )}
                    <td className="px-2 py-1">
                      <button type="button" onClick={() => removeRow(idx)} className="text-red-600 font-bold px-2 py-1 rounded hover:bg-red-100">X</button>
                                            </td>
                                        </tr>
                                    );
                 })}
                 {/* Removed bill-level discount row in table; discount handled per line */}
                                </tbody>
                            </table>
          </div>
          
          {/* Mobile Card View - Carousel Style */}
          <div className="md:hidden mb-4">
            {/* Carousel Container */}
            <div 
              className="relative bg-white border border-gray-200 rounded-lg p-4 shadow-sm transition-all duration-200 ease-in-out"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Swipe Instructions */}
              <div className="text-center text-xs text-gray-500 mb-2">
                Swipe left/right to navigate between items
              </div>
              
              {/* Navigation Header */}
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={goToPreviousItem}
                    disabled={currentMobileItemIndex === 0}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h4 className="text-sm font-semibold text-gray-800">Item #{currentMobileItemIndex + 1}</h4>
                  <button
                    onClick={goToNextItem}
                    disabled={currentMobileItemIndex === rows.length - 1}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <button
                  onClick={() => removeRow(currentMobileItemIndex)}
                  className="text-red-600 hover:text-red-800 text-lg"
                >
                  🗑️
                </button>
              </div>

              {/* Current Item Card */}
              {rows[currentMobileItemIndex] && (() => {
                const row = rows[currentMobileItemIndex];
                const index = currentMobileItemIndex;
                return (
                <div key={currentMobileItemIndex}>
                
                {/* Item Selection */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Item</label>
                  <input
                    type="text"
                    list={`mobile-items-list-${index}`}
                    value={itemSearchTextByRow[index] || ''}
                    onChange={(e) => {
                      const text = e.target.value;
                      setItemSearchTextByRow(prev => ({ ...prev, [index]: text }));
                      
                      if (!text.trim()) {
                        handleRowChange(currentMobileItemIndex, 'item', '');
                        return;
                      }
                      
                      const lower = text.toLowerCase();
                      const match = items.find(it =>
                        (it.itemName || '').toLowerCase() === lower ||
                        (it.code || '').toLowerCase() === lower ||
                        (it.hsn || it.hsnCode || '').toLowerCase() === lower
                      );
                      if (match) {
                        handleRowChange(currentMobileItemIndex, 'item', match.id);
                        setItemSearchTextByRow(prev => ({ ...prev, [currentMobileItemIndex]: match.itemName }));
                      }
                    }}
                    onBlur={(e) => {
                      const text = e.target.value;
                      
                      if (!text.trim()) {
                        handleRowChange(currentMobileItemIndex, 'item', '');
                        setItemSearchTextByRow(prev => ({ ...prev, [currentMobileItemIndex]: '' }));
                        return;
                      }
                      
                      const lower = text.toLowerCase();
                      const match = items.find(it =>
                        (it.itemName || '').toLowerCase().startsWith(lower) ||
                        (it.code || '').toLowerCase().startsWith(lower) ||
                        (it.hsn || it.hsnCode || '').toLowerCase().startsWith(lower)
                      );
                      if (match) {
                        handleRowChange(currentMobileItemIndex, 'item', match.id);
                        setItemSearchTextByRow(prev => ({ ...prev, [currentMobileItemIndex]: match.itemName }));
                      }
                    }}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="Search item..."
                  />
                  <datalist id={`mobile-items-list-${index}`}>
                    {items.map(item => (
                      <option key={item.id} value={item.itemName || item.name}>
                        {item.itemName || item.name} - {formatCurrency(item.rate || item.salePrice || 0, companyCurrency)}
                      </option>
                    ))}
                  </datalist>
                </div>
                
                {/* Tags Section */}
                <div className="mb-4 min-w-0">
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Service tags</label>
                  <p className="text-[11px] text-slate-500 mb-2 leading-snug">Tap a tag for vendor &amp; costs. Add opens the same picker as on desktop.</p>
                  <div className="flex flex-wrap gap-2 mb-2 min-h-[32px] max-w-full">
                    {(row.tags || []).map((tag, tagIdx) => (
                      <span
                        key={tagIdx}
                        className="inline-flex items-center max-w-full min-h-[36px] px-2.5 py-1.5 rounded-md text-sm font-medium bg-blue-100 text-blue-900 cursor-pointer hover:bg-blue-200 border border-blue-200/60 touch-manipulation"
                        onClick={() => openServiceEditor(index, tag)}
                        title="Tap to edit vendor, costs & status"
                      >
                        <span className="truncate">{getTagDisplayText(row, tag)}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveTag(index, tagIdx);
                          }}
                          className="ml-1.5 shrink-0 min-w-[1.25rem] text-center text-blue-700 hover:text-blue-900 text-lg leading-none"
                          aria-label="Remove tag"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div
                    className="relative min-w-0"
                    data-tag-picker-container="true"
                    ref={(el) => {
                      if (el) tagPickerContainerRefs.current[index] = el;
                      else delete tagPickerContainerRefs.current[index];
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => toggleTagPicker(index)}
                      className="text-sm text-blue-700 hover:text-blue-900 border border-blue-300 rounded-lg px-3 py-2.5 bg-blue-50 w-full min-h-[44px] touch-manipulation active:bg-blue-100 font-medium shadow-sm"
                    >
                      + Add tag
                    </button>
                    {showTagPicker[index] && (
                      <div className="absolute z-50 mt-2 left-0 right-0 w-full max-w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden touch-manipulation ring-1 ring-black/5">
                        {renderTagPickerDropdown(index)}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Measurement / Numbers Row */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Measurement / Numbers</label>
                  <div className="flex flex-col">
                    <input
                      type="text"
                      value={row.qtyExpr || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'qtyExpr', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                      placeholder="e.g. 5x3x3, 4x5, 50"
                    />
                    {row.qtyDisplay && (
                      <span className="text-xs text-gray-500 mt-1">= {row.qty} ({row.qtyDisplay})</span>
                    )}
                  </div>
                </div>
                
                {/* Quantity and Rate Row */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                    <input
                      type="number"
                      value={row.qty || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'qty', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                      placeholder="0"
                      min="0"
                      step="0.01"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rate</label>
                    <input
                      type="number"
                      value={row.rate || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'rate', e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                
                {/* Discount Row */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Discount</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      value={row.lineDiscountValue || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'lineDiscountValue', e.target.value)}
                      className="flex-1 p-2 border border-gray-300 rounded text-sm"
                      placeholder={row.lineDiscountType === 'percent' ? 'Percent' : 'Amount'}
                    />
                    <select
                      value={row.lineDiscountType || 'amount'}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'lineDiscountType', e.target.value)}
                      className="border border-gray-300 rounded text-sm p-2 w-20 relative z-10 appearance-none cursor-pointer bg-white text-center"
                      style={{
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 6px center',
                        backgroundSize: '14px',
                        paddingRight: '24px'
                      }}
                    >
                      <option value="amount">{getCurrencySymbol(companyCurrency)}</option>
                      <option value="percent">%</option>
                    </select>
                  </div>
                </div>
                
                {/* Amount Row */}
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                  <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-sm font-semibold text-blue-600">
                    {formatCurrency(parseFloat(row.amount) || 0, companyCurrency)}
                  </div>
                </div>
                
                {/* GST Row (if applicable) */}
                {company.gstinType === 'Regular' && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">SGST</label>
                      <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                        {formatCurrency(parseFloat(row.sgst) || 0, companyCurrency)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">CGST</label>
                      <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                        {formatCurrency(parseFloat(row.cgst) || 0, companyCurrency)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">IGST</label>
                      <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                        {formatCurrency(parseFloat(row.igst) || 0, companyCurrency)}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Total Row */}
                <div className="bg-blue-50 p-3 rounded border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700">Total:</span>
                    <span className="text-lg font-bold text-blue-600">
                      {formatCurrency(parseFloat(row.total) || parseFloat(row.amount) || 0, companyCurrency)}
                    </span>
                  </div>
                </div>
              </div>
                );
              })()}

              {/* Item Numbers Pagination */}
              <div className="mt-4 flex justify-center">
                <div className="flex gap-2">
                  {rows.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => goToItem(index)}
                      className={`w-8 h-8 rounded-full text-sm font-medium ${
                        index === currentMobileItemIndex
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-6">
            <div className="flex flex-col md:flex-row gap-2 mb-4 md:mb-0">
              <button onClick={addRow} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md">Add Item Row</button>
              <button onClick={() => setShowAddItemModal(true)} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md">Add New Item</button>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 w-full md:w-80 text-xs sm:text-sm min-w-0">
              <div className="flex justify-between gap-2 mb-1"><span className="min-w-0 shrink">Subtotal (Excl. GST):</span><span className="shrink-0 tabular-nums">{formatCurrency(rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0), companyCurrency)}</span></div>
              <div className="flex justify-between gap-2 mb-1"><span className="min-w-0 shrink">Discount:</span><span className="shrink-0 tabular-nums">-{formatCurrency(roundTo2Decimals(discount), companyCurrency)}</span></div>
              <div className="flex justify-between gap-2 mb-1 font-semibold"><span className="min-w-0 shrink">Net Subtotal:</span><span className="shrink-0 tabular-nums">{formatCurrency(discountedSubtotal, companyCurrency)}</span></div>
              <div className="flex justify-between gap-2 mb-1"><span className="min-w-0 shrink">Total SGST:</span><span className="shrink-0 tabular-nums">{formatCurrency(totalSGST, companyCurrency)}</span></div>
              <div className="flex justify-between gap-2 mb-1"><span className="min-w-0 shrink">Total CGST:</span><span className="shrink-0 tabular-nums">{formatCurrency(totalCGST, companyCurrency)}</span></div>
              <div className="flex justify-between gap-2 mb-1"><span className="min-w-0 shrink">Total IGST:</span><span className="shrink-0 tabular-nums">{formatCurrency(totalIGST, companyCurrency)}</span></div>
              <div className="flex justify-between gap-2 mb-1"><span className="min-w-0 shrink leading-tight">Service Charges (Invoice Visible):</span><span className="shrink-0 tabular-nums">{formatCurrency(totalVisibleServiceCharges, companyCurrency)}</span></div>
              <div className="flex justify-between gap-2 mb-1"><span className="min-w-0 shrink">Round Off:</span><span className="shrink-0 tabular-nums">{roundOffAmount >= 0 ? '+' : ''}{formatCurrency(roundOffAmount, companyCurrency)}</span></div>
              <div className="flex justify-between gap-2 mt-2 font-bold text-base sm:text-lg"><span className="min-w-0 shrink leading-tight">Grand Total (Incl. GST):</span><span className="text-blue-700 shrink-0 tabular-nums">{formatCurrency(grandTotal, companyCurrency)}</span></div>
            </div>
          </div>

          {/* Sub parties + Manage tags — directly above Add Note */}
          <div className="mb-3 print:hidden">
            <div className="flex flex-col gap-2">
              <div className="flex gap-2 sm:gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={openSubPartyModal}
                  title="Split invoice total across sub parties and assign lines"
                  className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 active:bg-indigo-200 border border-indigo-200 rounded-md transition-colors touch-manipulation"
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Sub parties
                  {subPartyBreakdown.length > 0 && (
                    <span className="text-xs font-normal text-indigo-600 tabular-nums">({subPartyBreakdown.length})</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowManageTagsModal(true)}
                  title="Manage item tags for this invoice"
                  className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-amber-800 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 border border-amber-200 rounded-md transition-colors touch-manipulation"
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Manage tags
                </button>
              </div>
              {subPartyBreakdown.length > 0 && (
                <div
                  className={`text-xs sm:text-sm ${subPartyAppliedMatches ? 'text-green-700' : 'text-amber-700'}`}
                >
                  Sum {formatCurrency(subPartyAppliedSum, companyCurrency)} → invoice total{' '}
                  {formatCurrency(grandTotal, companyCurrency)}
                  {subPartyAppliedMatches ? ' ✓' : ' — adjust in modal to match'}
                </div>
              )}
            </div>
          </div>

          {/* Add Note and E-way Bill Buttons */}
          <div className="mb-6 print:hidden">
            <div className="flex gap-2 sm:gap-3 flex-wrap">
              {/* Add Note Button */}
              <button
                type="button"
                onClick={() => setShowNotesModal(true)}
                className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 border border-blue-200 rounded-md transition-colors touch-manipulation"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Note
              </button>
              
              {/* Add E-way Bill Button */}
              <button
                type="button"
                onClick={() => setShowEwayBillModal(true)}
                className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 active:bg-green-200 border border-green-200 rounded-md transition-colors touch-manipulation"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add E-way Bill
              </button>

              <button
                type="button"
                onClick={openServiceOperations}
                className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 active:bg-purple-200 border border-purple-200 rounded-md transition-colors touch-manipulation"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6m3 6V7m3 10v-4M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Service Ops
              </button>
            </div>
          </div>

          <div className="flex gap-4 mt-4">
            <button
              id="save-invoice-button"
              onClick={handleSaveInvoice}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105 print:hidden"
            >
              {editingBillId ? 'Update Invoice' : 'Save Complete Invoice'}
            </button>
            <button
              onClick={resetFormToInitialState}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105 print:hidden"
            >
              Clear Data
            </button>
            {editingBillId && (
              <button
                onClick={() => handleAddPayment(bills.find(b => b.id === editingBillId))}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105 print:hidden"
              >
                Add Payment
              </button>
            )}
          </div>
          </div> {/* Close Center Panel */}
          
                      {/* Right Panel - Party-wise Items (20%) */}
            <div className="hidden lg:block col-span-12 lg:col-span-2 bg-gradient-to-br from-green-50 via-green-50 to-green-100 border border-green-200 rounded-lg p-4 flex flex-col shadow-sm">
            <h3 className="text-lg font-bold text-green-800 mb-4 text-center bg-white bg-opacity-70 rounded-lg py-2 px-3 shadow-sm">Party Items</h3>

            {!selectedPartyForQuickItems ? (
              <div className="text-center text-gray-500 text-sm py-4">
                Select a party to see their items
              </div>
            ) : (
              <>
                            <div className="text-xs text-gray-600 mb-3 text-center">
              {parties.find(p => p.id === selectedPartyForQuickItems)?.firmName}
            </div>
                <div className={`grid grid-cols-${getResponsiveColumns()} gap-2 flex-1 overflow-y-auto`}>
                  {partyWiseItems.map((itemData) => {
                    const item = items.find(it => it.id === itemData.itemId);
                    if (!item) return null;
                    
                    return (
                      <button
                        key={itemData.itemId || itemData.id}
                        onClick={() => handleQuickItemClick(itemData, true)}
                        className="bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded-md p-1.5 text-left transition-all duration-200 shadow-sm hover:shadow-md"
                        title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times with this party`}
                      >
                        <div className="font-semibold text-sm text-green-900 truncate">
                          {itemData.itemName || itemData.name || 'Unknown Item'}
                        </div>
                        <div className="text-xs text-green-700 font-medium">
                          {formatCurrency(itemData.lastRate || 0, companyCurrency)} × {itemData.lastQty || 0}
                        </div>
                        <div className="text-xs text-green-600">
                          Used {itemData.count} times
                        </div>
                      </button>
                    );
                  })}
                </div>
                {partyWiseItems.length === 0 && (
                  <div className="text-center text-gray-500 text-sm py-4">
                    No items used with this party
                  </div>
                )}
              </>
            )}
          </div>
        </div> {/* Close Quick Items Grid */}
        
        {/* Quick Items Modal - Compact */}
        {showQuickItemsModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowQuickItemsModal(false);
              }
            }}
          >
            <div 
              className="absolute bg-white rounded-lg shadow-xl max-w-2xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-hidden"
              style={quickItemModalPosition}
            >
              <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Quick Items</h3>
                <button
                  onClick={() => setShowQuickItemsModal(false)}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-3 overflow-y-auto max-h-[calc(85vh-60px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Most Used Items */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-200">
                      Most Used ({mostUsedItems.length})
                    </h4>
                    <div className="grid gap-1.5 max-h-80 overflow-y-auto" style={{
                      gridTemplateColumns: 
                        window.innerWidth >= 360 ? 'repeat(3, 1fr)' : 
                        window.innerWidth >= 144 ? 'repeat(2, 1fr)' : 
                        '1fr'
                    }}>
                      {mostUsedItems.map((itemData) => {
                        const item = items.find(it => it.id === itemData.id);
                        if (!item) return null;
                        
                        return (
                          <button
                            key={itemData.itemId || itemData.id}
                            onClick={() => {
                              handleQuickItemClick(itemData);
                              setShowQuickItemsModal(false);
                            }}
                            className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded p-1.5 text-left transition-all duration-200 shadow-sm hover:shadow-md"
                            title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                          >
                            <div className="font-medium text-xs text-blue-900 leading-tight break-words">
                              {itemData.itemName || itemData.name || 'Unknown Item'}
                            </div>
                            <div className="text-xs text-blue-700 font-medium mt-0.5">
                              {formatCurrency(itemData.lastRate || 0, companyCurrency)}×{itemData.lastQty || 1}
                            </div>
                            <div className="text-xs text-blue-600">
                              {itemData.count}×
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {mostUsedItems.length === 0 && (
                      <div className="text-center text-gray-500 text-xs py-3">
                        No items used yet
                      </div>
                    )}
                  </div>

                  {/* Party-wise Items */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-200">
                      Party Items ({partyWiseItems.length})
                    </h4>
                    <select
                      value={selectedPartyForQuickItems}
                      onChange={(e) => setSelectedPartyForQuickItems(e.target.value)}
                      className="w-full p-1.5 border border-gray-300 rounded text-xs mb-2"
                    >
                      <option value="">Select Party</option>
                      {Array.from(new Set(bills.map(bill => bill.party).filter(Boolean))).map((partyId, index) => {
                        const partyName = parties.find(p => p.id === partyId)?.firmName || partyId;
                        return (
                          <option key={index} value={partyId}>{partyName}</option>
                        );
                      })}
                    </select>
                    
                    {!selectedPartyForQuickItems ? (
                      <div className="text-center text-gray-500 text-xs py-3">
                        Select a party to see their items
                      </div>
                    ) : (
                      <div className="grid gap-1.5 max-h-80 overflow-y-auto" style={{
                        gridTemplateColumns: 
                          window.innerWidth >= 360 ? 'repeat(3, 1fr)' : 
                          window.innerWidth >= 144 ? 'repeat(2, 1fr)' : 
                          '1fr'
                      }}>
                        {partyWiseItems.map((itemData) => {
                          const item = items.find(it => it.id === itemData.itemId);
                          if (!item) return null;
                          
                          return (
                            <button
                              key={itemData.itemId || itemData.id}
                              onClick={() => {
                                handleQuickItemClick(itemData);
                                setShowQuickItemsModal(false);
                              }}
                              className="bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded p-1.5 text-left transition-all duration-200 shadow-sm hover:shadow-md"
                              title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                            >
                              <div className="font-medium text-xs text-green-900 leading-tight break-words">
                                {itemData.itemName || itemData.name || 'Unknown Item'}
                              </div>
                              <div className="text-xs text-green-700 font-medium mt-0.5">
                                {formatCurrency(itemData.lastRate || 0, companyCurrency)}×{itemData.lastQty || 1}
                              </div>
                              <div className="text-xs text-green-600">
                                {itemData.count}×
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {partyWiseItems.length === 0 && selectedPartyForQuickItems && (
                      <div className="text-center text-gray-500 text-xs py-3">
                        No items found for this party
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Mobile Edit Modal */}
        {showMobileEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Edit Details</h3>
                <button
                  onClick={() => setShowMobileEditModal(false)}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{numberLabel}</label>
                  <input 
                    type="text" 
                    value={invoiceNumber} 
                    onChange={e => setInvoiceNumber(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input 
                    type="date" 
                    value={invoiceDate} 
                    onChange={e => setInvoiceDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" 
                  />
                </div>
                {company.gstinType === 'Regular' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST Calculation</label>
                    <select
                      value={gstCalculationType}
                      onChange={(e) => setGstCalculationType(e.target.value)}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                    >
                      <option value="excluding">Rate Excluding GST</option>
                      <option value="including">Rate Including GST</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {gstCalculationType === 'excluding' 
                        ? 'GST will be added to the rate' 
                        : 'GST is included in the rate'}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Party (Buyer)</label>
                  <div className="relative" ref={partyDropdownRef}>
                    <input
                      type="text"
                      value={partySearchText}
                      onChange={(e) => {
                        setPartySearchText(e.target.value);
                        if (e.target.value === '') {
                          setParty('');
                          setSelectedPartyForQuickItems('');
                        }
                      }}
                      onFocus={() => setShowPartyDropdown(true)}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                      placeholder="Search or type new party name..."
                    />
                    {showPartyDropdown && (
                      <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-32 overflow-y-auto">
                        {filteredParties.map(p => (
                          <button
                            key={p.id}
                            onClick={() => {
                              setParty(p.id);
                              setSelectedPartyForQuickItems(p.id);
                              setPartySearchText(p.firmName);
                              setShowPartyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                          >
                            {p.firmName}
                          </button>
                        ))}
                        {partySearchText && !filteredParties.some(p => p.firmName.toLowerCase() === partySearchText.toLowerCase()) && (
                          <button
                            onClick={() => {
                              setNewPartyName(partySearchText);
                              setShowAddPartyModal(true);
                              setShowPartyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-200"
                          >
                            + Add "{partySearchText}" as new party
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setShowMobileEditModal(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowMobileEditModal(false)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md text-sm"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Success Modal - Desktop: over form, Mobile: after form */}
        {showSuccessModal && (
          <>
            {/* Mobile View - Inline */}
            <div className="mt-6 w-full mx-auto lg:hidden">
              <div ref={successModalRef} className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 max-w-md w-full mx-auto relative mx-4">
                <div className="absolute top-2 right-2 flex items-center space-x-2">
                  <span className="text-xs text-gray-500">Press ESC to close</span>
                  <button 
                    onClick={() => setShowSuccessModal(false)} 
                    className="text-gray-500 hover:text-gray-800 text-xl font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                  >
                    ×
                  </button>
                </div>
                
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Saved Successfully!
                  </h3>
                  <p className="text-gray-600">What would you like to do next?</p>
                </div>
                
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      const bill = bills.find(b => b.id === savedBillId);
                      if (bill) {
                        const invoiceData = {
                          ...bill,
                          companyDetails: billPreviewCompanyDetails,
                          partyDetails: parties.find(p => p.id === bill.party) || {},
                          items: items,
                          docType: docType
                        };
                        handleMobileInvoiceView(invoiceData);
                      }
                      setShowSuccessModal(false);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                    </svg>
                    Print {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      resetFormToInitialState();
                      const prefixMap = { invoice: 'INV', challan: 'CHA', quotation: 'QUO' };
                      const prefix = prefixMap[docType] || 'INV';
                      const fy = getFinancialYear(new Date());
                      const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
                      const serials = bills
                        .filter(bill => (bill.number || '').startsWith(`${prefix}${fyShort}/`))
                        .map(bill => parseInt((bill.number || '').split('/')[1], 10))
                        .filter(n => !isNaN(n));
                      const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
                      setInvoiceNumber(`${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`);
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Create New {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                  </button>
                  
                  <button
                    onClick={() => {
                      const bill = bills.find(b => b.id === savedBillId);
                      if (bill) {
                        handleAddPayment(bill);
                      }
                      setShowSuccessModal(false);
                    }}
                    className="w-full bg-green-100 hover:bg-green-200 text-green-700 font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Add Payment
                  </button>
                  
                  <button
                    onClick={() => {
                      const bill = bills.find(b => b.id === savedBillId);
                      if (bill) {
                        handleEditBill(bill);
                      }
                      setShowSuccessModal(false);
                    }}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-md"
                  >
                    Edit This {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop View - Overlay */}
            <div className="hidden lg:fixed lg:inset-0 lg:flex lg:items-center lg:justify-center lg:z-50">
              <div ref={successModalRef} className="bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-6 max-w-md w-full mx-auto relative">
              <div className="absolute top-2 right-2 flex items-center space-x-2">
                <span className="text-xs text-gray-500">Press ESC to close</span>
                <button 
                  onClick={() => setShowSuccessModal(false)} 
                  className="text-gray-500 hover:text-gray-800 text-xl font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                >
                  ×
                </button>
              </div>
              
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Saved Successfully!
                </h3>
                <p className="text-gray-600">What would you like to do next?</p>
              </div>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    const bill = bills.find(b => b.id === savedBillId);
                    if (bill) {
                      const invoiceData = {
                        ...bill,
                        companyDetails: billPreviewCompanyDetails,
                        partyDetails: parties.find(p => p.id === bill.party) || {},
                        items: items,
                        docType: docType
                      };
                      handleMobileInvoiceView(invoiceData);
                    }
                    setShowSuccessModal(false);
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                  </svg>
                  Print {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                </button>
                
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    resetFormToInitialState();
                    const prefixMap = { invoice: 'INV', challan: 'CHA', quotation: 'QUO' };
                    const prefix = prefixMap[docType] || 'INV';
                    const fy = getFinancialYear(new Date());
                    const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
                    const serials = bills
                      .filter(bill => (bill.number || '').startsWith(`${prefix}${fyShort}/`))
                      .map(bill => parseInt((bill.number || '').split('/')[1], 10))
                      .filter(n => !isNaN(n));
                    const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
                    setInvoiceNumber(`${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  Create New {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                </button>
                
                <button
                  onClick={() => {
                    const bill = bills.find(b => b.id === savedBillId);
                    if (bill) {
                      handleAddPayment(bill);
                    }
                    setShowSuccessModal(false);
                  }}
                  className="w-full bg-green-100 hover:bg-green-200 text-green-700 font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  Add Payment
                </button>
                
                <button
                  onClick={() => {
                    const bill = bills.find(b => b.id === savedBillId);
                    if (bill) {
                      handleEditBill(bill);
                    }
                    setShowSuccessModal(false);
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-md"
                >
                  Edit This {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                </button>
              </div>
            </div>
          </div>
          </>
        )}
        
        {/* Sales Bill List Section - 100% Width Centered */}
        <div className="mt-10 w-full mx-auto">
            <div className="flex justify-between items-center mb-4 px-4">
              <h3 className="text-xl font-bold text-gray-800">Sales Bill List</h3>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
              >
                {showFilters ? 'Hide Table & Filters' : 'Show Table & Filters'}
              </button>
            </div>
            
            {/* Filter Section and Bill List - Both controlled by showFilters */}
            {showFilters ? (
              <>
              <CompactFilterBar
                filters={[
                  {
                    key: 'search',
                    label: 'Search Bills & Parties',
                    type: 'text',
                    value: filterSearch,
                    placeholder: 'Search by bill number or party name...',
                    fullWidth: true
                  },
                  {
                    key: 'date',
                    label: 'Date',
                    type: 'text',
                    value: filterDate,
                    placeholder: 'DD/MM/YYYY or partial'
                  },
                  {
                    key: 'amount',
                    label: 'Amount',
                    type: 'text',
                    value: filterAmount,
                    placeholder: t('filterByAmount')
                  },
                  {
                    key: 'tag',
                    label: 'Tag',
                    type: 'select',
                    value: filterTag,
                    placeholder: 'Filter by tag...',
                    options: [
                      { value: '', label: 'All Tags' },
                      ...getAllKnownTags().map(tag => ({ value: tag, label: tag.charAt(0).toUpperCase() + tag.slice(1) }))
                    ]
                  }
                ]}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                showClearButton={true}
                inlineSearch={true}
                className="bg-gray-50"
              />
            <div className="overflow-x-auto rounded-lg border border-gray-200 mb-4 w-full">
              {/* Desktop Dropdown Form View */}
              <div className="hidden lg:block">
                {/* Header Row */}
                <div className="bg-gray-50 border border-gray-200 rounded-t-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-6 gap-2 lg:gap-4 items-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <SortableHeader
                        columnKey="number"
                        label={numberLabel.split(' ')[0] + ' No.'}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center truncate cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="invoiceDate"
                        label={t('date')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="partyName"
                        label={t('party')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="amount"
                        label={t('amount')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="paid"
                        label={t('paid')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="outstanding"
                        label={t('outstanding')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                    </div>
                    <div className="ml-2 lg:ml-4 w-20 lg:w-24 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </div>
                  </div>
                </div>
                
                {/* Dropdown Form Rows */}
                <div className="max-h-[600px] overflow-y-auto custom-scrollbar">
                  {pagination.currentData.map((bill, idx) => (
                    <DropdownFormRow
                      key={idx}
                      bill={bill}
                      docType={docType}
                      docTypeOptions={docTypeOptions}
                      companyCurrency={companyCurrency}
                      currentCompany={currentCompany}
                      onView={handleViewBill}
                      onEdit={handleEditBill}
                      onDelete={handleDeleteBill}
                      onPrint={() => {
                                  const invoiceData = {
                            ...bill,
                            companyDetails: billPreviewCompanyDetails,
                            partyDetails: parties.find(p => p.id === bill.party) || {},
                            items: items,
                                    docType: docType
                                  };
                                  handleMobileInvoiceView(invoiceData);
                      }}
                      onPayment={handleAddPayment}
                      onReceipts={handleViewPaymentReceipts}
                      receiptsCount={getBillPayments(bill).length}
                      receiptNumbers={getBillPayments(bill).map(payment => payment.receiptNumber)}
                      items={items}
                      index={idx}
                      isExpanded={expandedRows[idx] || false}
                      onToggle={toggleRowExpansion}
                    />
                  ))}
                </div>
              </div>
                        
                        {/* Mobile Card View with Collapsible Dropdown */}
                        <div className="lg:hidden">
                            {pagination.currentData.map((bill, idx) => (
                                <SalesCard 
                                    key={idx} 
                                    bill={bill}
                                    docType={docType}
                                    docTypeOptions={docTypeOptions}
                                    companyCurrency={companyCurrency}
                                    currentCompany={currentCompany}
                                    items={items}
                                    index={idx}
                                    isExpanded={expandedRows[idx]}
                                    onToggle={toggleRowExpansion}
                                    onView={handleViewBill}
                                    onEdit={handleEditBill}
                                    onDelete={handleDeleteBill}
                                    onPayment={handleAddPayment}
                                    onReceipts={handleViewPaymentReceipts}
                                    receiptsCount={getBillPayments(bill).length}
                                    receiptNumbers={getBillPayments(bill).map(payment => payment.receiptNumber)}
                                    onPrint={() => {
                                        const invoiceData = {
                                            ...bill,
                                            companyDetails: billPreviewCompanyDetails,
                                            partyDetails: parties.find(p => p.id === bill.party) || {},
                                            items: items,
                                            docType: docType
                                        };
                                        handleMobileInvoiceView(invoiceData);
                                    }}
                                />
                            ))}
                        </div>
                        
                                                {/* Pagination Controls */}
                        <PaginationControls {...pagination} />
                        </div>
              </>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p className="text-lg">Table and filters are hidden</p>
                <p className="text-sm">Click "Show Table & Filters" to view sales bills</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Bill Modal */}
      {showViewModal && viewBill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full relative">
            <div className="absolute top-2 right-2 flex items-center space-x-2">
              <span className="text-xs text-gray-500">Press ESC to close</span>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-800 text-xl">&times;</button>
            </div>
            <h3 className="text-xl font-bold mb-4 text-center">Sales Bill Summary</h3>
            <div className="mb-2 flex flex-col gap-1">
              <div><span className="font-semibold">{numberLabel}:</span> {viewBill.number}</div>
              <div><span className="font-semibold">Date:</span> {formatDateToCompanyFormat(viewBill.invoiceDate || viewBill.date, companyProfile)}</div>
              <div><span className="font-semibold">Party:</span> {(parties.find(p => p.id === viewBill.party)?.firmName) || viewBill.party || 'Unknown'}</div>
              <div><span className="font-semibold">Amount:</span> {typeof viewBill.amount === 'number' ? formatCurrency(viewBill.amount, companyCurrency) : viewBill.amount}</div>
              {viewBill.notes && <div><span className="font-semibold">Notes:</span> {viewBill.notes}</div>}
            </div>
            <div className="mt-4">
              <div className="font-semibold mb-1">Items Sold:</div>
              <ul className="list-disc list-inside text-sm">
                {(viewBill.rows || []).map((row, i) => {
                  const itemObj = (items || []).find(it => it.id === row.item) || {};
                  const itemName = itemObj.itemName || row.item || '?';
                  return (
                    <li key={i}>{itemName} (Qty: {row.qty}, Rate: {formatCurrency(row.rate, companyCurrency)})</li>
                  );
                })}
              </ul>
            </div>
          </div>
                    </div>
                )}
             {/* Delete Confirmation Modal */}
       <ConfirmationModal section={MODAL_SECTIONS.SALES}
         isOpen={showDeleteConfirm}
         onClose={cancelDeleteBill}
         title={t('confirmDelete')}
         message="Are you sure you want to delete this sales bill?"
         onConfirm={confirmDeleteBill}
         confirmText={t('delete')}
         cancelText={t('cancel')}
         variant="danger"
       />
             {/* Invoice Preview Modal */}
       <PreviewModal section={MODAL_SECTIONS.SALES}
         isOpen={showInvoiceModal && invoiceBill}
         onClose={() => setShowInvoiceModal(false)}
         title={`${docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Preview`}
         maxWidth="max-w-[90vw] sm:max-w-[60vw] lg:max-w-4xl"
         maxHeight="max-h-[85vh] sm:max-h-[90vh]"
         showPrintButton={true}
         onPrint={handleInvoicePrint}
         showPdfButton={true}
         onPdf={handleInvoiceDownloadWithHelpers}
         extraActions={
           <SalesShareButton
             onExportPDF={handleInvoiceSharePdf}
             onExportImage={handleInvoiceShareImage}
             disabled={!invoiceBill}
           />
         }
         showZoomControls={true}
         zoom={invoiceZoom}
         onZoomIn={() => setInvoiceZoom(z => Math.min(3, z + 0.1))}
         onZoomOut={() => setInvoiceZoom(z => Math.max(0.5, z - 0.1))}
         onZoomReset={() => setInvoiceZoom(1)}
         zIndex="z-50"
       >
         <div 
           className="flex-1 overflow-auto bg-gray-50 print:bg-white relative" 
           onWheel={handleInvoiceWheel} 
           style={{ 
             minHeight: 0,
             maxHeight: 'calc(100vh - 200px)', // Ensure it has a max height for scrolling
             overflowY: 'auto'
           }}
         >
           {/* Zoom instruction */}
           <div className="absolute top-2 left-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md z-10 hidden sm:block">
             Hold Ctrl + Scroll to zoom
           </div>
           <div className="flex justify-center items-start w-full p-4 min-h-full">
             <div className="w-full max-w-4xl min-h-full">
               <div 
                 ref={invoiceRef} 
                 style={{ 
                   transform: `scale(${invoiceZoom})`, 
                   transformOrigin: 'top center', 
                   transition: 'transform 0.2s', 
                   background: 'white', 
                   boxShadow: '0 0 8px #ccc', 
                   margin: '0 auto', 
                   width: '210mm', 
                   minHeight: '297mm', 
                   maxWidth: '100%', 
                   padding: 0,
                   height: 'auto' // Ensure height is auto to allow content to expand
                 }} 
                 className="print:shadow-none print:bg-white print:transform-none print:p-0 print:m-0 print:max-w-none print:w-[210mm] print:min-h-[297mm]"
               >
                 {invoiceBill?.docType === 'challan' ? (
                   <ChallanTemplate
                     billData={{
                       ...invoiceBill,
                       challanNumber: invoiceBill?.number || invoiceBill?.challanNumber || '',
                       challanDate: invoiceBill?.invoiceDate || invoiceBill?.date || '',
                       items: (invoiceBill?.rows || invoiceBill?.items || []).map(row => {
                         const itemMaster = items.find(it => it.id === row.item);
                         return {
                           ...row,
                           description: itemMaster?.itemName || '',
                           hsn: itemMaster?.hsnCode || '',
                         };
                       }),
                     }}
                     companyDetails={{
                       firmName: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       logoUrl: currentCompany?.logoUrl || currentCompany?.branding?.logoUrl || currentCompany?.details?.logoUrl || company?.logoUrl || company?.branding?.logoUrl || '',
                       address: currentCompany?.address || currentCompany?.details?.address || company?.address || '',
                       city: currentCompany?.city || currentCompany?.details?.city || company?.city || '',
                       pincode: currentCompany?.pincode || currentCompany?.details?.pincode || company?.pincode || '',
                       state: currentCompany?.state || currentCompany?.details?.state || company?.state || '',
                       gstin: currentCompany?.basicInfo?.gstNumber || currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.details?.gstin || company?.basicInfo?.gstNumber || company?.gstin || company?.gstNumber || '',
                       contactNumber: currentCompany?.basicInfo?.phone || currentCompany?.phone || currentCompany?.contactNumber || company?.basicInfo?.phone || company?.contactNumber || '',
                       email: currentCompany?.basicInfo?.email || currentCompany?.email || company?.basicInfo?.email || company?.email || '',
                       gstinType: currentCompany?.basicInfo?.gstType || currentCompany?.gstType || company?.basicInfo?.gstType || company?.gstType || 'Unregistered'
                     }}
                     partyDetails={{
                       ...parties.find(p => p.id === invoiceBill?.party) || {},
                       name: (parties.find(p => p.id === invoiceBill?.party)?.firmName || parties.find(p => p.id === invoiceBill?.party)?.name || ''),
                     }}
                     bankDetails={{
                       name: company.bankName,
                       account: company.bankAccount,
                       ifsc: company.bankIfsc,
                       upi: company.upiId,
                       upiQrUrl: company.upiQrUrl,
                       paymentGatewayLink: company.paymentGatewayLink,
                       sealUrl: (currentCompany || company)?.sealUrl || (currentCompany || company)?.details?.sealUrl || (currentCompany || company)?.branding?.sealUrl,
                       signUrl: (currentCompany || company)?.signatureUrl || (currentCompany || company)?.signUrl || (currentCompany || company)?.details?.signUrl || (currentCompany || company)?.branding?.signatureUrl,
                     }}
                     payments={invoiceBill?.payments || []}
                     pageSize={'a4'}
                     orientation={'portrait'}
                     previewMode={false}
                   />
                 ) : invoiceBill?.docType === 'quotation' ? (
                   <QuotationTemplate
                     billData={{
                       ...invoiceBill,
                       quotationNumber: invoiceBill?.number || invoiceBill?.quotationNumber || '',
                       date: invoiceBill?.invoiceDate || invoiceBill?.date || '',
                       items: (invoiceBill?.rows || invoiceBill?.items || []).map(row => {
                         const itemMaster = items.find(it => it.id === row.item);
                         return {
                           ...row,
                           description: itemMaster?.itemName || '',
                         };
                       }),
                     }}
                     companyDetails={{
                       name: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       firmName: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       logoUrl: currentCompany?.logoUrl || currentCompany?.branding?.logoUrl || currentCompany?.details?.logoUrl || company?.logoUrl || company?.branding?.logoUrl || '',
                       address: currentCompany?.address || currentCompany?.details?.address || company?.address || '',
                       city: currentCompany?.city || currentCompany?.details?.city || company?.city || '',
                       pincode: currentCompany?.pincode || currentCompany?.details?.pincode || company?.pincode || '',
                       state: currentCompany?.state || currentCompany?.details?.state || company?.state || '',
                       gstin: currentCompany?.basicInfo?.gstNumber || currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.details?.gstin || company?.basicInfo?.gstNumber || company?.gstin || company?.gstNumber || '',
                       contactNumber: currentCompany?.basicInfo?.phone || currentCompany?.phone || currentCompany?.contactNumber || company?.basicInfo?.phone || company?.contactNumber || '',
                       email: currentCompany?.basicInfo?.email || currentCompany?.email || company?.basicInfo?.email || company?.email || '',
                       gstinType: currentCompany?.basicInfo?.gstType || currentCompany?.gstType || company?.basicInfo?.gstType || company?.gstType || 'Unregistered'
                     }}
                     partyDetails={{
                       ...parties.find(p => p.id === invoiceBill?.party) || {},
                       name: (parties.find(p => p.id === invoiceBill?.party)?.firmName || parties.find(p => p.id === invoiceBill?.party)?.name || ''),
                     }}
                     bankDetails={{
                       name: company.bankName,
                       account: company.bankAccount,
                       ifsc: company.bankIfsc,
                       upi: company.upiId,
                       upiQrUrl: company.upiQrUrl,
                       paymentGatewayLink: company.paymentGatewayLink,
                       sealUrl: (currentCompany || company)?.sealUrl || (currentCompany || company)?.details?.sealUrl || (currentCompany || company)?.branding?.sealUrl,
                       signUrl: (currentCompany || company)?.signatureUrl || (currentCompany || company)?.signUrl || (currentCompany || company)?.details?.signUrl || (currentCompany || company)?.branding?.signatureUrl,
                     }}
                     payments={invoiceBill?.payments || []}
                     pageSize={'a4'}
                     orientation={'portrait'}
                     previewMode={false}
                   />
                 ) : invoiceBill ? (
                   <InvoiceTemplate
                     billData={{
                       ...invoiceBill,
                       invoiceNumber: invoiceBill?.number || invoiceBill?.invoiceNumber || '',
                       date: invoiceBill?.invoiceDate || invoiceBill?.date || '',
                       items: (invoiceBill?.rows || invoiceBill?.items || []).map(row => {
                         const itemMaster = items.find(it => it.id === row.item);
                         return {
                           ...row,
                           description: itemMaster?.itemName || '',
                           hsn: itemMaster?.hsnCode || '',
                         };
                       }),
                     }}
                     companyDetails={{
                       name: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       firmName: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       logoUrl: currentCompany?.logoUrl || currentCompany?.branding?.logoUrl || currentCompany?.details?.logoUrl || company?.logoUrl || company?.branding?.logoUrl || '',
                       address: currentCompany?.address || currentCompany?.details?.address || company?.address || '',
                       city: currentCompany?.city || currentCompany?.details?.city || company?.city || '',
                       pincode: currentCompany?.pincode || currentCompany?.details?.pincode || company?.pincode || '',
                       state: currentCompany?.state || currentCompany?.details?.state || company?.state || '',
                       gstin: currentCompany?.basicInfo?.gstNumber || currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.details?.gstin || company?.basicInfo?.gstNumber || company?.gstin || company?.gstNumber || '',
                       contactNumber: currentCompany?.basicInfo?.phone || currentCompany?.phone || currentCompany?.contactNumber || company?.basicInfo?.phone || company?.contactNumber || '',
                       email: currentCompany?.basicInfo?.email || currentCompany?.email || company?.basicInfo?.email || company?.email || '',
                       gstinType: currentCompany?.basicInfo?.gstType || currentCompany?.gstType || company?.basicInfo?.gstType || company?.gstType || 'Unregistered'
                     }}
                     partyDetails={{
                       ...parties.find(p => p.id === invoiceBill?.party) || {},
                       name: (parties.find(p => p.id === invoiceBill?.party)?.firmName || parties.find(p => p.id === invoiceBill?.party)?.name || ''),
                     }}
                     bankDetails={{
                       name: company.bankName,
                       account: company.bankAccount,
                       ifsc: company.bankIfsc,
                       upi: company.upiId,
                       upiQrUrl: company.upiQrUrl,
                       paymentGatewayLink: company.paymentGatewayLink,
                       sealUrl: (currentCompany || company)?.sealUrl || (currentCompany || company)?.details?.sealUrl || (currentCompany || company)?.branding?.sealUrl,
                       signUrl: (currentCompany || company)?.signatureUrl || (currentCompany || company)?.signUrl || (currentCompany || company)?.details?.signUrl || (currentCompany || company)?.branding?.signatureUrl,
                     }}
                     payments={invoiceBill?.payments || []}
                     pageSize={'a4'}
                     orientation={'portrait'}
                     previewMode={false}
                     sharePreview={true}
                   />
                 ) : (
                   <div className="flex items-center justify-center h-64 text-gray-500">
                     <div className="text-center">
                       <div className="text-lg font-semibold mb-2">No Invoice Data</div>
                       <div className="text-sm">Please select a valid invoice to preview</div>
                     </div>
                   </div>
                 )}
                 <style>{`
                   @media print {
                     html, body, #root, .print\\:w-[210mm] { width: 210mm !important; }
                     .print\\:min-h-[297mm] { min-height: 297mm !important; }
                     .print\\:max-w-none { max-width: none !important; }
                     .print\\:p-0 { padding: 0 !important; }
                     .print\\:m-0 { margin: 0 !important; }
                   }
                 `}</style>
               </div>
             </div>
           </div>
         </div>
       </PreviewModal>
      
             {/* ADD ITEM Modal */}
       <FormModal section={MODAL_SECTIONS.SALES}
         isOpen={showAddItemModal}
         onClose={() => { setShowAddItemModal(false); clearAddItemForm(); }}
         title={t('addNewItem')}
         size="2xl"
         onSubmit={(e) => {
           e.preventDefault();
           handleAddItem();
         }}
         submitText={t('addItem')}
         cancelText={t('cancel')}
         submitVariant="success"
       >
         {addItemMessage && (
           <div className={`mb-4 p-3 rounded ${addItemMessage.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
             {addItemMessage}
           </div>
         )}
         
         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
             <input
               type="text"
               value={newItemName}
               onChange={(e) => setNewItemName(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder={t('enterItemName')}
               required
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
             <select
               value={newItemType}
               onChange={(e) => setNewItemType(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
             >
               <option value="Service">{t('service')}</option>
               <option value="Goods">{t('goods')}</option>
             </select>
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Measurement *</label>
             <select
               value={newItemMeasurement}
               onChange={(e) => setNewItemMeasurement(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               required
             >
               <option value="">Select Unit</option>
               {predefinedUnits.map((unit) => (
                 <option key={unit} value={unit}>{unit}</option>
               ))}
             </select>
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Custom Unit (if not in list)</label>
             <input
               type="text"
               value={newItemCustomUnit}
               onChange={(e) => setNewItemCustomUnit(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder={t('enterCustomUnit')}
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Default Rate</label>
             <input
               type="number"
               value={newItemRate}
               onChange={(e) => setNewItemRate(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0.00"
               min="0"
               step="0.01"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">GST Percentage</label>
             <input
               type="number"
               value={newItemGstPercentage}
               onChange={(e) => setNewItemGstPercentage(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0"
               min="0"
               max="100"
               step="0.01"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">HSN Code</label>
             <input
               type="text"
               value={newItemHsnCode}
               onChange={(e) => setNewItemHsnCode(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder={t('enterHsnCode')}
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price</label>
             <input
               type="number"
               value={newItemPurchasePrice}
               onChange={(e) => setNewItemPurchasePrice(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0.00"
               min="0"
               step="0.01"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price</label>
             <input
               type="number"
               value={newItemSalePrice}
               onChange={(e) => setNewItemSalePrice(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0.00"
               min="0"
               step="0.01"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock</label>
             <input
               type="number"
               value={newItemCurrentStock}
               onChange={(e) => setNewItemCurrentStock(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0"
               min="0"
               step="0.01"
             />
           </div>
           
           <div className="flex items-center">
             <input
               type="checkbox"
               id="isActive"
               checked={newItemIsActive}
               onChange={(e) => setNewItemIsActive(e.target.checked)}
               className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
             />
             <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
               Active Item
             </label>
           </div>
         </div>
         
         <div className="mt-4">
           <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
           <textarea
             value={newItemDescription}
             onChange={(e) => setNewItemDescription(e.target.value)}
             className="w-full border border-gray-300 rounded-md shadow-sm p-2"
             rows="3"
             placeholder={t('enterItemDescription')}
           />
         </div>
       </FormModal>
      {docType === 'quotation' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Terms and Conditions (Override)</label>
          <textarea
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            rows={3}
            placeholder="Enter terms and conditions for this quotation (leave blank to use company default)"
            value={quotationTermsOverride}
            onChange={e => setQuotationTermsOverride(e.target.value)}
          />
          <div className="text-xs text-gray-500 mt-1">If left blank, the company's default Quotation Terms and Conditions will be used.</div>
        </div>
      )}

             {/* Payment Modal */}
       <FormModal section={MODAL_SECTIONS.SALES}
         isOpen={showPaymentModal}
         onClose={() => {
           setShowPaymentModal(false);
           setSelectedBillForPayment(null);
         }}
         title={t('addPayment')}
         size="md"
         className="!w-[90vw] sm:!w-[60vw] !max-w-2xl"
         onSubmit={(e) => {
           e.preventDefault();
           handleSavePayment();
         }}
         submitText={t('savePayment')}
         cancelText={t('cancel')}
         submitVariant="success"
       >
         {/* Bill Information */}
         {selectedBillForPayment && (
           <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
             <h4 className="font-semibold text-blue-800 mb-1 text-xs sm:text-sm">Bill Information</h4>
             <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2 text-xs">
               <div><span className="font-medium">Bill:</span> {selectedBillForPayment.number}</div>
               <div><span className="font-medium">Party:</span> {getPartyName(selectedBillForPayment.party)}</div>
               <div><span className="font-medium">Total:</span> {formatCurrency(selectedBillForPayment.amount, companyCurrency)}</div>
               <div><span className="font-medium">Outstanding:</span> {formatCurrency(getBillOutstanding(selectedBillForPayment), companyCurrency)}</div>
             </div>
           </div>
         )}
         
         {/* Advance Information - Only show if advance is available */}
         {selectedBillForPayment && getPartyAdvance(selectedBillForPayment.party, payments) > 0 && (
           <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
             <h4 className="font-semibold text-green-800 mb-1 text-xs sm:text-sm">Advance Information</h4>
             <div className="text-xs space-y-1">
               <div><span className="font-medium">Available Advance:</span> {formatCurrency(getPartyAdvance(selectedBillForPayment.party, payments), companyCurrency)}</div>
               <div className="text-xs text-green-600">
                 Note: Excess payment will be allocated to other bills using FIFO method.
               </div>
             </div>
           </div>
         )}
         
         <div className="space-y-2 sm:space-y-3">
           {/* Receipt Number and Date - Inline Edit */}
           <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 mb-2">
             <div className="flex justify-between items-center mb-2">
               <h4 className="text-xs font-medium text-gray-700">Payment Details</h4>
               <button
                 type="button"
                 onClick={() => {
                   setReceiptNumber('');
                   setPaymentDate('');
                 }}
                 className="text-gray-400 hover:text-gray-600 transition-colors text-xs"
                 title="Clear and regenerate"
               >
                 Regenerate
               </button>
             </div>
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
           <div>
                 <label className="block text-xs font-medium text-gray-700 mb-1">Receipt Number</label>
             <input
               type="text"
               value={receiptNumber}
               onChange={(e) => setReceiptNumber(e.target.value)}
                   className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                   placeholder="Auto-generated"
             />
           </div>
           
           <div>
                 <label className="block text-xs font-medium text-gray-700 mb-1">Payment Date</label>
             <input
               type="date"
               value={paymentDate}
               onChange={(e) => setPaymentDate(e.target.value)}
                   className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
               required
             />
               </div>
             </div>
           </div>
           
           {/* Amount and Payment Mode in one line */}
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
           <div>
               <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
             <input
               type="number"
               value={paymentAmount}
               onChange={(e) => setPaymentAmount(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
               placeholder={t('enterPaymentAmount')}
               step="0.01"
               min="0"
               required
             />
           </div>
           
           <div>
               <label className="block text-xs font-medium text-gray-700 mb-1">Payment Mode</label>
             <select
               value={paymentMode}
               onChange={(e) => setPaymentMode(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
               required
             >
               <option value="Cash">{t('cash')}</option>
               <option value="Cheque">{t('cheque')}</option>
               <option value="Bank Transfer">{t('bankTransfer')}</option>
               <option value="UPI">UPI</option>
               <option value="Card">{t('card')}</option>
             </select>
             </div>
           </div>
           
           {/* Payment calculation info */}
           {selectedBillForPayment && paymentAmount && (
             <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 space-y-1">
               <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                 <div>Outstanding: {formatCurrency(getBillOutstanding(selectedBillForPayment), companyCurrency)}</div>
                 <div>Advance: {formatCurrency(getPartyAdvance(selectedBillForPayment.party, payments), companyCurrency)}</div>
                 <div className="font-medium text-blue-600 col-span-2 sm:col-span-1">
                   Payment needed: {formatCurrency(Math.max(0, getBillOutstanding(selectedBillForPayment) - getPartyAdvance(selectedBillForPayment.party, payments)), companyCurrency)}
                 </div>
               </div>
             </div>
           )}
           
           {/* Reference and Notes in one line */}
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
           <div>
               <label className="block text-xs font-medium text-gray-700 mb-1">Reference</label>
             <input
               type="text"
               value={paymentReference}
               onChange={(e) => setPaymentReference(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
               placeholder="Cheque number, UPI reference, etc."
             />
           </div>
           
           <div>
               <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
             <textarea
               value={paymentNotes}
               onChange={(e) => setPaymentNotes(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                 rows="2"
               placeholder={t('additionalNotes')}
             />
             </div>
           </div>
         </div>
       </FormModal>

             {/* Payment Details Modal */}
       <StandardModal section={MODAL_SECTIONS.SALES}
         isOpen={showPaymentDetailsModal && selectedBillForReceipts}
         onClose={() => setShowPaymentDetailsModal(false)}
         title={t('paymentReceipts')}
         size="2xl"
         className="!w-[90vw] sm:!w-[60vw] !max-w-4xl"
       >
         <div className="mb-2">
           <p className="text-xs sm:text-sm text-gray-600 break-words">
             <span className="hidden sm:inline">{docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}: {selectedBillForReceipts?.number} | Party: {getPartyName(selectedBillForReceipts?.party)} | Total Amount: ₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
             <span className="sm:hidden">{selectedBillForReceipts?.number} | ₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
           </p>
         </div>
         
         {/* Payment Summary */}
         <div className="bg-gray-50 p-2 sm:p-3 rounded-lg mb-2 sm:mb-3">
           <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 text-xs">
             <div className="text-center sm:text-left">
               <span className="font-medium text-gray-700">Total Bill:</span>
               <span className="ml-1 text-xs sm:text-sm font-semibold">₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
             </div>
             <div className="text-center sm:text-left">
               <span className="font-medium text-gray-700">Paid:</span>
               <span className="ml-1 text-xs sm:text-sm font-semibold text-green-600">₹{(selectedBillForReceipts?.paid || 0).toLocaleString('en-IN')}</span>
             </div>
             <div className="text-center sm:text-left">
               <span className="font-medium text-gray-700">Outstanding:</span>
               <span className="ml-1 text-xs sm:text-sm font-semibold text-red-600">₹{(selectedBillForReceipts?.outstanding || 0).toLocaleString('en-IN')}</span>
             </div>
           </div>
         </div>
         
         <div className="overflow-x-auto max-h-[400px]">
           {/* Desktop Table View */}
           <div className="hidden sm:block">
             <table className="w-full text-xs">
             <thead className="bg-gray-50">
               <tr>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     RECEIPT NO
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   AGAINST {docType.toUpperCase()}
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     AMOUNT
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   DATE
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   MODE
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   ACTIONS
                 </th>
               </tr>
             </thead>
             <tbody className="bg-white divide-y divide-gray-200">
               {selectedBillForReceipts && getBillPayments(selectedBillForReceipts).map((payment) => {
                 const allocation = payment.allocations.find(a => a.billId === selectedBillForReceipts.id && a.billType === docType);
                 return (
                   <tr key={payment.id} className="hover:bg-gray-50">
                       <td className="px-2 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                       {payment.receiptNumber}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                       {allocation?.billNumber || 'N/A'}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                       ₹{parseFloat(allocation?.allocatedAmount || 0).toLocaleString('en-IN')}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                       {payment.paymentDate}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                       {payment.paymentMode}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                         <StandardButton
                           variant="info"
                           size="xs"
                           onClick={() => {
                             setSelectedPaymentForReceipt(payment);
                             setShowReceiptModal(true);
                             setShowPaymentDetailsModal(false);
                           }}
                         >
                           Preview
                         </StandardButton>
                     </td>
                   </tr>
                 );
               })}
             </tbody>
           </table>
           </div>

           {/* Mobile Card View */}
           <div className="sm:hidden space-y-2">
             {selectedBillForReceipts && getBillPayments(selectedBillForReceipts).map((payment) => {
               const allocation = payment.allocations.find(a => a.billId === selectedBillForReceipts.id && a.billType === docType);
               return (
                 <div key={payment.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                   <div className="flex justify-between items-start mb-2">
                     <div className="flex-1">
                       <div className="text-xs font-medium text-gray-900">{payment.receiptNumber}</div>
                       <div className="text-xs text-gray-600">vs {allocation?.billNumber || 'N/A'}</div>
                     </div>
                     <div className="text-right">
                       <div className="text-xs font-semibold text-gray-900">₹{parseFloat(allocation?.allocatedAmount || 0).toLocaleString('en-IN')}</div>
                       <div className="text-xs text-gray-600">{payment.paymentMode}</div>
                     </div>
                   </div>
                   <div className="flex justify-between items-center">
                     <div className="text-xs text-gray-600">{payment.paymentDate}</div>
                     <StandardButton
                       variant="info"
                       size="xs"
                       onClick={() => {
                         setSelectedPaymentForReceipt(payment);
                         setShowReceiptModal(true);
                         setShowPaymentDetailsModal(false);
                       }}
                     >
                       Preview
                     </StandardButton>
                   </div>
                 </div>
               );
             })}
           </div>
         </div>
       </StandardModal>

             {/* Payment Receipt Preview Modal */}
       <PreviewModal section={MODAL_SECTIONS.SALES}
         isOpen={showReceiptModal && selectedPaymentForReceipt}
         onClose={() => setShowReceiptModal(false)}
         title={t('paymentReceiptPreview')}
         maxWidth="max-w-[90vw] sm:max-w-[60vw] lg:max-w-2xl"
         maxHeight="max-h-[70vh] sm:max-h-[80vh]"
         extraActions={
           <ShareButton
             onExportPDF={async () => {
               try {
                 const jsPDFLocal = window.jsPDF || (window.jspdf && window.jspdf.jsPDF);
                 if (!jsPDFLocal || !selectedPaymentForReceipt) return;
                 const doc = new jsPDFLocal();
                 let y = 15;
                 doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(31,41,55);
                 doc.text('PAYMENT RECEIPT', 105, y, { align: 'center' }); y += 12;
                 doc.setFontSize(12); doc.setTextColor(59,130,246); doc.setFont('helvetica', 'bold');
                 doc.text(`Receipt No: ${selectedPaymentForReceipt.receiptNumber}`, 105, y, { align: 'center' }); y += 12;
                 doc.setFontSize(14); doc.setTextColor(0,0,0); doc.setFont('helvetica', 'bold');
                 doc.text(company?.firmName || company?.name || 'Company Name', 105, y, { align: 'center' }); y += 10;
                 doc.setDrawColor(209,213,219); doc.line(25, y, 185, y); y += 10;
                 doc.setFontSize(12); doc.setFont('helvetica', 'normal');
                 const addKV = (k, v) => { doc.text(`${k}: ${v}`, 25, y); y += 8; };
                 addKV('Date', selectedPaymentForReceipt.paymentDate || new Date().toISOString().split('T')[0]);
                 addKV('Received From', selectedPaymentForReceipt.partyName || '');
                 addKV('Amount', `₹${parseFloat(selectedPaymentForReceipt.totalAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
                 addKV('Payment Mode', selectedPaymentForReceipt.paymentMode || 'Cash');
                 addKV('Reference', selectedPaymentForReceipt.reference || '-');
                 if (selectedPaymentForReceipt.notes) addKV('Notes', selectedPaymentForReceipt.notes);
                 const blob = doc.output('blob');
                 const file = new File([blob], `receipt-${selectedPaymentForReceipt.receiptNumber}.pdf`, { type: 'application/pdf' });
                 if (navigator.canShare && navigator.canShare({ files: [file] })) {
                   await navigator.share({ files: [file], title: `Receipt ${selectedPaymentForReceipt.receiptNumber}` });
                 } else {
                   doc.save(`receipt-${selectedPaymentForReceipt.receiptNumber}.pdf`);
                 }
               } catch (e) { console.error('Share receipt PDF failed:', e); }
             }}
             onExportImage={async () => {
               try {
                 const el = document.querySelector('.receipt-container');
                 if (!el || !selectedPaymentForReceipt) return;
                 const html2canvas = await import('html2canvas');
                 const canvas = await html2canvas.default(el, { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff' });
                 const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                 const file = new File([blob], `receipt-${selectedPaymentForReceipt.receiptNumber}.png`, { type: 'image/png' });
                 if (navigator.canShare && navigator.canShare({ files: [file] })) {
                   await navigator.share({ files: [file], title: `Receipt ${selectedPaymentForReceipt.receiptNumber}` });
                 } else {
                   const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `receipt-${selectedPaymentForReceipt.receiptNumber}.png`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                 }
               } catch (e) { console.error('Share receipt image failed:', e); }
             }}
           />
         }
         showBackButton={true}
         onBack={() => {
           setShowReceiptModal(false);
           setShowPaymentDetailsModal(true);
         }}
         showPrintButton={true}
         onPrint={printReceipt}
         showPdfButton={true}
         onPdf={generatePDFReceipt}
         zIndex="z-50"
       >
         <div ref={receiptRef} className="flex justify-center">
           <ReceiptTemplate
             receipt={{
               totalAmount: selectedPaymentForReceipt?.totalAmount,
               date: selectedPaymentForReceipt?.paymentDate,
               mode: selectedPaymentForReceipt?.paymentMode,
               reference: selectedPaymentForReceipt?.reference,
               notes: selectedPaymentForReceipt?.notes,
               billReference: selectedPaymentForReceipt?.billId,
               allocations: selectedPaymentForReceipt?.allocations,
               advanceAllocations: selectedPaymentForReceipt?.advanceAllocations,
               fifoAllocationUsed: selectedPaymentForReceipt?.fifoAllocationUsed,
               remainingAmount: selectedPaymentForReceipt?.remainingAmount
             }}
             bill={{
               number: selectedPaymentForReceipt?.billNumber,
               invoiceNumber: selectedPaymentForReceipt?.billNumber,
               billNumber: selectedPaymentForReceipt?.billNumber
             }}
             company={company}
             party={{ firmName: selectedPaymentForReceipt?.partyName }}
             receiptNumber={selectedPaymentForReceipt?.receiptNumber}
             fifoAllocation={selectedPaymentForReceipt?.allocations}
             customImages={selectedImages}
             isPurchase={false}
           />
         </div>
       </PreviewModal>

      {/* ADD PARTY Modal */}
      <FormModal section={MODAL_SECTIONS.SALES}
        isOpen={showAddPartyModal}
        onClose={() => { setShowAddPartyModal(false); clearAddPartyForm(); }}
        title="Add New Party"
        size="2xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleAddParty();
        }}
        submitText="Add Party"
        cancelText="Cancel"
        submitVariant="success"
      >
        {addPartyMessage && (
          <div className={`mb-4 p-3 rounded ${addPartyMessage.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {addPartyMessage}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Party Name *</label>
            <input
              type="text"
              value={newPartyName}
              onChange={(e) => setNewPartyName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter party name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GST Type</label>
            <select
              value={newPartyGstType}
              onChange={(e) => setNewPartyGstType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Regular">Regular</option>
              <option value="Composition">Composition</option>
              <option value="Unregistered">Unregistered</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              GSTIN {newPartyGstType === 'Regular' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={newPartyGstin}
              onChange={(e) => setNewPartyGstin(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder={newPartyGstType === 'Regular' ? 'Enter GSTIN (required for Regular)' : 'Enter GSTIN (if applicable)'}
              required={newPartyGstType === 'Regular'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="text"
              value={newPartyPhone}
              onChange={(e) => setNewPartyPhone(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={newPartyEmail}
              onChange={(e) => setNewPartyEmail(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email address"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            value={newPartyAddress}
            onChange={(e) => setNewPartyAddress(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            rows="3"
            placeholder="Enter party address"
          />
        </div>
        
        {/* Advanced Fields Toggle */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowAdvancedFields(!showAdvancedFields)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            <svg className={`w-4 h-4 transition-transform ${showAdvancedFields ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showAdvancedFields ? 'Hide' : 'Show'} Advanced Fields (Opening Balance, Credit Limit)
          </button>
        </div>
        
        {/* Advanced Fields */}
        {showAdvancedFields && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Advanced Settings (Optional)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                <input
                  type="number"
                  value={newPartyOpeningBalance}
                  onChange={(e) => setNewPartyOpeningBalance(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">+ve: Customer owes you, -ve: You owe customer</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Limit (₹)</label>
                <input
                  type="number"
                  value={newPartyCreditLimit}
                  onChange={(e) => setNewPartyCreditLimit(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum credit allowed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Period (Days)</label>
                <input
                  type="number"
                  value={newPartyCreditPeriod}
                  onChange={(e) => setNewPartyCreditPeriod(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Payment terms in days</p>
              </div>
            </div>
          </div>
        )}
      </FormModal>

      <FormModal section={MODAL_SECTIONS.SALES}
        isOpen={showSubPartyModal}
        onClose={() => setShowSubPartyModal(false)}
        title="Sub parties"
        size="xl"
        className={SUB_PARTY_MODAL_SHELL_CLASS}
        onSubmit={(e) => {
          e.preventDefault();
          applySubPartyModal();
        }}
        submitText="Apply"
        cancelText="Cancel"
        submitVariant="primary"
      >
        <p className="text-xs sm:text-sm text-gray-600 mb-3 leading-relaxed flex flex-wrap items-start gap-1.5">
          <span>
            <strong>Select lines</strong> for each sub party — the <strong>amount defaults</strong> to the sum of those line totals (incl. GST where applicable). Sub party amounts must sum to the Grand Total.
          </span>
          <InfoHoverTip text={SUB_PARTY_MODAL_HELP} placement="bottom" className="mt-0.5" />
        </p>

        {subPartyLineRefs.length === 0 ? (
          <div className="flex flex-wrap items-start gap-2 p-3 rounded-md bg-amber-50 text-amber-900 text-sm mb-3">
            <span>No line items yet — add items to the invoice first, then assign lines to sub parties.</span>
            <InfoHoverTip text={SUB_PARTY_NO_LINES_HELP} placement="bottom" />
          </div>
        ) : null}

        <div className={subPartyLineRefs.length === 0 ? 'hidden' : SUB_PARTY_SCROLL_LIST_CLASS}>
          {subPartyDraft.map((row, idx) => (
            <div key={idx} className={SUB_PARTY_CARD_CLASS}>
              <div className={`${SUB_PARTY_ROW_LAYOUT_CLASS} mb-3`}>
                <div className={SUB_PARTY_NAME_FIELD_CLASS}>
                  <label className="block text-xs text-gray-600 mb-0.5">Sub party name</label>
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) => updateSubPartyDraftRow(idx, 'name', e.target.value)}
                    className="w-full min-w-0 max-w-full p-2.5 border border-gray-300 rounded text-base md:text-sm bg-white touch-manipulation"
                    placeholder="e.g. end customer"
                    autoComplete="off"
                  />
                </div>
                <div className={SUB_PARTY_AMOUNT_FIELD_CLASS}>
                  <label className="flex flex-wrap items-center gap-1 text-xs text-gray-600 mb-0.5">
                    <span>Amount <span className="font-normal text-gray-400">(editable)</span></span>
                    <InfoHoverTip text={SUB_PARTY_AMOUNT_HELP} placement="bottom" />
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={row.amount}
                    onChange={(e) => updateSubPartyDraftRow(idx, 'amount', e.target.value)}
                    className="w-full min-w-0 p-2.5 border border-gray-300 rounded text-base md:text-sm tabular-nums bg-white touch-manipulation"
                    placeholder="From lines"
                  />
                  {Array.isArray(row.lineIndices) && row.lineIndices.length > 0 && (() => {
                    const lineSum = sumLineTotalsForSubPartyIndices(row.lineIndices, subPartyLineRefs, company.gstinType);
                    const adj = roundTo2Decimals(parseFloat(String(row._invoiceRoundOffAdj ?? '').replace(/,/g, '')) || 0);
                    const expectedDefault = roundTo2Decimals(lineSum + adj);
                    const entered = parseFloat(String(row.amount ?? '').replace(/,/g, ''));
                    const differs = !Number.isFinite(entered) || Math.abs(entered - expectedDefault) > 0.02;
                    return (
                      <div
                        className={`mt-1.5 space-y-0.5 text-[10px] leading-snug tabular-nums ${differs ? 'text-amber-800' : 'text-gray-500'}`}
                      >
                        <div className="flex flex-wrap items-center gap-1">
                          <span>Line total (items): {formatCurrency(lineSum, companyCurrency)}</span>
                          <InfoHoverTip text={SUB_PARTY_AMOUNT_DETAIL_HELP} placement="bottom" />
                        </div>
                        {Math.abs(adj) > 0.001 ? (
                          <div>+ invoice round-off: {formatCurrency(adj, companyCurrency)}</div>
                        ) : null}
                        <div>
                          Default amount: {formatCurrency(expectedDefault, companyCurrency)}
                          {differs ? (
                            <span className="font-normal"> — field differs; OK if intentional</span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className={SUB_PARTY_ACTIONS_CLASS}>
                  <div className={SUB_PARTY_SYNC_ROW_CLASS}>
                    <button
                      type="button"
                      onClick={() => fillSubPartyDraftAmountFromLines()}
                      className="min-h-[44px] lg:min-h-0 text-xs px-3 py-2.5 rounded border border-indigo-300 bg-white text-indigo-800 hover:bg-indigo-50 touch-manipulation text-center w-full"
                    >
                      Sync all from lines
                    </button>
                    <InfoHoverTip text={SUB_PARTY_SYNC_HELP} placement="bottom" />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSubPartyDraftRow(idx)}
                    className="w-full min-h-[44px] lg:min-h-0 text-sm text-red-600 px-3 py-2.5 rounded border border-transparent hover:bg-red-50 shrink-0 disabled:opacity-40 touch-manipulation text-center"
                    disabled={subPartyDraft.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-gray-600 mb-2">
                <span>Assign lines (item · measurement)</span>
                <InfoHoverTip text={SUB_PARTY_ASSIGN_LINES_HELP} placement="bottom" />
              </div>
              {(() => {
                const visibleLineRefs = subPartyLineRefs.filter(({ cleanedIdx: ci }) =>
                  !subPartyDraft.some(
                    (r, i) => i !== idx && Array.isArray(r.lineIndices) && r.lineIndices.includes(ci)
                  )
                );
                const hiddenCount = subPartyLineRefs.length - visibleLineRefs.length;
                return (
                  <>
                    {hiddenCount > 0 ? (
                      <div className="flex flex-wrap items-start gap-1.5 text-[11px] text-gray-500 mb-2 leading-snug">
                        <span>
                          {hiddenCount} line{hiddenCount === 1 ? '' : 's'} assigned to other sub-parties — uncheck there first to assign here.
                        </span>
                        <InfoHoverTip text={subPartyHiddenLinesHelp(hiddenCount)} placement="bottom" />
                      </div>
                    ) : null}
                    <div className={SUB_PARTY_LINE_LIST_CLASS}>
                      {visibleLineRefs.length === 0 && subPartyLineRefs.length > 0 ? (
                        <div className="flex flex-wrap items-start gap-2 text-xs text-gray-600 p-2 rounded border border-dashed border-gray-300 bg-white leading-snug">
                          <span>
                            No lines available for this sub-party — every line is already assigned to another sub-party above.
                          </span>
                          <InfoHoverTip text={SUB_PARTY_NO_AVAILABLE_LINES_HELP} placement="bottom" />
                        </div>
                      ) : (
                        visibleLineRefs.map(({ row: invRow, cleanedIdx }) => {
                          const itemMaster = items.find((it) => it.id === invRow.item);
                          const itemLabel = itemMaster?.itemName || itemMaster?.name || '';
                          let measure = '';
                          if (invRow.qtyDisplay) measure = String(invRow.qtyDisplay);
                          else if (invRow.qtyExpr && String(invRow.qtyExpr).trim()) measure = String(invRow.qtyExpr).trim();
                          const q = parseFloat(invRow.qty) || 0;
                          const u = invRow.unit || invRow.quantityMeasurement || 'Nos';
                          const qtyBit = q > 0 ? `${q} ${u}` : '';
                          if (qtyBit && !measure) measure = qtyBit;
                          else if (qtyBit && measure) measure = `${measure} · ${qtyBit}`;
                          if (!measure) {
                            const n = parseFloat(invRow.nos) || 0;
                            const l = parseFloat(invRow.length) || 0;
                            const h = parseFloat(invRow.height) || 0;
                            if (n || l || h) measure = `${n}×${l || 1}×${h || 1}`;
                          }
                          if (!measure) measure = '—';
                          const lineTot = getLineTotalForSubParty(invRow, company.gstinType);
                          const checked = Array.isArray(row.lineIndices) && row.lineIndices.includes(cleanedIdx);
                          return (
                            <label
                              key={cleanedIdx}
                              className={`flex items-start gap-2 p-2 rounded border cursor-pointer text-xs sm:text-sm ${
                                checked ? 'border-indigo-400 bg-indigo-50/90' : 'border-gray-200 bg-white'
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5 shrink-0"
                                checked={checked}
                                onChange={() => toggleSubPartyDraftLine(idx, cleanedIdx)}
                              />
                              <span className="min-w-0 flex-1 break-words">
                                <span className="font-medium text-gray-900">{cleanedIdx + 1}. {itemLabel || '—'}</span>
                                <span className="text-gray-600"> · {measure}</span>
                                <span className="block sm:inline sm:ml-1 tabular-nums text-gray-800 font-medium break-words">
                                  {' '}
                                  ({formatCurrency(lineTot, companyCurrency)})
                                </span>
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addSubPartyDraftRow}
          className={`${subPartyLineRefs.length === 0 ? 'hidden' : 'mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium'}`}
        >
          + Add row
        </button>
        <div className={`${subPartyLineRefs.length === 0 ? 'hidden' : 'mt-2'}`}>
          <button
            type="button"
            onClick={() => {
              setSubPartyBreakdown([]);
              setShowSubPartyModal(false);
            }}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Clear breakdown
          </button>
        </div>
        <div
          className={`${subPartyLineRefs.length === 0 ? 'hidden' : 'mt-4 p-3 rounded-md text-sm'} ${subPartyDraftMatchesGrand || normalizeSubPartyBreakdownForSave(subPartyDraft).length === 0 ? 'bg-slate-50 text-slate-800' : 'bg-amber-50 text-amber-900'}`}
        >
          <div className={SUB_PARTY_TOTALS_ROW_CLASS}>
            <span className="font-medium">Sum of entered amounts</span>
            <span className="font-semibold tabular-nums text-right">{formatCurrency(subPartyDraftSum, companyCurrency)}</span>
          </div>
          <div className={`${SUB_PARTY_TOTALS_ROW_CLASS} mt-1`}>
            <span className="text-xs">Invoice grand total</span>
            <span className="text-xs tabular-nums font-medium text-right">{formatCurrency(grandTotal, companyCurrency)}</span>
          </div>
          {Math.abs(roundOffAmount) > 0.001 && subPartyLineRefCount > 0 ? (
            <p className="flex flex-wrap items-start gap-1.5 text-[11px] mt-1.5 text-gray-600 leading-snug">
              <span>
                Invoice round-off ({formatCurrency(roundOffAmount, companyCurrency)}) is added to one sub-party when every line is assigned, so totals match the rounded grand total.
              </span>
              <InfoHoverTip text={SUB_PARTY_ROUND_OFF_HELP} placement="bottom" />
            </p>
          ) : null}
          {normalizeSubPartyBreakdownForSave(subPartyDraft).length > 0 && !subPartyDraftMatchesGrand && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs font-medium">
              <span>
                Difference: {formatCurrency(roundTo2Decimals(grandTotal - subPartyDraftSum), companyCurrency)} (must be 0)
              </span>
              <InfoHoverTip text={SUB_PARTY_TOTAL_MISMATCH_HELP} placement="bottom" />
            </div>
          )}
        </div>
      </FormModal>

      {showServiceEditorModal &&
        createPortal(
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/50 p-3 sm:p-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))]"
            style={{ zIndex: 10050 }}
            data-modal-overlay="true"
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeServiceEditor();
            }}
          >
            <div
              className="bg-white rounded-t-xl sm:rounded-lg shadow-xl w-full min-w-0 max-w-md max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-1.5rem))] sm:max-h-[min(90vh,720px)] flex flex-col overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-labelledby="service-details-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-3 sm:p-4 border-b shrink-0 gap-2">
                <h3 id="service-details-title" className="text-base sm:text-lg font-semibold text-gray-900 min-w-0 pr-2">
                  Service Details
                </h3>
                <button
                  onClick={closeServiceEditor}
                  className="shrink-0 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 inline-flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors touch-manipulation rounded-lg hover:bg-gray-100"
                  type="button"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4 sm:p-5 space-y-4 overflow-y-auto min-h-0 overscroll-contain [-webkit-overflow-scrolling:touch]">
                <p className="text-sm text-gray-600 leading-relaxed rounded-lg border border-slate-100 bg-slate-50/90 px-3 py-2.5">
                  Optional: link this line&apos;s work to a vendor and internal cost. Customer charge and invoice visibility control how it appears on the PDF and in Service Operations.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Service tag</label>
                  <input
                    type="text"
                    value={serviceEditorForm.serviceName || ''}
                    onChange={(e) => setServiceEditorForm(prev => ({ ...prev, serviceName: e.target.value.toLowerCase() }))}
                    className="w-full min-h-[44px] sm:min-h-0 border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400"
                    placeholder="e.g. pasting, frame, vinyl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Vendor (optional)</label>
                  <select
                    value={serviceEditorForm.vendorId || ''}
                    onChange={(e) => {
                      const vendorId = e.target.value;
                      const selectedVendor = parties.find(p => p.id === vendorId);
                      setServiceEditorForm(prev => ({ ...prev, vendorId, vendorName: selectedVendor?.firmName || '' }));
                    }}
                    className="w-full min-h-[44px] sm:min-h-0 border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400"
                  >
                    <option value="">Select vendor</option>
                    {parties.map(p => (
                      <option key={p.id} value={p.id}>{p.firmName}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Internal cost</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={serviceEditorForm.internalCost}
                      onChange={(e) => setServiceEditorForm(prev => ({ ...prev, internalCost: e.target.value }))}
                      className="w-full min-h-[44px] sm:min-h-0 border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Charge to customer</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={serviceEditorForm.customerCharge}
                      onChange={(e) => setServiceEditorForm(prev => ({ ...prev, customerCharge: e.target.value }))}
                      className="w-full min-h-[44px] sm:min-h-0 border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <label className="inline-flex items-start gap-3 text-sm text-gray-700 touch-manipulation rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-3">
                  <input
                    type="checkbox"
                    checked={Boolean(serviceEditorForm.showOnInvoice)}
                    onChange={(e) => setServiceEditorForm(prev => ({ ...prev, showOnInvoice: e.target.checked }))}
                    className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300"
                  />
                  <span>Show as an extra line on the customer invoice PDF</span>
                </label>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Job status</label>
                  <select
                    value={serviceEditorForm.status || 'pending'}
                    onChange={(e) => setServiceEditorForm(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full min-h-[44px] sm:min-h-0 border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400"
                  >
                    {SERVICE_STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-3 sm:p-4 border-t bg-gray-50 rounded-b-lg shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4">
                <button
                  type="button"
                  onClick={closeServiceEditor}
                  className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-3 sm:py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-md touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveServiceEditor}
                  className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-3 sm:py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-md touch-manipulation"
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <StandardModal section={MODAL_SECTIONS.SALES}
        isOpen={showManageTagsModal}
        onClose={() => setShowManageTagsModal(false)}
        title="Service tags"
        size="lg"
        zIndex="z-[10050]"
      >
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          Create and manage tags for your company. Tags used on past bills still appear when filtering until you remove them from those bills.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <input
            type="text"
            value={manageTagsNewName}
            onChange={(e) => setManageTagsNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleManageAddTag();
            }}
            className="flex-1 min-h-[44px] sm:min-h-0 border border-gray-300 rounded-md px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation"
            placeholder="New tag name"
          />
          <button
            type="button"
            onClick={() => void handleManageAddTag()}
            className="min-h-[44px] sm:min-h-0 px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 active:bg-blue-800 touch-manipulation sm:shrink-0"
          >
            Add
          </button>
        </div>
        <div className="border border-gray-200 rounded-xl max-h-[min(60dvh,22rem)] sm:max-h-80 overflow-y-auto overscroll-contain divide-y divide-gray-100 [-webkit-overflow-scrolling:touch] shadow-inner bg-gray-50/30">
          {[...serviceTagMaster]
            .sort((a, b) =>
              normalizeTagName(a.normalizedName || a.name).localeCompare(normalizeTagName(b.normalizedName || b.name))
            )
            .map((t) => {
              const label = normalizeTagName(t.normalizedName || t.name);
              const active = t.isActive !== false;
              return (
                <div
                  key={t.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-3 sm:py-2 text-sm min-w-0"
                >
                  <span
                    className={`min-w-0 break-words ${active ? 'text-gray-900' : 'text-gray-400 line-through'}`}
                  >
                    {label || t.id}
                  </span>
                  <div className="flex gap-2 shrink-0 self-end sm:self-auto">
                    {active ? (
                      <button
                        type="button"
                        onClick={() => void deactivateServiceTag(t)}
                        className="min-h-[40px] sm:min-h-0 px-2 py-2 sm:py-0 text-sm sm:text-xs text-red-600 hover:underline touch-manipulation"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void restoreServiceTag(t)}
                        className="min-h-[40px] sm:min-h-0 px-2 py-2 sm:py-0 text-sm sm:text-xs text-blue-600 hover:underline touch-manipulation"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          {serviceTagMaster.length === 0 && (
            <div className="p-4 text-sm text-gray-500 text-center">No registry tags yet. Add one above or add tags while billing.</div>
          )}
        </div>
      </StandardModal>

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add Note</h3>
              <button
                onClick={() => setShowNotesModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full h-64 border border-gray-300 rounded-md shadow-sm p-3 resize-none"
                placeholder="Enter additional notes..."
                autoFocus
              />
              <div className="mt-2 text-right text-sm text-gray-500">
                {notes.length} characters
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* E-way Bill Modal */}
      {showEwayBillModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add E-way Bill Details</h3>
              <button
                onClick={() => setShowEwayBillModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-way Bill No</label>
                  <input
                    type="text"
                    value={customFields.ewayBillNo}
                    onChange={e => setCustomFields(f => ({ ...f, ewayBillNo: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-3"
                    placeholder="Enter E-way Bill number"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-way Bill Date</label>
                  <input
                    type="date"
                    value={customFields.ewayDate}
                    onChange={e => setCustomFields(f => ({ ...f, ewayDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-3"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">QR Code URL</label>
                  <input
                    type="text"
                    value={customFields.ewayQr}
                    onChange={e => setCustomFields(f => ({ ...f, ewayQr: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-3"
                    placeholder="Enter QR code URL"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowEwayBillModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowEwayBillModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              >
                Save E-way Bill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg max-w-md ${
          notification.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center justify-between">
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 text-white hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}

    </div>
    );
}

export default Sales;